// src/ConferenceRegistration.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import Select from "react-select";
//import "../index.css";
import "../ConferenceRegistration.css";
import TermsAndConditions from "./TermsAndConditions";

const currentHost = window.location.host;
const TOTAL_STEPS = 7;
const MOBILE_MAX_LENGTH = 15;

// Read keys and URLs from .env
const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY;
const REGISTRATION_BASE_URL =
  process.env.REACT_APP_REGISTRATION_API_URL || "http://localhost:8085";
const PAYMENT_BASE_URL =
  process.env.REACT_APP_PAYMENT_API_URL || "http://localhost:8085";
const CONFERENCE_API =
  process.env.REACT_APP_CONFERENCE_API_URL ||
  "http://localhost:8080/conference";
const REVIEW_API =
  process.env.REACT_APP_REVIEW_API_URL || "http://localhost:8082";
const USER_API_URL =
  process.env.REACT_APP_USER_API_URL || "http://localhost:8085/registration";

// Frontend-only mapping for additionalData JSON payload.
// Add new entries here to persist more form fields into additionalData
// without any backend entity/controller changes.
const ADDITIONAL_DATA_FIELD_MAPPINGS = [
  { additionalKey: "dietaryRestrictions", formKey: "dietaryRestrictions" },
  { additionalKey: "specialization", formKey: "specialization" },
  { additionalKey: "designation", formKey: "designation" },
];

// Log environment variables for debugging
console.log("🔧 Environment Variables:");
console.log("  REGISTRATION_BASE_URL:", REGISTRATION_BASE_URL);
console.log("  PAYMENT_BASE_URL:", PAYMENT_BASE_URL);
console.log("  CONFERENCE_API:", CONFERENCE_API);

// Updated to use v2 API endpoints
const CONFERENCE_REGISTRATION_URL = `${REGISTRATION_BASE_URL}/registration`;
const CHECK_REGISTRATION_URL = `${REGISTRATION_BASE_URL}/registration/check-registration`;
const ORDER_URL = `${PAYMENT_BASE_URL}/payment/create-order`;
const PAYMENT_VERIFY_URL = `${PAYMENT_BASE_URL}/payment/verify`;
const PAYMENT_SAVE_URL = `${PAYMENT_BASE_URL}/payment/save-with-breakdown`; // Updated to use breakdown endpoint

const DEFAULT_COUNTRIES = [
  "India",
  "United States",
  "United Kingdom",
  "Australia",
  "Canada",
].map((name) => ({
  label: name,
  value: name,
}));

const ProgressBar = ({ step, isAbstractRequired, abstractStepNumber }) => {
  const allSteps = [
    "Personal",
    "Address",
    "Paper/Abstract",
    "Affiliation",
    "Category",
    "Summary",
    "Payment",
  ];

  // Filter out Paper/Abstract step if abstractStepNumber is 0 (hidden) or 7 (shown after payment)
  const steps =
    abstractStepNumber === 0 || abstractStepNumber === 7
      ? allSteps.filter((label) => label !== "Paper/Abstract")
      : allSteps;

  // Adjust display step number if Paper/Abstract is hidden and current step > 2
  let displayStep = step;
  if ((abstractStepNumber === 0 || abstractStepNumber === 7) && step > 2) {
    displayStep = step - 1; // Shift step numbers down since step 3 is hidden
  }

  return (
    <div className="progress-bar">
      {steps.map((label, i) => {
        const idx = i + 1;
        const status =
          displayStep === idx
            ? "current"
            : displayStep > idx
              ? "done"
              : "upcoming";
        return (
          <div key={i} className={`progress-step ${status}`}>
            <div className="progress-circle" data-title={label}>
              {idx}
            </div>
            {/* <div className="progress-label">{label}</div>
            {i < steps.length - 1 && <div className="progress-line" />} */}
          </div>
        );
      })}
    </div>
  );
};

const ConferenceRegistration = ({
  existingUser,
  prefilledEmail,
  onBackToLogin,
}) => {
  const [step, setStep] = useState(1);
  const [bannerUrl, setBannerUrl] = useState(null);
  const [categories, setCategories] = useState([]); // Array of {id, value}
  const [registrationTypes, setRegistrationTypes] = useState([]); // Array of {id, value}
  const [departments, setDepartments] = useState([]); // Array of {id, value}
  const [countries, setCountries] = useState([]);
  const [errors, setErrors] = useState({
    email: "",
    mobile: "",
    firstName: "",
    lastName: "",
    country: "",
  });
  const [saving, setSaving] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentInitiated, setPaymentInitiated] = useState(false);
  const [paymentErrorShown, setPaymentErrorShown] = useState(false);
  const [isExistingUser, setIsExistingUser] = useState(!!existingUser);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsError, setTermsError] = useState("");
  const [hasReadTerms, setHasReadTerms] = useState(false);
  const [pendingProceedToPayment, setPendingProceedToPayment] = useState(false);
  const [conferenceId, setConferenceId] = useState(null);
  const [conferenceName, setConferenceName] = useState(null);
  const [registrationFee, setRegistrationFee] = useState(null);
  const [feeLoading, setFeeLoading] = useState(false);
  const [feeError, setFeeError] = useState(null);
  const [paperDiscountRules, setPaperDiscountRules] = useState([]);
  const [submittedPaperCount, setSubmittedPaperCount] = useState(0);
  const [studentIdCardFile, setStudentIdCardFile] = useState(null);
  const [studentIdCardError, setStudentIdCardError] = useState("");

  // Accompanying persons state
  const [accompanyingPersons, setAccompanyingPersons] = useState([]);
  const [accompanyingPersonFee, setAccompanyingPersonFee] = useState(null);

  // Conveyance fee state (percentage from API)
  const [conveyanceFeePercentage, setConveyanceFeePercentage] = useState(null);

  // Focused sessions state
  const [registerForFocusedSessions, setRegisterForFocusedSessions] =
    useState("No");
  const [focusedSessions, setFocusedSessions] = useState([]);
  const [selectedFocusedSessions, setSelectedFocusedSessions] = useState([]);
  const [focusedSessionFees, setFocusedSessionFees] = useState({}); // Store fees by session ID

  // Gender options state
  const [genderOptions, setGenderOptions] = useState([]);

  // Paper/Abstract submission state
  const [abstractStepNumber, setAbstractStepNumber] = useState(0); // 0=hide, 3=step 3, 7=step 7
  const [abstractConfig, setAbstractConfig] = useState(null);
  const [paperFile, setPaperFile] = useState(null);
  const [paperFileError, setPaperFileError] = useState("");
  const [abstractText, setAbstractText] = useState("");
  const [paperTitle, setPaperTitle] = useState("");
  const [selectedTrack, setSelectedTrack] = useState("");
  const [presentationMode, setPresentationMode] = useState(""); // ORAL or POSTER
  const [submissionMethod, setSubmissionMethod] = useState(""); // TEXTAREA, FILE, or BOTH
  const [abstractWordCount, setAbstractWordCount] = useState(0);
  const [tracks, setTracks] = useState([]); // Array of tracks from API
  const [maxAbstractChars, setMaxAbstractChars] = useState(1000); // Default 1000 chars
  const [maxFileSizeMb, setMaxFileSizeMb] = useState(10); // Default 10MB
  const [coAuthors, setCoAuthors] = useState([]); // Array of co-authors {name, email, phone, specialization, designation}
  const [coAuthorErrors, setCoAuthorErrors] = useState([]); // Array of validation errors for co-authors
  const [wantsToSubmitAbstract, setWantsToSubmitAbstract] = useState(null); // null = not decided, true = yes, false = no (delegate)
  const [isPaperLocked, setIsPaperLocked] = useState(false); // true if paper is in review/accepted/rejected status
  const [paperFileUploaded, setPaperFileUploaded] = useState(false); // true if paper file was previously uploaded
  const [paperFileName, setPaperFileName] = useState(""); // name of previously uploaded file
  const [existingPaperId, setExistingPaperId] = useState(null); // ID of existing paper for updates
  const [hasPaperSubmitted, setHasPaperSubmitted] = useState(false); // true if user has already submitted a paper
  const [paperApprovalStatus, setPaperApprovalStatus] = useState(null); // current paper status (used to gate payment in step-3 mode)
  const [paperSubmissionAction, setPaperSubmissionAction] = useState(null); // "draft" or "submit" - controls whether paper is saved as DRAFT or SUBMITTED
  const [showPaperSubmissionSummary, setShowPaperSubmissionSummary] =
    useState(false); // true to show paper submission summary page (step 3.x)

  // Track if registration has been updated (for payment validation)
  const [registrationJustUpdated, setRegistrationJustUpdated] = useState(false);

  // State to preserve category-specific data when switching categories
  const [savedMembershipNumber, setSavedMembershipNumber] = useState("");
  const [savedStudentIdCard, setSavedStudentIdCard] = useState(null);
  const [savedStudentIdCardName, setSavedStudentIdCardName] = useState("");
  const [savedStudentIdCardType, setSavedStudentIdCardType] = useState("");

  // Post-payment category and registration type history (for append mode)
  const [categoriesHistory, setCategoriesHistory] = useState([]);
  const [registrationTypesHistory, setRegistrationTypesHistory] = useState([]);
  const [initialPaidCategoryId, setInitialPaidCategoryId] = useState(null);
  const [initialPaidRegistrationTypeId, setInitialPaidRegistrationTypeId] =
    useState(null);
  const [initialPaidCategoryValue, setInitialPaidCategoryValue] = useState("");
  const [
    initialPaidRegistrationTypeValue,
    setInitialPaidRegistrationTypeValue,
  ] = useState("");

  // Ref to track Razorpay instance and prevent duplicate error handling
  const razorpayInstanceRef = useRef(null);
  const paymentFailureHandledRef = useRef(false);
  const razorpayOpenRef = useRef(false);

  // Ref to track if checkExistingRegistration has loaded conference data
  const conferenceDataLoadedRef = useRef(false);

  // Refs to store navigation decision data (for handling race condition with abstractStepNumber)
  const navigationDecisionRef = useRef({
    isPaid: false,
    hasPaperSubmitted: false,
    paperStatus: null,
    ready: false,
  });
  const registrationCheckRequestIdRef = useRef(0);

  const isPendingPaperApprovalStatus = (status) => {
    const normalized = (status || "").trim().toUpperCase();
    if (!normalized) return false;

    // Route papers back to intermediate summary until explicitly accepted.
    // Includes DRAFT so users can continue from paper-summary flow after login.
    return !["ACCEPTED", "REVISION_REQUESTED", "REJECTED"].includes(normalized);
  };

  // Computed: Is abstract/paper submission required? Based on abstractStepNumber
  // abstractStepNumber: 0 = not required/disabled, 3 = required at step 3, 7 = required at step 7
  const isAbstractRequired =
    abstractStepNumber === 3 || abstractStepNumber === 7;

  const selectedTrackName = (() => {
    if (!selectedTrack) return "";
    const matchedTrack = tracks.find(
      (track) => String(track.id) === String(selectedTrack),
    );
    return matchedTrack?.name || selectedTrack;
  })();

  // Validation functions for co-authors
  const validateCoAuthorEmail = (email) => {
    if (!email || email.trim() === "") return ""; // Empty is allowed (optional field)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) ? "" : "Invalid email format";
  };

  const validateCoAuthorPhone = (phone) => {
    if (!phone || phone.trim() === "") return ""; // Empty is allowed (optional field)
    const phoneRegex =
      /^[+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,9}$/;
    return phoneRegex.test(phone) ? "" : "Invalid phone number format";
  };

  // Helper function to render category/registration type history as pills/badges
  const renderHistoryPills = (historyArray, label) => {
    if (!historyArray || historyArray.length === 0) {
      return null;
    }

    const colors = [
      "#007bff",
      "#28a745",
      "#fd7e14",
      "#dc3545",
      "#6f42c1",
      "#17a2b8",
    ];

    return (
      <div
        className="grid-item grid-item--full"
        style={{ marginBottom: "15px" }}
      >
        <div
          style={{
            padding: "12px 16px",
            backgroundColor: "#f8f9fa",
            border: "1px solid #dee2e6",
            borderRadius: "8px",
          }}
        >
          <div
            style={{
              marginBottom: "10px",
              fontSize: "14px",
              fontWeight: "500",
              color: "#495057",
            }}
          >
            Previously Selected {label}:
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {historyArray.map((item, index) => (
              <div
                key={index}
                style={{
                  display: "inline-block",
                  padding: "6px 12px",
                  backgroundColor: colors[index % colors.length],
                  color: "#fff",
                  borderRadius: "20px",
                  fontSize: "13px",
                  fontWeight: "500",
                  whiteSpace: "nowrap",
                }}
              >
                {item.value}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Initialize form data with existing user data or prefilled email
  const initializeFormData = () => {
    if (existingUser) {
      return {
        id: existingUser.id || "",
        title: existingUser.title || "",
        firstName: existingUser.firstName || "",
        lastName: existingUser.lastName || "",
        email: existingUser.email || "",
        mobile: existingUser.phone || "",
        gender: existingUser.gender || "",
        country: existingUser.country || "",
        state: existingUser.state || "",
        city: existingUser.city || "",
        pincode: existingUser.zipCode || "",
        address: existingUser.address || "",
        affiliation: existingUser.affiliation || "",
        institution: existingUser.institution || "",
        specialization: "",
        designation: "",
        fullNameCertificate: existingUser.getFullName
          ? existingUser.getFullName()
          : `${existingUser.firstName || ""} ${
              existingUser.lastName || ""
            }`.trim(),
        mciRegistered: "No",
        mciNumber: "",
        department: "",
        departmentId: null,
        otherDepartment: "",
        category: "",
        categoryId: null,
        registrationType: "",
        registrationTypeId: null,
        membershipNumber: "",
        isaMembershipNumber: "",
        studentIdCard: null,
        studentIdCardName: "",
        studentIdCardType: "",
        prefix: "",
        conference: "",
      };
    } else {
      return {
        id: "",
        title: "",
        firstName: "",
        lastName: "",
        email: prefilledEmail || "",
        mobile: "",
        gender: "",
        country: "",
        state: "",
        city: "",
        pincode: "",
        address: "",
        affiliation: "",
        institution: "",
        specialization: "",
        designation: "",
        fullNameCertificate: "",
        mciRegistered: "No",
        mciNumber: "",
        department: "",
        departmentId: null,
        otherDepartment: "",
        category: "",
        categoryId: null,
        registrationType: "",
        registrationTypeId: null,
        membershipNumber: "",
        isaMembershipNumber: "",
        studentIdCard: null,
        studentIdCardName: "",
        studentIdCardType: "",
        prefix: "",
        conference: "",
      };
    }
  };

  const [formData, setFormData] = useState(initializeFormData());

  // Update form data when existingUser prop changes (e.g., after login)
  // This useEffect should ONLY update user profile fields, NOT conference-specific fields
  // Conference fields will be populated by checkExistingRegistration
  useEffect(() => {
    if (existingUser && existingUser.email) {
      setFormData((prev) => {
        // Only update user profile if user fields are empty OR if they match the existing user
        // This prevents overwriting when user navigates back from editing
        const shouldUpdate = !prev.email || prev.email === existingUser.email;

        if (!shouldUpdate) {
          return prev;
        }

        // If conference data has been loaded by checkExistingRegistration, don't overwrite it
        if (conferenceDataLoadedRef.current) {
          return prev;
        }

        // ONLY update user profile fields - DO NOT touch conference-specific fields
        // Let checkExistingRegistration handle those
        const updated = {
          ...prev, // Start with all existing fields
          // Update ONLY user profile fields
          id: existingUser.id || prev.id || "",
          title: existingUser.title || prev.title || "",
          firstName: existingUser.firstName || prev.firstName || "",
          lastName: existingUser.lastName || prev.lastName || "",
          email: existingUser.email || prev.email || "",
          mobile: existingUser.phone || prev.mobile || "",
          gender: existingUser.gender || prev.gender || "",
          country: existingUser.country || prev.country || "",
          state: existingUser.state || prev.state || "",
          city: existingUser.city || prev.city || "",
          pincode: existingUser.zipCode || prev.pincode || "",
          address: existingUser.address || prev.address || "",
          affiliation: existingUser.affiliation || prev.affiliation || "",
          institution: existingUser.institution || prev.institution || "",
          fullNameCertificate: existingUser.getFullName
            ? existingUser.getFullName()
            : prev.fullNameCertificate ||
              `${existingUser.firstName || ""} ${
                existingUser.lastName || ""
              }`.trim(),
          // DO NOT include conference fields here - they will be set by checkExistingRegistration
        };

        return updated;
      });
      setIsExistingUser(true);

      // Note: Navigation to step 6 will be handled by checkExistingRegistration
      // after conference data is loaded
    }
  }, [existingUser]);

  // Update form data when prefilledEmail prop changes (e.g., new user registration)
  useEffect(() => {
    if (prefilledEmail && !existingUser) {
      setFormData((prev) => ({
        ...prev,
        email: prefilledEmail,
      }));
      setIsExistingUser(false);
    }
  }, [prefilledEmail, existingUser]);

  // Initialize saved category-specific values on first load or when formData has initial values
  useEffect(() => {
    if (formData.membershipNumber && formData.category === "ISCCM(Members)") {
      setSavedMembershipNumber(formData.membershipNumber);
    }
    if (
      formData.studentIdCard &&
      (formData.category === "Student" || formData.category === "student")
    ) {
      setSavedStudentIdCard(formData.studentIdCard);
      setSavedStudentIdCardName(formData.studentIdCardName);
      setSavedStudentIdCardType(formData.studentIdCardType);
    }
  }, []); // Run only once on mount

  // Debug effect to monitor formData.paid changes
  useEffect(() => {}, [formData.paid]);

  // Reset paymentInitiated flag when navigating to/from Step 7
  // This prevents the button from being stuck in "Payment Initiated" state
  useEffect(() => {
    if (step === 7 && paymentInitiated) {
      setPaymentInitiated(false);
    }
  }, [step]);

  // Automatically skip step 3 if abstractStepNumber is not 3
  // This prevents any direct access or navigation to step 3 when abstract is disabled or at step 7
  useEffect(() => {
    if (step === 3 && abstractStepNumber !== 3) {
      // If we're on step 3 but abstractStepNumber is not 3, redirect to step 4
      setStep(4);
    }
  }, [step, abstractStepNumber]);

  // Basic handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    let nextValue = value;

    if (name === "mobile") {
      nextValue = value.replace(/[^0-9]/g, "");
      if (nextValue.length > MOBILE_MAX_LENGTH) {
        nextValue = nextValue.slice(0, MOBILE_MAX_LENGTH);
      }
    }

    setFormData((p) => ({ ...p, [name]: nextValue }));

    if (["email", "mobile", "firstName", "lastName"].includes(name)) {
      validateField(name, nextValue, {
        value: nextValue,
        country: name === "mobile" ? formData.country : undefined,
      });
    }

    // Clear student ID card error if category or registration type changes away from student
    if (name === "category" || name === "registrationType") {
      const newCategory = name === "category" ? nextValue : formData.category;
      const newRegType =
        name === "registrationType" ? nextValue : formData.registrationType;

      const isStudentCategory =
        newCategory && newCategory.toLowerCase().includes("student");
      const isStudentRegType =
        newRegType && newRegType.toLowerCase().includes("student");

      // If neither is student anymore, clear the error
      if (!isStudentCategory && !isStudentRegType) {
        setStudentIdCardError("");
      }
    }
  };

  // Handle student ID card file upload
  const handleStudentIdCardUpload = async (e) => {
    // Only allow single file upload
    if (e.target.files.length > 1) {
      setStudentIdCardError("Please upload only one file");
      e.target.value = ""; // Clear the input
      return;
    }

    const file = e.target.files[0];

    if (!file) {
      setStudentIdCardFile(null);
      setStudentIdCardError("");
      setFormData((prev) => ({
        ...prev,
        studentIdCard: null,
        studentIdCardName: "",
        studentIdCardType: "",
      }));
      return;
    }

    // Validate file type (JPEG or PDF only)
    const allowedTypes = ["image/jpeg", "image/jpg", "application/pdf"];
    if (!allowedTypes.includes(file.type)) {
      setStudentIdCardError("Please upload only JPEG or PDF files");
      e.target.value = ""; // Clear the input
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      setStudentIdCardError("File size must be less than 5MB");
      e.target.value = ""; // Clear the input
      return;
    }

    // File is valid - convert to base64 and store in state
    // Will be uploaded to database when registration is saved
    setStudentIdCardError("");
    setStudentIdCardFile(file);

    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;

        // Store file data in state (will be sent to backend when saving registration)
        setFormData((prev) => ({
          ...prev,
          studentIdCard: base64String, // Store base64 data
          studentIdCardName: file.name,
          studentIdCardType: file.type,
        }));

        console.log("Student ID card loaded, size:", file.size, "bytes");
      };

      reader.onerror = () => {
        console.error("Error reading file");
        setStudentIdCardError("Failed to read file. Please try again.");
        e.target.value = "";
        setStudentIdCardFile(null);
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Error processing student ID card:", error);
      setStudentIdCardError("Failed to process file. Please try again.");
      e.target.value = ""; // Clear the input
      setStudentIdCardFile(null);
    }
  };

  const handleCountryChange = (opt) => {
    const nextCountry = opt ? opt.value : "";
    setFormData((p) => ({ ...p, country: nextCountry }));
    validateField("country", nextCountry);
    if (formData.mobile) {
      validateField("mobile", formData.mobile, {
        country: nextCountry,
        value: formData.mobile,
      });
    }
  };

  // Accompanying persons handlers
  const handleAddAccompanyingPerson = () => {
    setAccompanyingPersons([
      ...accompanyingPersons,
      {
        name: "",
        paid: false,
        paymentId: null,
        feeTypeId: accompanyingPersonFee?.id, // Set feeTypeId when adding
      },
    ]);
    setRegistrationJustUpdated(false); // Reset flag since user added a new item
  };

  const handleRemoveAccompanyingPerson = (index) => {
    const updated = accompanyingPersons.filter((_, i) => i !== index);
    setAccompanyingPersons(updated);
    setRegistrationJustUpdated(false); // Reset flag since user modified items
  };

  const handleAccompanyingPersonNameChange = (index, value) => {
    const updated = [...accompanyingPersons];
    updated[index] = { ...updated[index], name: value }; // Preserve existing fields
    setAccompanyingPersons(updated);
    setRegistrationJustUpdated(false); // Reset flag since user modified items
  };

  // Focused sessions handlers
  const handleFocusedSessionsChange = (value) => {
    setRegisterForFocusedSessions(value);
    if (value === "Yes" && focusedSessions.length === 0) {
      // Fetch focused sessions when user selects "Yes"
      fetchFocusedSessions();
    }
    if (value === "No") {
      // Clear selected sessions when user selects "No"
      setSelectedFocusedSessions([]);
    }
  };

  const handleFocusedSessionCheckboxChange = async (sessionId) => {
    setRegistrationJustUpdated(false); // Reset flag since user modified focused sessions
    setSelectedFocusedSessions((prev) => {
      if (prev.includes(sessionId)) {
        // Remove if already selected
        // Also remove the fee from the fees object
        setFocusedSessionFees((prevFees) => {
          const newFees = { ...prevFees };
          delete newFees[sessionId];
          return newFees;
        });
        return prev.filter((id) => id !== sessionId);
      } else {
        // Add if not selected - fetch the fee
        fetchFocusedSessionFee(sessionId).then((feeData) => {
          if (feeData) {
            setFocusedSessionFees((prevFees) => ({
              ...prevFees,
              [sessionId]: feeData,
            }));
          }
        });
        return [...prev, sessionId];
      }
    });
  };

  // Enhanced validation helper functions
  const validateEmailAdvanced = (email) => {
    if (!email) return "Email is required.";

    // Basic pattern check
    const emailPattern =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    if (!emailPattern.test(email)) {
      return "Please enter a valid email address format.";
    }

    // Check for disposable email domains
    const disposableDomains = [
      "10minutemail.com",
      "tempmail.org",
      "guerrillamail.com",
      "mailinator.com",
      "trashmail.com",
      "0-mail.com",
      "throwaway.email",
      "temp-mail.org",
      "yopmail.com",
      "emailondeck.com",
      "tempail.com",
      "sharklasers.com",
    ];

    const domain = email.split("@")[1]?.toLowerCase();
    if (disposableDomains.includes(domain)) {
      return "Please use a permanent email address, not a temporary one.";
    }

    // Check for proper domain structure
    if (domain) {
      // Domain should have at least one dot and valid TLD
      const domainParts = domain.split(".");
      if (domainParts.length < 2) {
        return "Email domain must have a valid top-level domain.";
      }

      // Check if TLD is reasonable (2-6 characters)
      const tld = domainParts[domainParts.length - 1];
      if (tld.length < 2 || tld.length > 6) {
        return "Email domain has an invalid top-level domain.";
      }

      // Check for consecutive dots
      if (domain.includes("..")) {
        return "Email address cannot contain consecutive dots.";
      }
    }

    // Check email length
    if (email.length > 254) {
      return "Email address is too long.";
    }

    // Check local part (before @) length
    const localPart = email.split("@")[0];
    if (localPart.length > 64) {
      return "Email address local part is too long.";
    }

    return "";
  };

  const validateMobileAdvanced = (mobile, country) => {
    const normalized = mobile.replace(/[\s()-+]/g, "");
    if (!normalized) return "Mobile number is required.";

    // Check for obviously invalid patterns
    if (!/^\d+$/.test(normalized)) {
      return "Mobile number can only contain digits.";
    }

    // Check for sequential or repeated numbers (like 1111111111 or 1234567890)
    const isSequential = (num) => {
      for (let i = 1; i < num.length; i++) {
        if (parseInt(num[i]) !== parseInt(num[i - 1]) + 1) return false;
      }
      return true;
    };

    const isRepeated = (num) => {
      return num.split("").every((digit) => digit === num[0]);
    };

    if (isRepeated(normalized) || isSequential(normalized)) {
      return "Please enter a valid mobile number.";
    }

    // Country-specific validation
    if (country === "India") {
      // Remove country code if present
      const indianNumber = normalized.replace(/^(?:91|0)/, "");

      if (indianNumber.length !== 10) {
        return "Indian mobile number must be 10 digits.";
      }

      // Check if starts with valid digits (6,7,8,9)
      if (!/^[6-9]/.test(indianNumber)) {
        return "Indian mobile number must start with 6, 7, 8, or 9.";
      }

      // Check for valid operator codes
      const validPrefixes = [
        "60",
        "61",
        "62",
        "63",
        "64",
        "65",
        "66",
        "67",
        "68",
        "69",
        "70",
        "71",
        "72",
        "73",
        "74",
        "75",
        "76",
        "77",
        "78",
        "79",
        "80",
        "81",
        "82",
        "83",
        "84",
        "85",
        "86",
        "87",
        "88",
        "89",
        "90",
        "91",
        "92",
        "93",
        "94",
        "95",
        "96",
        "97",
        "98",
        "99",
      ];

      const prefix = indianNumber.substring(0, 2);
      if (!validPrefixes.includes(prefix)) {
        return "Please enter a valid Indian mobile number.";
      }
    } else if (country === "United States") {
      // Remove country code if present
      const usNumber = normalized.replace(/^1/, "");

      if (usNumber.length !== 10) {
        return "US mobile number must be 10 digits.";
      }

      // Check area code (first 3 digits) - shouldn't start with 0 or 1
      if (/^[01]/.test(usNumber)) {
        return "US phone number area code cannot start with 0 or 1.";
      }

      // Check exchange code (next 3 digits) - shouldn't start with 0 or 1
      const exchange = usNumber.substring(3, 6);
      if (/^[01]/.test(exchange)) {
        return "US phone number exchange code cannot start with 0 or 1.";
      }
    } else if (country === "United Kingdom") {
      // UK mobile numbers start with 07 and are 11 digits total
      const ukNumber = normalized.replace(/^44/, "");

      if (!ukNumber.startsWith("07") || ukNumber.length !== 11) {
        return "UK mobile number must start with 07 and be 11 digits.";
      }
    } else if (country === "Australia") {
      // Australian mobile numbers start with 04 and are 10 digits total
      const auNumber = normalized.replace(/^61/, "");

      if (!auNumber.startsWith("04") || auNumber.length !== 10) {
        return "Australian mobile number must start with 04 and be 10 digits.";
      }
    } else if (country === "Canada") {
      // Same as US format
      const caNumber = normalized.replace(/^1/, "");

      if (caNumber.length !== 10) {
        return "Canadian mobile number must be 10 digits.";
      }

      if (/^[01]/.test(caNumber)) {
        return "Canadian phone number area code cannot start with 0 or 1.";
      }
    } else {
      // Generic international validation
      if (normalized.length < 7 || normalized.length > 15) {
        return "Mobile number must be between 7 and 15 digits.";
      }

      // Should not start with 00 (typically used for international dialing)
      if (normalized.startsWith("00")) {
        return "Please enter the mobile number without international dialing codes.";
      }
    }

    return "";
  };

  const validateField = (field, rawValue, overrides = {}) => {
    if (
      !["email", "mobile", "firstName", "lastName", "country"].includes(field)
    )
      return true;

    const valueSource = overrides.value ?? rawValue;
    const value = typeof valueSource === "string" ? valueSource.trim() : "";
    const country = overrides.country ?? formData.country;
    let error = "";

    if (field === "firstName") {
      if (!value) {
        error = "First name is required.";
      } else if (value.length < 2) {
        error = "First name must be at least 2 characters.";
      } else if (!/^[a-zA-Z\s.'-]+$/.test(value)) {
        error =
          "First name can only contain letters, spaces, dots, apostrophes, and hyphens.";
      }
    }

    if (field === "lastName") {
      if (!value) {
        error = "Last name is required.";
      } else if (value.length < 1) {
        error = "Last name must be at least 2 characters.";
      } else if (!/^[a-zA-Z\s.'-]+$/.test(value)) {
        error =
          "Last name can only contain letters, spaces, dots, apostrophes, and hyphens.";
      }
    }

    if (field === "country") {
      if (!value) {
        error = "Country is required.";
      }
    }

    if (field === "email") {
      error = validateEmailAdvanced(value);
    }

    if (field === "mobile") {
      error = validateMobileAdvanced(value, country);
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
    return !error;
  };

  const validateAllMandatoryFields = () => {
    const firstNameValid = validateField("firstName", formData.firstName);
    const lastNameValid = validateField("lastName", formData.lastName);
    const emailValid = validateField("email", formData.email);
    const mobileValid = validateField("mobile", formData.mobile, {
      country: formData.country,
      value: formData.mobile,
    });
    const countryValid = validateField("country", formData.country);
    return (
      firstNameValid &&
      lastNameValid &&
      emailValid &&
      mobileValid &&
      countryValid
    );
  };

  // Check if user is already registered for this conference and auto-populate data
  const checkExistingRegistration = async (email, conferenceNameParam) => {
    if (!email) {
      return;
    }

    const requestId = ++registrationCheckRequestIdRef.current;
    const isLatestRequest = () =>
      registrationCheckRequestIdRef.current === requestId;
    const safeSetStep = (nextStep) => {
      if (!isLatestRequest()) {
        console.log(
          "⏭️ Skipping stale step update from older registration check:",
          nextStep,
        );
        return;
      }
      setStep(nextStep);
    };

    // Reset paper submission status for fresh check
    setHasPaperSubmitted(false);
    setPaperApprovalStatus(null);

    try {
      // Always fetch user's registration data to check what they have
      const fetchUrl = `${REGISTRATION_BASE_URL}/registration/user/${encodeURIComponent(
        email,
      )}/${encodeURIComponent(conferenceNameParam)}`;

      const dataResponse = await fetch(fetchUrl);

      if (dataResponse.ok) {
        const registrations = await dataResponse.json();

        // Debug each registration's conference field
        registrations.forEach((reg, index) => {});

        // Find the registration for this specific conference
        // Compare against conference name (from API), not currentHost (which is hostname like localhost:3000)

        const existingReg = registrations.find(
          (reg) => reg.conference === conferenceNameParam && !reg.cancelled,
        );

        if (existingReg && existingReg.user) {
          // Debug category and registration type fields from API

          // Debug payment status fields

          // Determine payment status based on actual API response structure
          // Priority: Use 'paid' field from backend if available, otherwise check payment records
          const isPaid =
            existingReg.paid === true ||
            (existingReg.payment &&
              Array.isArray(existingReg.payment) &&
              existingReg.payment.length > 0 &&
              existingReg.payment.some(
                (p) => p.status === "SUCCESS" || p.status === "COMPLETED",
              )) ||
            existingReg.confirmationDate !== null;

          // Debug the actual fields that exist in the API response

          if (existingReg.payment) {
          }

          // Test each condition separately

          if (existingReg.payment) {
          }

          // Auto-populate form with existing registration data
          setFormData((prev) => {
            // Mark that conference data has been loaded
            conferenceDataLoadedRef.current = true;

            const newFormData = {
              ...prev,
              // User details from existing registration
              email: existingReg.user.email,
              title: existingReg.user.title || "",
              firstName: existingReg.user.firstName || "",
              lastName: existingReg.user.lastName || "",
              mobile: existingReg.user.phone || "",
              gender: existingReg.user.gender || "",
              country: existingReg.user.country || "",
              state: existingReg.user.state || "",
              city: existingReg.user.city || "",
              address: existingReg.user.address || "",
              pincode: existingReg.user.zipCode || "",
              affiliation: existingReg.affiliation || "",
              institution: existingReg.institution || "",
              // Conference details from existing registration
              conference: existingReg.conference,
              category: existingReg.category || "",
              categoryId: existingReg.categoryId || null,
              registrationType: existingReg.registrationType || "",
              registrationTypeId: existingReg.registrationTypeId || null,
              membershipNumber: existingReg.membershipNumber || "",
              isaMembershipNumber: existingReg.isaMembershipNumber || "",
              department: existingReg.department || "",
              departmentId: existingReg.departmentId || null,
              otherDepartment: existingReg.otherDepartment || "",
              // Student ID - only set if there's actually a file stored
              studentIdCard: existingReg.studentIdURL
                ? existingReg.studentIdURL
                : null,
              studentIdCardUploaded: existingReg.studentIdURL ? true : false, // Only mark as uploaded if there's a URL
              // MCI details from existing registration
              mciRegistered: existingReg.mciRegistered || "No",
              mciNumber: existingReg.mciNumber || "",
              specialRequirements: existingReg.specialRequirements || "",
              // JSON column fields - extract from additionalData map
              dietaryRestrictions:
                existingReg.additionalData?.dietaryRestrictions || "",
              specialization:
                existingReg.additionalData?.specialization ||
                existingReg.additionalData?.specilization ||
                "",
              designation: existingReg.additionalData?.designation || "",
              // Payment status - use calculated value (no confirmed field in database)
              paid: isPaid,
              // Set existing registration IDs for future operations
              id: existingReg.registrationId,
              regId: existingReg.id,
              // Store additional fees for display in step-5 and protection in step-4
              additionalFees:
                existingReg.additionalFees &&
                Array.isArray(existingReg.additionalFees)
                  ? existingReg.additionalFees
                  : [],
              // Store zero payments (free items) for display in step-5
              zeroPayments:
                existingReg.zeroPayments &&
                Array.isArray(existingReg.zeroPayments)
                  ? existingReg.zeroPayments
                  : [],
              // Store payment data with breakdown (if available)
              payment:
                existingReg.payment && Array.isArray(existingReg.payment)
                  ? existingReg.payment
                  : [],
            };

            console.log("💾 Loaded zeroPayments:", existingReg.zeroPayments);

            return newFormData;
          });

          // Load category and registration type history from additionalData
          // Always set arrays (or empty arrays) to avoid stale UI state between logins.
          setCategoriesHistory(
            Array.isArray(existingReg.additionalData?.categoriesHistory)
              ? existingReg.additionalData.categoriesHistory
              : [],
          );
          setRegistrationTypesHistory(
            Array.isArray(existingReg.additionalData?.registrationTypesHistory)
              ? existingReg.additionalData.registrationTypesHistory
              : [],
          );
          if (isPaid) {
            setInitialPaidCategoryId(existingReg.categoryId || null);
            setInitialPaidRegistrationTypeId(
              existingReg.registrationTypeId || null,
            );
            setInitialPaidCategoryValue(existingReg.category || "");
            setInitialPaidRegistrationTypeValue(
              existingReg.registrationType || "",
            );
          } else {
            setInitialPaidCategoryId(null);
            setInitialPaidRegistrationTypeId(null);
            setInitialPaidCategoryValue("");
            setInitialPaidRegistrationTypeValue("");
          }

          console.log("🔄 Loading existing registration data...");
          console.log(
            "  existingReg.additionalFees:",
            existingReg.additionalFees,
          );
          console.log("  existingReg.zeroPayments:", existingReg.zeroPayments);
          console.log("  accompanyingPersonFee:", accompanyingPersonFee);

          // Track paper existence/status for post-login navigation.
          let hasPaperSubmitted = false;
          let loadedPaperStatus = null;

          // Fallback signal from saved registration payload.
          const savedWantsToSubmitAbstract =
            existingReg.additionalData?.wantsToSubmitAbstract === true ||
            existingReg.additionalData?.wantsToSubmitAbstract === "true";

          // Fetch and load paper/abstract data if it exists
          try {
            const candidateRegistrationIds = [
              existingReg.id,
              existingReg.registrationId,
            ].filter(
              (value, index, self) =>
                value != null && self.indexOf(value) === index,
            );

            console.log(
              "🔍 Attempting paper lookup with registration IDs:",
              candidateRegistrationIds,
            );

            let papers = [];
            for (const candidateId of candidateRegistrationIds) {
              const paperResponse = await fetch(
                `${REGISTRATION_BASE_URL}/api/papers/registration/${candidateId}`,
              );
              console.log(
                "🔍 Fetching papers for registration ID:",
                candidateId,
              );
              console.log(
                "🔍 Paper API URL:",
                `${REGISTRATION_BASE_URL}/api/papers/registration/${candidateId}`,
              );
              console.log(
                "🔍 Paper API response status:",
                paperResponse.status,
              );

              if (!paperResponse.ok) {
                continue;
              }

              const result = await paperResponse.json();
              console.log("🔍 Papers array from API:", result);
              console.log("🔍 Number of papers:", result?.length);

              if (Array.isArray(result) && result.length > 0) {
                papers = result;
                break;
              }
            }

            if (papers && papers.length > 0) {
              hasPaperSubmitted = true;
              setHasPaperSubmitted(true); // Update state for later use
              const paper = papers[0]; // Get the first paper
              console.log("📄 Loading existing paper data:", paper);
              console.log("  📝 id:", paper.id);
              console.log("  📝 title:", paper.title);
              console.log(
                "  📝 abstractText:",
                paper.abstractText
                  ? `"${paper.abstractText.substring(0, 50)}..."`
                  : "NULL or EMPTY",
              );
              console.log("  📝 fileId:", paper.fileId);
              console.log("  📝 status:", paper.status);
              console.log("  📝 trackId:", paper.trackId);
              console.log("  📝 presentationMode:", paper.presentationMode);

              // Store the existing paper ID for updates
              setExistingPaperId(paper.id);
              console.log("💾 Stored existing paper ID for updates:", paper.id);

              // Check if paper is in review mode (locked for editing)
              // REVISION_REQUESTED is not locked - authors can edit when revisions are requested
              const lockedStatuses = [
                "SUBMITTED",
                "UNDER_REVIEW",
                "ACCEPTED",
                "REJECTED",
                "REVIEWED",
              ];
              const isLocked = lockedStatuses.includes(paper.status);
              setIsPaperLocked(isLocked);
              setPaperApprovalStatus(paper.status || null);
              loadedPaperStatus = paper.status || null;

              if (isLocked) {
                console.log(
                  "🔒 Paper is locked for editing. Status:",
                  paper.status,
                );
              } else {
                console.log("🔓 Paper can be edited. Status:", paper.status);
              }

              // Set paper data in state
              setPaperTitle(paper.title || "");
              setAbstractText(paper.abstractText || "");
              setPresentationMode(paper.presentationMode || "");
              setSelectedTrack(paper.trackId || null);
              setWantsToSubmitAbstract(true); // User has submitted a paper

              // Check if file exists by making a HEAD request to the file endpoint
              let hasFile = false;
              let actualFileName = "uploaded-paper.pdf";
              const paperRegistrationId =
                paper.registrationId ||
                existingReg.id ||
                existingReg.registrationId;
              try {
                // Try to fetch the paper file to check if it exists
                const fileCheckUrl = `${REGISTRATION_BASE_URL}/registration/${paperRegistrationId}/file/paper`;
                console.log("🔍 Checking for file at:", fileCheckUrl);

                const fileCheckRes = await fetch(fileCheckUrl, {
                  method: "HEAD",
                });
                console.log(
                  "🔍 File check response status:",
                  fileCheckRes.status,
                );

                if (fileCheckRes.ok) {
                  hasFile = true;
                  // Extract filename from Content-Disposition header if available
                  const contentDisposition = fileCheckRes.headers.get(
                    "Content-Disposition",
                  );
                  console.log(
                    "🔍 Content-Disposition header:",
                    contentDisposition,
                  );

                  if (contentDisposition) {
                    // Parse filename from Content-Disposition header
                    // Format: inline; filename="CTC Breakup - Jayakumar M.pdf"
                    const filenameMatch = contentDisposition.match(
                      /filename\s*=\s*"([^"]+)"/,
                    );
                    if (filenameMatch && filenameMatch[1]) {
                      actualFileName = filenameMatch[1];
                      console.log("📎 Extracted filename:", actualFileName);
                    } else {
                      console.log("📎 Could not extract filename from header");
                    }
                  }
                  console.log(
                    "📎 Paper file exists. Final filename:",
                    actualFileName,
                  );
                } else {
                  console.log(
                    "📎 No paper file found, status:",
                    fileCheckRes.status,
                  );
                }
              } catch (error) {
                console.log("📎 Error checking file:", error.message);
                hasFile = false;
              }

              if (hasFile) {
                setPaperFileUploaded(true);
                setPaperFileName(actualFileName);
                console.log(
                  "✅ Set paper file uploaded. Filename:",
                  actualFileName,
                );
              }

              // Infer submission method from the data
              const hasAbstract =
                paper.abstractText && paper.abstractText.trim().length > 0;

              if (hasAbstract && hasFile) {
                setSubmissionMethod("BOTH");
                console.log(
                  "📝 Submission method: BOTH (has abstract and file)",
                );
              } else if (hasAbstract) {
                setSubmissionMethod("TEXTAREA");
                console.log(
                  "📝 Submission method: TEXTAREA (has abstract only)",
                );
              } else if (hasFile) {
                setSubmissionMethod("FILE");
                console.log("📝 Submission method: FILE (has file only)");
              } else {
                // Default to TEXTAREA if nothing is found (shouldn't normally happen)
                setSubmissionMethod("TEXTAREA");
                console.log(
                  "⚠️ No abstract or file found, defaulting to TEXTAREA",
                );
              }

              // Fetch and load co-authors for this paper
              try {
                const coAuthorsResponse = await fetch(
                  `${REGISTRATION_BASE_URL}/api/coauthors/paper/${paper.id}`,
                );
                if (coAuthorsResponse.ok) {
                  const coAuthorsData = await coAuthorsResponse.json();
                  if (coAuthorsData && coAuthorsData.length > 0) {
                    console.log(
                      "👥 Loading existing co-authors:",
                      coAuthorsData,
                    );
                    setCoAuthors(
                      coAuthorsData.map((ca) => ({
                        name: ca.name || "",
                        email: ca.email || "",
                        phone: ca.phone || "",
                        specialization: ca.specialization || "",
                        designation: ca.designation || "",
                      })),
                    );
                  }
                }
              } catch (error) {
                console.error("Error loading co-authors:", error);
              }
            }

            // Fallback: if registration-based lookup misses, verify by user+conference.
            // This catches cases where paper rows exist but are linked via a different registration ID.
            if (!hasPaperSubmitted && email && conferenceNameParam) {
              try {
                const paperCountRes = await fetch(
                  `${REGISTRATION_BASE_URL}/api/papers/count?email=${encodeURIComponent(email)}&conference=${encodeURIComponent(conferenceNameParam)}`,
                );
                if (paperCountRes.ok) {
                  const paperCount = await paperCountRes.json();
                  const parsedCount = Number(paperCount);
                  if (Number.isFinite(parsedCount) && parsedCount > 0) {
                    console.log(
                      "🔍 Paper count fallback detected existing paper(s):",
                      parsedCount,
                    );
                    hasPaperSubmitted = true;
                    setHasPaperSubmitted(true);
                    setWantsToSubmitAbstract(true);
                    // Default to DRAFT flow when status is unavailable from fallback endpoint.
                    if (!loadedPaperStatus) {
                      loadedPaperStatus = "DRAFT";
                      setPaperApprovalStatus("DRAFT");
                    }
                  }
                }
              } catch (fallbackError) {
                console.error(
                  "Error in paper count fallback lookup:",
                  fallbackError,
                );
              }
            }

            if (!hasPaperSubmitted && savedWantsToSubmitAbstract) {
              console.log(
                "🔍 additionalData fallback indicates abstract flow exists",
              );
              hasPaperSubmitted = true;
              setHasPaperSubmitted(true);
              setWantsToSubmitAbstract(true);
              if (!loadedPaperStatus) {
                loadedPaperStatus = "DRAFT";
                setPaperApprovalStatus("DRAFT");
              }
            }
          } catch (error) {
            console.error("Error loading paper data:", error);
          }

          // Load accompanying persons and focused sessions from additionalFees
          const storedAdditionalFees =
            existingReg.additionalFees &&
            Array.isArray(existingReg.additionalFees)
              ? existingReg.additionalFees
              : [];

          console.log("📦 Stored additional fees:", storedAdditionalFees);

          // Load zero payments once for both accompanying persons and focused sessions
          const storedZeroPayments =
            existingReg.zeroPayments && Array.isArray(existingReg.zeroPayments)
              ? existingReg.zeroPayments
              : [];

          console.log("📦 Stored zero payments:", storedZeroPayments);

          if (storedAdditionalFees.length > 0 && accompanyingPersonFee) {
            // Separate accompanying persons from focused sessions
            // Load from BOTH additionalFees AND zeroPayments
            const accompanyingPersonsFromAdditionalFees = storedAdditionalFees
              .filter((fee) => fee.feeTypeId === accompanyingPersonFee.id)
              .map((fee) => {
                console.log(
                  "  - Accompanying person from additionalFees:",
                  fee,
                );
                return {
                  name: fee.name,
                  paymentId: fee.paymentId,
                  paid: !!fee.paymentId,
                  feeTypeId: fee.feeTypeId,
                  id: fee.id,
                };
              });

            const accompanyingPersonsFromZeroPayments = storedZeroPayments
              .filter((zp) => zp.feeTypeId === accompanyingPersonFee.id)
              .map((zp) => {
                console.log("  - Accompanying person from zeroPayments:", zp);
                return {
                  name: zp.name,
                  paymentId: null, // Zero payments don't have paymentId
                  paid: true, // But they are saved in the database
                  feeTypeId: zp.feeTypeId,
                  id: zp.id,
                };
              });

            const accompanyingPersonsList = [
              ...accompanyingPersonsFromAdditionalFees,
              ...accompanyingPersonsFromZeroPayments,
            ];

            console.log(
              "👥 Accompanying persons from additionalFees:",
              accompanyingPersonsFromAdditionalFees,
            );
            console.log(
              "👥 Accompanying persons from zeroPayments:",
              accompanyingPersonsFromZeroPayments,
            );
            console.log(
              "👥 Combined accompanying persons:",
              accompanyingPersonsList,
            );

            if (accompanyingPersonsList.length > 0) {
              setAccompanyingPersons(accompanyingPersonsList);
            }

            // Load focused sessions (non-accompanying person fees) from BOTH additionalFees AND zeroPayments
            const focusedSessionsFromAdditionalFees = storedAdditionalFees
              .filter((fee) => fee.feeTypeId !== accompanyingPersonFee.id)
              .map((fee) => fee.feeTypeId);

            const focusedSessionsFromZeroPayments = storedZeroPayments
              .filter((zp) => zp.feeTypeId !== accompanyingPersonFee.id)
              .map((zp) => zp.feeTypeId);

            // Combine both lists and remove duplicates
            const focusedSessionsList = [
              ...new Set([
                ...focusedSessionsFromAdditionalFees,
                ...focusedSessionsFromZeroPayments,
              ]),
            ];

            console.log(
              "🎯 Focused sessions from additional fees:",
              focusedSessionsFromAdditionalFees,
            );
            console.log(
              "🎯 Focused sessions from zero payments:",
              focusedSessionsFromZeroPayments,
            );
            console.log("🎯 Combined focused sessions:", focusedSessionsList);

            if (focusedSessionsList.length > 0) {
              setRegisterForFocusedSessions("Yes");
              setSelectedFocusedSessions(focusedSessionsList);
              console.log(
                "✅ Set selectedFocusedSessions to:",
                focusedSessionsList,
              );
              console.log("📍 conferenceId available?", conferenceId);

              // Fetch fees for these sessions (needed to display rates)
              // Wait a bit if conferenceId is not available yet
              if (conferenceId) {
                console.log("✅ conferenceId is available, fetching fees now");
                focusedSessionsList.forEach((sessionId) => {
                  fetchFocusedSessionFee(sessionId).then((feeData) => {
                    console.log(
                      `💰 Fetched fee for session ${sessionId}:`,
                      feeData,
                    );
                    if (feeData) {
                      setFocusedSessionFees((prevFees) => ({
                        ...prevFees,
                        [sessionId]: feeData,
                      }));
                    }
                  });
                });
              } else {
                console.warn(
                  "⚠️ conferenceId not available yet, will fetch fees later",
                );
              }
            } else {
              console.log("⚠️ No focused sessions to load (list is empty)");
            }
          } else if (storedAdditionalFees.length > 0) {
            // Fallback: if accompanyingPersonFee not loaded yet, map all as accompanying persons
            console.log(
              "⚠️ accompanyingPersonFee not loaded, mapping all as accompanying persons",
            );
            const accompanyingPersonsList = storedAdditionalFees.map((fee) => ({
              name: fee.name,
              paymentId: fee.paymentId,
              paid: !!fee.paymentId,
              feeTypeId: fee.feeTypeId,
              id: fee.id,
            }));

            if (accompanyingPersonsList.length > 0) {
              setAccompanyingPersons(accompanyingPersonsList);
            }
          }

          // Store navigation decision data for later use (after abstractStepNumber is loaded)
          console.log("💾 Navigation decision data:");
          console.log("  - isPaid:", isPaid);
          console.log("  - hasPaperSubmitted:", hasPaperSubmitted);
          console.log("  - paperStatus:", loadedPaperStatus);

          const isPaperPendingApproval =
            hasPaperSubmitted &&
            (isPendingPaperApprovalStatus(loadedPaperStatus) ||
              !loadedPaperStatus);

          // Update navigationDecisionRef for use in navigation useEffect
          navigationDecisionRef.current = {
            isPaid: isPaid,
            hasPaperSubmitted: hasPaperSubmitted,
            paperStatus: loadedPaperStatus,
            ready: true,
          };

          // Pending-review submissions should return to the intermediate paper summary (step 3.x).
          if (isPaperPendingApproval) {
            console.log(
              "🎯 Paper pending approval - routing to step 3 paper summary",
            );
            navigationDecisionRef.current.ready = false;
            setShowPaperSubmissionSummary(true);
            safeSetStep(3);
            return;
          }

          // Navigate to summary page by default
          // The abstractStepNumber will be set by the metadata fetch useEffect
          // If user is paid and hasn't submitted paper in pay & register mode, navigation useEffect will handle it
          console.log("🎯 Navigating to step 6 - Summary page");
          safeSetStep(6);
        } else if (registrations.length > 0) {
          // User has registrations for other conferences, just populate personal details
          const anyRegistration = registrations[0];
          if (anyRegistration && anyRegistration.user) {
            console.log("🔄 User has registration for OTHER conference");
            console.log("  Other conference:", anyRegistration.conference);
            console.log("  Current conference:", conferenceNameParam);
            console.log(
              "  CRITICAL: Clearing id and regId for NEW conference registration",
            );

            // Only update fields that are empty to preserve data from existingUser prop
            setFormData((prev) => ({
              ...prev,
              // Only populate fields if they're empty (preserve data from login)
              email: prev.email || anyRegistration.user.email,
              firstName: prev.firstName || anyRegistration.user.firstName || "",
              lastName: prev.lastName || anyRegistration.user.lastName || "",
              mobile: prev.mobile || anyRegistration.user.phone || "",
              country: prev.country || anyRegistration.user.country || "",
              state: prev.state || anyRegistration.user.state || "",
              city: prev.city || anyRegistration.user.city || "",
              address: prev.address || anyRegistration.user.address || "",
              pincode: prev.pincode || anyRegistration.user.zipCode || "",
              affiliation:
                prev.affiliation || anyRegistration.affiliation || "",
              institution:
                prev.institution || anyRegistration.institution || "",
              // CRITICAL: Clear registration IDs so new conference creates POST, not PUT
              id: null,
              regId: null,
              // Also clear conference-specific fields
              conference: conferenceNameParam,
              category: "",
              categoryId: null,
              registrationType: "",
              registrationTypeId: null,
            }));
          }

          // Navigate logged-in user to summary page to complete conference-specific details

          safeSetStep(5);
        } else {
          // No registrations found for this conference - treat as new user
          // User data should already be populated from existingUser prop
          console.log("✨ NEW conference registration for existing user");
          console.log("  Conference:", conferenceNameParam);
          console.log(
            "  CRITICAL: Clearing id and regId for NEW conference registration",
          );

          // CRITICAL: Clear registration IDs so new conference creates POST, not PUT
          setFormData((prev) => ({
            ...prev,
            id: null,
            regId: null,
            conference: conferenceNameParam,
            // Clear conference-specific fields
            category: "",
            categoryId: null,
            registrationType: "",
            registrationTypeId: null,
          }));

          // Navigate to step 1 to start fresh registration process

          safeSetStep(1);
        }
      } else {
        console.error(
          "Failed to fetch registration data:",
          dataResponse.status,
        );
        // Still navigate logged-in user to summary page even if fetch fails
        safeSetStep(5);
      }
    } catch (error) {
      console.error("Error checking existing registration:", error);
      // Don't block the user if the check fails - navigate to summary page
      safeSetStep(5);
    }
  };

  // Check existing registration only when existingUser changes (from login)
  useEffect(() => {
    if (existingUser && existingUser.email && conferenceName) {
      // Longer delay to ensure conference data is loaded first
      setTimeout(() => {
        checkExistingRegistration(existingUser.email, conferenceName);
      }, 1000);
    } else if (existingUser && existingUser.email && !conferenceName) {
    }
  }, [existingUser?.email, conferenceName]); // Run when existingUser email OR conferenceName changes

  // Handle navigation after abstractStepNumber is loaded (fixes race condition)
  useEffect(() => {
    // Only navigate if we have navigation data ready AND abstractStepNumber has been loaded (not initial 0)
    if (
      navigationDecisionRef.current.ready &&
      conferenceDataLoadedRef.current
    ) {
      const { isPaid, hasPaperSubmitted, paperStatus } =
        navigationDecisionRef.current;

      console.log("🎯 Navigation useEffect triggered:");
      console.log("  - abstractStepNumber:", abstractStepNumber);
      console.log("  - isPaid:", isPaid);
      console.log("  - hasPaperSubmitted:", hasPaperSubmitted);
      console.log("  - paperStatus:", paperStatus);

      const isPaperPendingApproval =
        hasPaperSubmitted &&
        (isPendingPaperApprovalStatus(paperStatus) || !paperStatus);

      if (isPaperPendingApproval) {
        console.log(
          "🎯 Navigation useEffect: paper pending approval - routing to step 3 paper summary",
        );
        setShowPaperSubmissionSummary(true);
        setStep(3);
        navigationDecisionRef.current.ready = false;
        return;
      }

      // SPECIAL CASE: If abstractStepNumber is 7, payment is complete, and no paper submitted yet,
      // navigate to step 7 (abstract submission) instead of step 6 (summary)
      if (abstractStepNumber === 7 && isPaid && !hasPaperSubmitted) {
        console.log(
          "🎯 Navigating to step 7 - Abstract submission after payment",
        );
        setStep(7); // Navigate to abstract submission step
      } else {
        console.log("🎯 Navigating to step 6 - Summary page");
        setStep(6); // Navigate to summary page
      }

      // Clear the flag so we don't navigate again
      navigationDecisionRef.current.ready = false;
    }
  }, [abstractStepNumber, navigationDecisionRef.current.ready]); // Trigger when abstractStepNumber changes OR navigation data becomes ready

  // Process additional fees and zero payments when accompanyingPersonFee is loaded
  useEffect(() => {
    // Only process if we have formData with fees AND accompanyingPersonFee is now loaded
    if (
      !accompanyingPersonFee ||
      !formData.additionalFees ||
      formData.additionalFees.length === 0
    ) {
      return;
    }

    // CRITICAL: Don't overwrite user's manual changes
    // Only auto-populate on initial load (when accompanyingPersons is empty)
    // If user has already modified the list, don't overwrite it
    if (accompanyingPersons.length > 0) {
      return; // User has data in state, don't overwrite
    }

    // Check if we've already processed focused sessions
    // Only check selectedFocusedSessions - let accompanying persons be reprocessed if needed
    if (selectedFocusedSessions.length > 0) {
      return; // Already processed
    }

    const storedAdditionalFees = formData.additionalFees || [];
    const storedZeroPayments = formData.zeroPayments || [];

    // Separate accompanying persons from focused sessions
    const accompanyingPersonsFromAdditionalFees = storedAdditionalFees
      .filter((fee) => fee.feeTypeId === accompanyingPersonFee.id)
      .map((fee) => ({
        name: fee.name,
        paymentId: fee.paymentId,
        paid: !!fee.paymentId,
        feeTypeId: fee.feeTypeId,
        id: fee.id,
      }));

    const accompanyingPersonsFromZeroPayments = storedZeroPayments
      .filter((zp) => zp.feeTypeId === accompanyingPersonFee.id)
      .map((zp) => ({
        name: zp.name,
        paymentId: null,
        paid: true,
        feeTypeId: zp.feeTypeId,
        id: zp.id,
      }));

    const accompanyingPersonsList = [
      ...accompanyingPersonsFromAdditionalFees,
      ...accompanyingPersonsFromZeroPayments,
    ];

    if (accompanyingPersonsList.length > 0) {
      setAccompanyingPersons(accompanyingPersonsList);
    }

    // Load focused sessions
    const focusedSessionsFromAdditionalFees = storedAdditionalFees
      .filter((fee) => fee.feeTypeId !== accompanyingPersonFee.id)
      .map((fee) => fee.feeTypeId);

    const focusedSessionsFromZeroPayments = storedZeroPayments
      .filter((zp) => zp.feeTypeId !== accompanyingPersonFee.id)
      .map((zp) => zp.feeTypeId);

    const focusedSessionsList = [
      ...new Set([
        ...focusedSessionsFromAdditionalFees,
        ...focusedSessionsFromZeroPayments,
      ]),
    ];

    if (focusedSessionsList.length > 0) {
      setRegisterForFocusedSessions("Yes");
      setSelectedFocusedSessions(focusedSessionsList);

      // Fetch fees for these sessions (needed to display rates)
      focusedSessionsList.forEach((sessionId) => {
        fetchFocusedSessionFee(sessionId).then((feeData) => {
          if (feeData) {
            setFocusedSessionFees((prevFees) => ({
              ...prevFees,
              [sessionId]: feeData,
            }));
          }
        });
      });
    }
  }, [accompanyingPersonFee, formData.additionalFees, formData.zeroPayments]); // Run when accompanyingPersonFee loads

  // Fetch focused session fees when conferenceId becomes available
  useEffect(() => {
    // If we have selected focused sessions but no fees loaded yet, and now conferenceId is available
    if (conferenceId && selectedFocusedSessions.length > 0) {
      // Check which sessions don't have fees loaded yet
      const sessionsNeedingFees = selectedFocusedSessions.filter(
        (sessionId) => !focusedSessionFees[sessionId],
      );

      if (sessionsNeedingFees.length > 0) {
        sessionsNeedingFees.forEach((sessionId) => {
          fetchFocusedSessionFee(sessionId).then((feeData) => {
            if (feeData) {
              setFocusedSessionFees((prevFees) => ({
                ...prevFees,
                [sessionId]: feeData,
              }));
            }
          });
        });
      }
    }
  }, [conferenceId, selectedFocusedSessions, focusedSessionFees]); // Run when conferenceId, selectedFocusedSessions, or focusedSessionFees changes

  // Fetch focused sessions list when "Yes" is selected (either manually or from loaded data)
  useEffect(() => {
    if (
      registerForFocusedSessions === "Yes" &&
      conferenceId &&
      focusedSessions.length === 0
    ) {
      fetchFocusedSessions();
    }
  }, [registerForFocusedSessions, conferenceId, focusedSessions.length]);

  // Fetch gender options when conference ID is available
  useEffect(() => {
    if (conferenceId) {
      fetchGenderOptions();
    }
  }, [conferenceId]);

  // Fetch abstract/paper metadata when conference ID is available
  useEffect(() => {
    if (conferenceId) {
      // Fetch abstract metadata for character limits and other config
      fetch(
        `${CONFERENCE_API}/api/abstract-metadata/conference/${conferenceId}`,
      )
        .then((r) => r.json())
        .then((data) => {
          console.log("Abstract metadata response:", data);
          // API may return either a single object or an array with one object
          const metadata = Array.isArray(data) ? data[0] : data;
          console.log("Abstract metadata:", metadata);
          console.log("Co-authors enabled:", metadata?.coAuthors);
          if (metadata) {
            setAbstractConfig(metadata);
            // Set abstract step number (0=hide, 3=step3, 7=step7)
            let stepNumber = metadata.abstractStepNumber || 0;

            // IMPORTANT: Override logic for pay & register mode (abstractStepNumber = 7)
            // - If user has NOT paid: keep at step 7 (must pay first)
            // - If user has paid but paper NOT submitted: keep at step 7 (show button in summary to navigate)
            // - If user has paid AND paper submitted: override to step 3 (can edit at step 3)
            // CRITICAL: Do NOT override if user is currently on step 7 (payment/abstract page)
            if (
              formData.paid === true &&
              stepNumber === 7 &&
              hasPaperSubmitted &&
              step !== 7
            ) {
              console.log(
                "✅ User has paid AND paper submitted - overriding abstractStepNumber from 7 to 3",
              );
              console.log(
                "   This allows existing paid users to edit papers at step 3",
              );
              stepNumber = 3;
            } else if (
              formData.paid === true &&
              stepNumber === 7 &&
              !hasPaperSubmitted &&
              step !== 7
            ) {
              console.log(
                "💳 User has paid but paper NOT submitted - keeping abstractStepNumber at 7",
              );
              console.log(
                "   Button in summary page will allow navigation to step 7 for paper submission",
              );
            } else if (
              formData.paid === true &&
              stepNumber === 7 &&
              step === 7
            ) {
              console.log(
                "💳 User just completed payment at step 7 - keeping abstractStepNumber at 7",
              );
              console.log(
                "   This allows abstract form to show immediately after payment",
              );
            } else if (!formData.paid && stepNumber === 7) {
              console.log(
                "⏳ User has NOT paid - keeping abstractStepNumber at 7 (pay & register mode)",
              );
              console.log("   Abstract will be shown at step 7 after payment");
            }

            setAbstractStepNumber(stepNumber);
            console.log("Abstract step number (final):", stepNumber);
            // Set max chars from API, default to 1000 if not provided
            setMaxAbstractChars(metadata.maxAbstractChars || 1000);
            // Set max file size from API, default to 10MB if not provided
            setMaxFileSizeMb(metadata.maxFileSizeMb || 10);
            // Set presentation mode default ONLY if there's a single option
            if (metadata.mode) {
              const modes = metadata.mode.split(",").map((m) => m.trim());
              if (modes.length === 1) {
                // Auto-select if only one option available
                setPresentationMode(modes[0]);
              }
              // If multiple modes, force user to select - don't set a default
            }
          } else {
            setAbstractStepNumber(0);
            setMaxAbstractChars(1000);
            setMaxFileSizeMb(10);
          }
        })
        .catch((error) => {
          console.error("Failed to fetch abstract metadata:", error);
          setAbstractStepNumber(0);
          setMaxAbstractChars(1000);
        });
    }
  }, [conferenceId, formData.paid, step, hasPaperSubmitted]);

  // Fetch tracks for paper submission when conference ID is available
  useEffect(() => {
    console.log("🎯 Tracks useEffect triggered - conferenceId:", conferenceId);
    if (conferenceId) {
      const tracksUrl = `${CONFERENCE_API}/api/tracks/conference/${conferenceId}`;
      console.log("📡 Fetching tracks from:", tracksUrl);
      fetch(tracksUrl)
        .then((r) => {
          console.log("📡 Tracks response status:", r.status);
          return r.json();
        })
        .then((data) => {
          console.log("✅ Tracks fetched successfully:", data);
          // Assuming API returns array of tracks with id and name or value
          const tracksArray = Array.isArray(data) ? data : [];
          console.log(
            "📋 Setting tracks array (length " + tracksArray.length + "):",
            tracksArray,
          );
          setTracks(tracksArray);
        })
        .catch((error) => {
          console.error("❌ Failed to fetch tracks:", error);
          setTracks([]);
        });
    } else {
      console.log("⚠️ Cannot fetch tracks - conferenceId is null/undefined");
    }
  }, [conferenceId]);

  // Fetch initial data
  useEffect(() => {
    fetch(`${CONFERENCE_API}/${currentHost}/json`)
      .then((r) => r.json())
      .then((data) => {
        if (data.bannerUrl) setBannerUrl(data.bannerUrl);
        if (data.prefix) {
          setFormData((p) => ({ ...p, prefix: data.prefix }));
        } else {
        }

        // Store conference name for registration
        if (data.name) {
          setConferenceName(data.name);
          setFormData((p) => ({ ...p, conference: data.name }));
        }

        // Extract conference ID and use it for subsequent API calls
        const confId = data.id;
        if (confId) {
          setConferenceId(confId); // Store conference ID in state

          // Fetch categories using conference ID - store full objects with id and value
          fetch(`${CONFERENCE_API}/${confId}/master/type/category/details`)
            .then((r) => r.json())
            .then((categoryData) => {
              // Store full objects: {id, value}
              const categoryObjects = Array.isArray(categoryData)
                ? categoryData
                : [];
              setCategories(categoryObjects);
            })
            .catch(() => setCategories([]));

          // Fetch registration types using conference ID - store full objects with id and value
          fetch(
            `${CONFERENCE_API}/${confId}/master/type/conferenceType/details`,
          )
            .then((r) => r.json())
            .then((regData) => {
              // Store full objects: {id, value}
              const regTypeObjects = Array.isArray(regData) ? regData : [];
              setRegistrationTypes(regTypeObjects);
            })
            .catch(() => setRegistrationTypes([]));

          // Fetch departments using conference ID - store full objects with id and value
          fetch(`${CONFERENCE_API}/${confId}/master/type/Department/details`)
            .then((r) => r.json())
            .then((deptData) => {
              // Store full objects: {id, value}
              const deptObjects = Array.isArray(deptData) ? deptData : [];
              setDepartments(deptObjects);
            })
            .catch(() => setDepartments([]));

          // Fetch focused sessions using conference ID - store full objects with id and value
          fetch(
            `${CONFERENCE_API}/${confId}/master/type/FocussedSessions/details`,
          )
            .then((r) => r.json())
            .then((sessionsData) => {
              // Store full objects: {id, value}
              const sessionsArray = Array.isArray(sessionsData)
                ? sessionsData
                : [];
              setFocusedSessions(sessionsArray);
            })
            .catch(() => setFocusedSessions([]));
        }
      })
      .catch((error) => {
        console.error("Failed to fetch conference data:", error);
      });

    fetch("https://restcountries.com/v3.1/all?fields=name")
      .then((r) => r.json())
      .then((data) => {
        const list = (Array.isArray(data) ? data : [])
          .map((c) => {
            const name =
              typeof c?.name === "string"
                ? c.name
                : c?.name?.common ||
                  c?.name?.official ||
                  c?.name?.nativeName ||
                  null;
            if (!name) return null;
            return { label: name, value: name };
          })
          .filter(Boolean)
          .sort((a, b) => a.label.localeCompare(b.label));
        setCountries(list.length ? list : DEFAULT_COUNTRIES);
      })
      .catch(() => setCountries(DEFAULT_COUNTRIES));
  }, []);

  // Fetch registration fee based on category, registration type, and current date
  const fetchRegistrationFee = async () => {
    // Only fetch if we have at least conferenceId and categoryId
    // registrationTypeId is optional
    if (!conferenceId || !formData.categoryId) {
      setRegistrationFee(null);
      return;
    }

    setFeeLoading(true);
    setFeeError(null);

    try {
      // Get current date in YYYY-MM-DD format
      const today = new Date().toISOString().split("T")[0];

      // Fetch all fees valid for today's date for this conference
      // The backend will filter fees where current date is between valid_from and valid_to
      const feeUrl = `${CONFERENCE_API}/fees/conference/${conferenceId}/valid-on?date=${today}`;

      const response = await fetch(feeUrl);

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("No fee configuration found for the current date");
        }
        throw new Error("Failed to fetch registration fee");
      }

      const validFees = await response.json();

      // Filter fees by participantTypeId (category) and optionally eventTypeId (registration type)
      // If registrationTypeId is provided, match both; otherwise match only by category
      const matchingFee = validFees.find(
        (fee) =>
          fee.participantType?.id === formData.categoryId &&
          fee.isActive === true &&
          (formData.registrationTypeId
            ? fee.eventType?.id === formData.registrationTypeId // Match both if registrationTypeId exists
            : !fee.eventType || fee.eventType === null), // Match category-only fees if no registrationTypeId
      );

      if (!matchingFee) {
        const errorMsg = formData.registrationTypeId
          ? "No fee configuration found for the selected category and registration type"
          : "No fee configuration found for the selected category";
        throw new Error(errorMsg);
      }

      setRegistrationFee(matchingFee);
      setFeeError(null);
    } catch (error) {
      console.error("Error fetching registration fee:", error);
      setFeeError(error.message);
      setRegistrationFee(null);
    } finally {
      setFeeLoading(false);
    }
  };

  // Fetch accompanying person fee
  const fetchAccompanyingPersonFee = async () => {
    if (!conferenceId) {
      setAccompanyingPersonFee(null);
      return;
    }

    try {
      const feeUrl = `${CONFERENCE_API}/fees/conference/${conferenceId}/AccompanyingFee`;

      const response = await fetch(feeUrl);

      if (!response.ok) {
        console.error("❌ Failed to fetch accompanying person fee");
        console.error("Status:", response.status);
        console.error("Status Text:", response.statusText);
        setAccompanyingPersonFee(null);
        return;
      }

      const feeData = await response.json();

      // Check if response is an array or single object
      if (Array.isArray(feeData)) {
        // Find the active accompanying person fee
        const accompanyingFee = feeData.find((fee) => fee.isActive === true);

        if (accompanyingFee) {
          setAccompanyingPersonFee(accompanyingFee);
        } else {
          setAccompanyingPersonFee(null);
        }
      } else if (feeData && typeof feeData === "object") {
        // Single object response

        if (feeData.isActive === true || feeData.isActive === undefined) {
          setAccompanyingPersonFee(feeData);
        } else {
          setAccompanyingPersonFee(null);
        }
      } else {
        setAccompanyingPersonFee(null);
      }
    } catch (error) {
      console.error("Error fetching accompanying person fee:", error);
      setAccompanyingPersonFee(null);
    }
  };

  // Fetch conveyance fee percentage from API
  const fetchConveyanceFeePercentage = async () => {
    if (!conferenceId) {
      setConveyanceFeePercentage(null);
      return;
    }

    try {
      const conveyanceFeeUrl = `${CONFERENCE_API}/${conferenceId}/master/type/conveyanceFee/details`;

      const response = await fetch(conveyanceFeeUrl);

      // If response is not OK (including 404 for deleted items), silently set to null
      if (!response.ok) {
        setConveyanceFeePercentage(null);
        return;
      }

      const conveyanceFeeData = await response.json();

      // Handle both array and single object responses
      let conveyanceFeePercentageValue = null;

      if (Array.isArray(conveyanceFeeData)) {
        // If array is empty (deleted), set to null
        if (conveyanceFeeData.length === 0) {
          setConveyanceFeePercentage(null);
          return;
        }
        // If array, take the first active item
        const activeConveyanceFee =
          conveyanceFeeData.find((fee) => fee.isActive === true) ||
          conveyanceFeeData[0];
        if (activeConveyanceFee) {
          conveyanceFeePercentageValue =
            activeConveyanceFee.value ||
            activeConveyanceFee.percentage ||
            activeConveyanceFee.rate;
        }
      } else if (conveyanceFeeData && typeof conveyanceFeeData === "object") {
        // Single object response
        conveyanceFeePercentageValue =
          conveyanceFeeData.value ||
          conveyanceFeeData.percentage ||
          conveyanceFeeData.rate;
      }

      if (
        conveyanceFeePercentageValue !== null &&
        conveyanceFeePercentageValue !== undefined &&
        conveyanceFeePercentageValue !== ""
      ) {
        const parsedValue = parseFloat(conveyanceFeePercentageValue);
        // Only set if parsedValue is a valid number greater than 0
        if (!isNaN(parsedValue) && parsedValue > 0) {
          setConveyanceFeePercentage(parsedValue);
        } else {
          setConveyanceFeePercentage(null);
        }
      } else {
        setConveyanceFeePercentage(null);
      }
    } catch (error) {
      console.error("Error fetching conveyance fee percentage:", error);
      setConveyanceFeePercentage(null);
    }
  };

  // Fetch focused sessions from API
  const fetchFocusedSessions = async () => {
    if (!conferenceId) {
      setFocusedSessions([]);
      return;
    }

    try {
      const sessionsUrl = `${CONFERENCE_API}/${conferenceId}/master/type/FocussedSessions/details`;

      const response = await fetch(sessionsUrl);

      if (!response.ok) {
        console.error("❌ Failed to fetch focused sessions");
        console.error("Status:", response.status);
        console.error("Status Text:", response.statusText);
        setFocusedSessions([]);
        return;
      }

      const sessionsData = await response.json();

      // Store the sessions array
      const sessionsArray = Array.isArray(sessionsData) ? sessionsData : [];
      setFocusedSessions(sessionsArray);
    } catch (error) {
      console.error("Error fetching focused sessions:", error);
      setFocusedSessions([]);
    }
  };

  // Fetch gender options
  const fetchGenderOptions = async () => {
    if (!conferenceId) {
      setGenderOptions([]);
      return;
    }

    try {
      const genderUrl = `${CONFERENCE_API}/${conferenceId}/master/type/gender/details`;
      console.log("🔍 Fetching gender options from:", genderUrl);

      const response = await fetch(genderUrl);

      if (!response.ok) {
        console.error("❌ Failed to fetch gender options");
        console.error("Status:", response.status);
        setGenderOptions([]);
        return;
      }

      const genderData = await response.json();
      console.log("📦 Gender options received:", genderData);

      // Store the gender options array
      const genderArray = Array.isArray(genderData) ? genderData : [];
      setGenderOptions(genderArray);
    } catch (error) {
      console.error("Error fetching gender options:", error);
      setGenderOptions([]);
    }
  };

  // Fetch focused session fee by session ID
  const fetchFocusedSessionFee = async (sessionId) => {
    if (!conferenceId || !sessionId) {
      return null;
    }

    try {
      const feeUrl = `${CONFERENCE_API}/fees/conference/${conferenceId}/fee/${sessionId}`;
      console.log(
        `🔍 Fetching focused session fee for session ${sessionId} from:`,
        feeUrl,
      );

      const response = await fetch(feeUrl);

      if (!response.ok) {
        console.error(
          `❌ Failed to fetch fee for focused session ${sessionId}`,
        );
        console.error("Status:", response.status);
        console.error("Status Text:", response.statusText);
        return null;
      }

      const feeData = await response.json();
      console.log(
        `📦 Raw API response for session ${sessionId}:`,
        JSON.stringify(feeData, null, 2),
      );
      console.log(`💰 Fee rate extracted:`, feeData?.rate);
      console.log(`💵 Fee currency extracted:`, feeData?.currency);

      // Return the fee data
      return feeData;
    } catch (error) {
      console.error(
        `Error fetching focused session fee for ${sessionId}:`,
        error,
      );
      return null;
    }
  };

  const shouldApplyPaperDiscount =
    (abstractStepNumber === 3 && wantsToSubmitAbstract === true) ||
    abstractStepNumber === 7;

  const normalizedPaperApprovalStatus = (
    paperApprovalStatus || "PENDING"
  ).toUpperCase();

  const isRevisionPaperStatus =
    normalizedPaperApprovalStatus === "MINOR_REVISION" ||
    normalizedPaperApprovalStatus === "MAJOR_REVISION";

  const readablePaperApprovalStatus =
    {
      DRAFT: "Draft",
      SUBMITTED: "Submitted",
      UNDER_REVIEW: "Under Review",
      REVIEWED: "Reviewed",
      REVISION_REQUESTED: "Revision Requested",
      ACCEPTED: "Accepted",
      REJECTED: "Rejected",
    }[normalizedPaperApprovalStatus] || normalizedPaperApprovalStatus;

  const paymentBlockedPaperStatusMessage =
    normalizedPaperApprovalStatus === "REJECTED"
      ? `Paper status: ${readablePaperApprovalStatus}. Payment is unavailable because this submission was rejected. Please contact the conference organizers.`
      : normalizedPaperApprovalStatus === "REVISION_REQUESTED"
        ? `Paper status: ${readablePaperApprovalStatus}. Submit the requested revision and wait for acceptance before payment.`
        : isRevisionPaperStatus
          ? `Paper status: ${readablePaperApprovalStatus}. Please revise your paper and resubmit before payment.`
          : `Paper status: ${readablePaperApprovalStatus}. Payment will be enabled after your paper is accepted.`;

  // In step-3 mode, payment is allowed only after submitted paper is accepted.
  const isPaymentBlockedPendingPaperApproval =
    abstractStepNumber === 3 &&
    hasPaperSubmitted &&
    paperApprovalStatus !== "ACCEPTED";

  const currentPaperSequence = shouldApplyPaperDiscount
    ? Math.max(1, submittedPaperCount + (existingPaperId ? 0 : 1))
    : 0;

  const hasPostPaymentCategoryOrTypeChange =
    formData.paid === true &&
    initialPaidCategoryId !== null &&
    formData.categoryId !== null &&
    (Number(formData.categoryId) !== Number(initialPaidCategoryId) ||
      Number(formData.registrationTypeId || 0) !==
        Number(initialPaidRegistrationTypeId || 0));

  const previousCategoriesHistory = useMemo(() => {
    const base = Array.isArray(categoriesHistory) ? [...categoriesHistory] : [];
    const filtered = base.filter(
      (entry) => Number(entry?.id) !== Number(formData.categoryId),
    );

    const hasInitialInHistory = filtered.some(
      (entry) => Number(entry?.id) === Number(initialPaidCategoryId),
    );

    if (
      formData.paid === true &&
      initialPaidCategoryId !== null &&
      Number(formData.categoryId) !== Number(initialPaidCategoryId) &&
      !hasInitialInHistory
    ) {
      filtered.unshift({
        id: initialPaidCategoryId,
        value: initialPaidCategoryValue || "Previous Category",
      });
    }

    return filtered;
  }, [
    categoriesHistory,
    formData.categoryId,
    formData.paid,
    initialPaidCategoryId,
    initialPaidCategoryValue,
  ]);

  const previousRegistrationTypesHistory = useMemo(() => {
    const base = Array.isArray(registrationTypesHistory)
      ? [...registrationTypesHistory]
      : [];
    const filtered = base.filter(
      (entry) => Number(entry?.id) !== Number(formData.registrationTypeId),
    );

    const hasInitialInHistory = filtered.some(
      (entry) => Number(entry?.id) === Number(initialPaidRegistrationTypeId),
    );

    if (
      formData.paid === true &&
      initialPaidRegistrationTypeId !== null &&
      Number(formData.registrationTypeId || 0) !==
        Number(initialPaidRegistrationTypeId || 0) &&
      !hasInitialInHistory
    ) {
      filtered.unshift({
        id: initialPaidRegistrationTypeId,
        value: initialPaidRegistrationTypeValue || "Previous Registration Type",
      });
    }

    return filtered;
  }, [
    registrationTypesHistory,
    formData.registrationTypeId,
    formData.paid,
    initialPaidRegistrationTypeId,
    initialPaidRegistrationTypeValue,
  ]);

  const effectiveCategoriesHistory = useMemo(() => {
    const base = [...previousCategoriesHistory];
    if (formData.paid === true && formData.category) {
      base.push({ id: formData.categoryId, value: formData.category });
    }
    return base;
  }, [
    previousCategoriesHistory,
    formData.paid,
    formData.category,
    formData.categoryId,
  ]);

  const effectiveRegistrationTypesHistory = useMemo(() => {
    const base = [...previousRegistrationTypesHistory];
    if (formData.paid === true && formData.registrationType) {
      base.push({
        id: formData.registrationTypeId,
        value: formData.registrationType,
      });
    }
    return base;
  }, [
    previousRegistrationTypesHistory,
    formData.paid,
    formData.registrationType,
    formData.registrationTypeId,
  ]);

  const buildAdditionalDataPayload = () => {
    const additionalData = {};

    // Map configured form fields into additionalData (string fields trimmed).
    ADDITIONAL_DATA_FIELD_MAPPINGS.forEach(({ additionalKey, formKey }) => {
      const value = formData?.[formKey];

      if (typeof value === "string") {
        const trimmedValue = value.trim();
        if (trimmedValue) {
          additionalData[additionalKey] = trimmedValue;
        }
        return;
      }

      if (value !== undefined && value !== null && value !== "") {
        additionalData[additionalKey] = value;
      }
    });

    // Persist multi-select append history in JSON column instead of comma-separated strings
    if (effectiveCategoriesHistory.length > 0) {
      additionalData.categoriesHistory = effectiveCategoriesHistory
        .map((entry) => ({
          id: entry?.id ?? null,
          value: entry?.value || "",
          selectedAt: entry?.selectedAt || new Date().toISOString(),
        }))
        .filter((entry) => entry.value);
    }

    if (effectiveRegistrationTypesHistory.length > 0) {
      additionalData.registrationTypesHistory =
        effectiveRegistrationTypesHistory
          .map((entry) => ({
            id: entry?.id ?? null,
            value: entry?.value || "",
            selectedAt: entry?.selectedAt || new Date().toISOString(),
          }))
          .filter((entry) => entry.value);
    }

    return additionalData;
  };

  const getPaperDiscountPercentage = (paperSequence) => {
    if (
      !paperSequence ||
      !Array.isArray(paperDiscountRules) ||
      paperDiscountRules.length === 0
    ) {
      return 0;
    }

    const matchedRule = paperDiscountRules.find((rule) => {
      const from = Number.isFinite(parseInt(rule.paperSequenceFrom, 10))
        ? parseInt(rule.paperSequenceFrom, 10)
        : 1;
      const to = Number.isFinite(parseInt(rule.paperSequenceTo, 10))
        ? parseInt(rule.paperSequenceTo, 10)
        : Number.MAX_SAFE_INTEGER;
      return paperSequence >= from && paperSequence <= to;
    });

    if (!matchedRule) {
      return 0;
    }

    const pct = parseFloat(matchedRule.value);
    if (!Number.isFinite(pct) || pct <= 0) {
      return 0;
    }
    return Math.min(pct, 100);
  };

  const paperDiscountPercentage =
    getPaperDiscountPercentage(currentPaperSequence);

  const calculateOutstandingAmounts = () => {
    const regFee = registrationFee ? parseFloat(registrationFee.rate || 0) : 0;
    const existingAdditionalFees = formData.additionalFees || [];

    const registrationOutstanding = formData.paid
      ? hasPostPaymentCategoryOrTypeChange
        ? regFee
        : 0
      : regFee;

    const outstandingItems = existingAdditionalFees.filter(
      (fee) => !fee.paymentId,
    );
    const outstandingFromAdditionalFees = outstandingItems.reduce(
      (sum, fee) => {
        let itemRate = fee.rate ? parseFloat(fee.rate) : 0;

        if (
          itemRate === 0 &&
          accompanyingPersonFee &&
          fee.feeTypeId === accompanyingPersonFee.id
        ) {
          itemRate = parseFloat(accompanyingPersonFee.rate || 0);
        } else if (itemRate === 0 && focusedSessionFees[fee.feeTypeId]) {
          itemRate = parseFloat(focusedSessionFees[fee.feeTypeId].rate || 0);
        }

        return sum + itemRate;
      },
      0,
    );

    const existingAccompanyingCount = existingAdditionalFees.filter(
      (fee) =>
        accompanyingPersonFee && fee.feeTypeId === accompanyingPersonFee.id,
    ).length;

    const existingFocusedSessionIds = existingAdditionalFees
      .filter(
        (fee) =>
          !accompanyingPersonFee || fee.feeTypeId !== accompanyingPersonFee.id,
      )
      .map((fee) => fee.feeTypeId);

    const newAccompanyingCount = Math.max(
      0,
      accompanyingPersons.length - existingAccompanyingCount,
    );
    const accompanyingRate = accompanyingPersonFee
      ? parseFloat(accompanyingPersonFee.rate || 0)
      : 0;
    const newAccompanyingOutstanding = newAccompanyingCount * accompanyingRate;

    const newFocusedSessionIds = selectedFocusedSessions.filter(
      (sessionId) => !existingFocusedSessionIds.includes(sessionId),
    );
    const newFocusedOutstanding = newFocusedSessionIds.reduce(
      (sum, sessionId) => {
        const sessionFee = focusedSessionFees[sessionId];
        return (
          sum +
          (sessionFee && sessionFee.rate ? parseFloat(sessionFee.rate) : 0)
        );
      },
      0,
    );

    const totalOutstandingBeforeFee =
      registrationOutstanding +
      outstandingFromAdditionalFees +
      newAccompanyingOutstanding +
      newFocusedOutstanding;

    const conveyanceFeeRate =
      conveyanceFeePercentage !== null && conveyanceFeePercentage > 0
        ? conveyanceFeePercentage / 100
        : 0;
    const conveyanceFeeAmount = totalOutstandingBeforeFee * conveyanceFeeRate;
    const totalOutstanding = totalOutstandingBeforeFee + conveyanceFeeAmount;

    return {
      regFee,
      existingAdditionalFees,
      registrationOutstanding,
      outstandingItems,
      outstandingFromAdditionalFees,
      newAccompanyingCount,
      newAccompanyingOutstanding,
      newFocusedSessionIds,
      newFocusedOutstanding,
      totalOutstandingBeforeFee,
      conveyanceFeeRate,
      conveyanceFeeAmount,
      totalOutstanding,
    };
  };

  // Fetch fee when category or registration type changes
  // registrationTypeId is optional - fee can be fetched with just category
  useEffect(() => {
    if (conferenceId && formData.categoryId) {
      fetchRegistrationFee();
    }
  }, [conferenceId, formData.categoryId, formData.registrationTypeId]);

  useEffect(() => {
    if (!conferenceId) {
      setPaperDiscountRules([]);
      return;
    }

    const fetchPaperDiscountRules = async () => {
      try {
        const discountUrl = `${CONFERENCE_API}/${conferenceId}/master/type/paperDiscount/details`;
        const response = await fetch(discountUrl);
        if (!response.ok) {
          setPaperDiscountRules([]);
          return;
        }

        const details = await response.json();
        const normalized = Array.isArray(details) ? details : [];
        setPaperDiscountRules(normalized);
      } catch (error) {
        console.error("Error fetching paper discount rules:", error);
        setPaperDiscountRules([]);
      }
    };

    fetchPaperDiscountRules();
  }, [conferenceId]);

  useEffect(() => {
    const email = formData.email?.trim();
    const conference = (formData.conference || conferenceName || "").trim();
    if (!email || !conference) {
      setSubmittedPaperCount(0);
      return;
    }

    const fetchPaperCount = async () => {
      try {
        const countUrl = `${REGISTRATION_BASE_URL}/api/papers/count?email=${encodeURIComponent(email)}&conference=${encodeURIComponent(conference)}`;
        const response = await fetch(countUrl);
        if (!response.ok) {
          setSubmittedPaperCount(0);
          return;
        }

        const count = await response.json();
        setSubmittedPaperCount(
          Number.isFinite(Number(count)) ? Number(count) : 0,
        );
      } catch (error) {
        console.error("Error fetching submitted paper count:", error);
        setSubmittedPaperCount(0);
      }
    };

    fetchPaperCount();
  }, [formData.email, formData.conference, conferenceName]);

  // Fetch accompanying person fee and conveyance fee only when conference is loaded
  // Separate useEffect to prevent unnecessary refetches when category/registration type changes
  useEffect(() => {
    if (conferenceId) {
      fetchAccompanyingPersonFee();
      fetchConveyanceFeePercentage();
    }
  }, [conferenceId]); // Only re-fetch when conferenceId changes

  // Step navigation with field validation
  const validateCurrentStep = (currentStep) => {
    const requiredFields = [];

    if (currentStep === 1) {
      if (!formData.title || !formData.title.trim())
        requiredFields.push("Title");
      if (!formData.firstName.trim()) requiredFields.push("First Name");
      if (!formData.lastName.trim()) requiredFields.push("Last Name");
      if (!formData.email.trim()) requiredFields.push("Email");
      if (!formData.mobile.trim()) requiredFields.push("Mobile");
      if (
        genderOptions.length > 0 &&
        (!formData.gender || !formData.gender.trim())
      )
        requiredFields.push("Gender");

      // Also validate email and mobile format for step 1
      const emailValid = validateField("email", formData.email);
      const mobileValid = validateField("mobile", formData.mobile, {
        country: formData.country,
        value: formData.mobile,
      });

      if (!emailValid || !mobileValid) {
        return false;
      }
    }

    if (currentStep === 2) {
      if (!formData.country.trim()) requiredFields.push("Country");
      if (!formData.city.trim()) requiredFields.push("City");
      if (!formData.pincode.trim()) requiredFields.push("Pincode");
      if (!formData.address.trim()) requiredFields.push("Address");
    }

    // Handle abstract validation at step 3 or step 7 based on abstractStepNumber
    // Step 3: Abstract submission with choice (submit abstract OR register as delegate)
    if (currentStep === 3 && abstractStepNumber === 3) {
      console.log(`🔍 Step 3 Abstract Validation:`, {
        abstractStepNumber,
        wantsToSubmitAbstract,
      });

      // First check if user has made a choice
      if (wantsToSubmitAbstract === null) {
        console.log(
          "❌ Step 3 validation failed - user hasn't chosen abstract vs delegate",
        );
        requiredFields.push(
          "Please select whether you want to submit an abstract or register as a delegate",
        );
      } else if (wantsToSubmitAbstract === true) {
        // User wants to submit abstract - validate all abstract fields
        if (!paperTitle.trim()) requiredFields.push("Paper Title");
        if (tracks.length > 0 && !selectedTrack) requiredFields.push("Track");
        if (!presentationMode) requiredFields.push("Presentation Mode");
        if (!submissionMethod) requiredFields.push("Submission Method");

        if (submissionMethod === "TEXTAREA" || submissionMethod === "BOTH") {
          if (!abstractText.trim()) requiredFields.push("Structured Abstract");
          if (abstractText.length > maxAbstractChars) {
            alert(
              `Structured Abstract must not exceed ${maxAbstractChars} characters. Current character count: ${abstractText.length}`,
            );
            return false;
          }
        }

        if (submissionMethod === "FILE" || submissionMethod === "BOTH") {
          if (!paperFile && !paperFileUploaded)
            requiredFields.push("Paper File");
        }
      }
      // If wantsToSubmitAbstract === false, user chose delegate only - no validation needed
    }

    // Step 7: Abstract submission after payment (no choice - always submit)
    if (currentStep === 7 && abstractStepNumber === 7) {
      console.log(`🔍 Step 7 Abstract Validation:`, {
        abstractStepNumber,
        paid: formData.paid,
      });

      // At step 7, always validate abstract fields (no choice involved)
      if (!paperTitle.trim()) requiredFields.push("Paper Title");
      if (tracks.length > 0 && !selectedTrack) requiredFields.push("Track");
      if (!presentationMode) requiredFields.push("Presentation Mode");
      if (!submissionMethod) requiredFields.push("Submission Method");

      if (submissionMethod === "TEXTAREA" || submissionMethod === "BOTH") {
        if (!abstractText.trim()) requiredFields.push("Structured Abstract");
        if (abstractText.length > maxAbstractChars) {
          alert(
            `Structured Abstract must not exceed ${maxAbstractChars} characters. Current character count: ${abstractText.length}`,
          );
          return false;
        }
      }

      if (submissionMethod === "FILE" || submissionMethod === "BOTH") {
        if (!paperFile && !paperFileUploaded) requiredFields.push("Paper File");
      }
    }

    if (currentStep === 4) {
      if (!formData.affiliation.trim()) requiredFields.push("Affiliation");
      if (!formData.institution.trim())
        requiredFields.push("Institution or Hospital Name");
      // Check if MCI Number is required when "Yes" is selected
      if (formData.mciRegistered === "Yes" && !formData.mciNumber.trim()) {
        requiredFields.push("MCI Number");
      }
    }

    if (currentStep === 5) {
      // Only require fields that have data configured
      if (
        (categories.length > 0 && !formData.category.trim()) ||
        formData.category == "TBD"
      ) {
        requiredFields.push("Category");
      }
      if (registrationTypes.length > 0 && !formData.registrationType.trim()) {
        requiredFields.push("Registration Type");
      }
      // Check if membership number is required
      if (
        formData.category === "ISCCM(Members)" &&
        !formData.membershipNumber.trim()
      ) {
        requiredFields.push("Membership Number");
      }
      // Check if ISA membership number is required (only for ISA MEMBER, not NON-ISA MEMBER)
      if (
        formData.category &&
        formData.category.toString().toLowerCase().includes("isa") &&
        !formData.category.toString().toLowerCase().includes("non") &&
        !formData.category.toString().toLowerCase().includes("isccm") &&
        (!formData.isaMembershipNumber || !formData.isaMembershipNumber.trim())
      ) {
        requiredFields.push("ISA Membership Number");
      }
      // Check if student ID card is required - check both category and registration type
      const isStudentCategory =
        formData.category &&
        formData.category.toLowerCase().includes("student");
      const isStudentRegistrationType =
        formData.registrationType &&
        formData.registrationType.toLowerCase().includes("student");

      console.log("🎓 Student validation check:");
      console.log("  Category:", formData.category);
      console.log("  Registration Type:", formData.registrationType);
      console.log("  isStudentCategory:", isStudentCategory);
      console.log("  isStudentRegistrationType:", isStudentRegistrationType);
      console.log("  formData.studentIdCard:", formData.studentIdCard);
      console.log("  studentIdCard type:", typeof formData.studentIdCard);

      // Student ID card is required if either category or registration type includes "student"
      const isStudentRegistration =
        isStudentCategory || isStudentRegistrationType;

      // Check if student ID card is present
      // Valid states:
      // 1. Base64 data (new upload): starts with "data:"
      // 2. Database marker: value is "DATABASE"
      // 3. Database URL: value starts with "http" or has a path
      // Invalid states: null, undefined, empty string, or any other value
      const hasValidStudentIdCard =
        formData.studentIdCard &&
        formData.studentIdCard !== null &&
        formData.studentIdCard !== "" &&
        (formData.studentIdCard.startsWith("data:") || // New upload (base64)
          formData.studentIdCard === "DATABASE" || // Previously uploaded to DB
          formData.studentIdCard.startsWith("http") || // URL from DB
          formData.studentIdCard.includes("/")); // File path from DB

      console.log("  hasValidStudentIdCard:", hasValidStudentIdCard);

      if (isStudentRegistration && !hasValidStudentIdCard) {
        console.log(
          "  ❌ VALIDATION FAILED: Student ID Card is required but missing!",
        );
        requiredFields.push("Student ID Card");
        // Set error state for visual feedback
        setStudentIdCardError(
          "Student ID Card is required for student registrations",
        );
      } else if (!isStudentRegistration) {
        // Clear error if no longer a student
        console.log("  ✅ Not a student registration - clearing error");
        setStudentIdCardError("");
      } else {
        console.log("  ✅ Student ID Card validation passed - file present");
        setStudentIdCardError("");
      }

      // Validate accompanying persons - if added, name is mandatory
      accompanyingPersons.forEach((person, index) => {
        if (!person.name || !person.name.trim()) {
          requiredFields.push(`Accompanying Person ${index + 1} Name`);
        }
      });
    }

    console.log("📋 Validation complete for step", currentStep);
    console.log("  Required fields missing:", requiredFields.length);
    if (requiredFields.length > 0) {
      console.log("  Missing fields:", requiredFields);
    }

    if (requiredFields.length > 0) {
      alert(
        `Please fill in the following required fields: ${requiredFields.join(
          ", ",
        )}`,
      );
      return false;
    }

    console.log("✅ All validations passed for step", currentStep);
    return true;
  };

  const next = () => {
    if (!validateCurrentStep(step)) {
      return;
    }

    // Handle abstract step based on abstractStepNumber
    if (abstractStepNumber === 0) {
      // Abstract disabled - skip step 3 entirely
      if (step === 2) {
        setStep(4); // Skip to step 4 (Affiliation)
        return;
      }
    } else if (abstractStepNumber === 3) {
      // Abstract at step 3 - normal flow, no skip needed
    } else if (abstractStepNumber === 7) {
      // Abstract at step 7 AFTER payment - skip step 3
      if (step === 2) {
        setStep(4); // Skip step 3, go to step 4
        return;
      }
      // Step 7 will show payment first, then abstract after payment
    }

    // Special validation before proceeding to payment step
    const paymentStep = abstractStepNumber === 7 ? 8 : 7;
    if (step === 6) {
      // Moving from summary to payment
      if (!formData.id && !formData.regId) {
        alert(
          "Please save your registration before proceeding to payment. Click the 'Save' button first.",
        );
        return;
      }

      if (isPaymentBlockedPendingPaperApproval) {
        alert(paymentBlockedPaperStatusMessage);
        return;
      }

      // Check if terms and conditions are agreed
      if (!agreedToTerms) {
        setTermsError(
          "You must agree to the Terms & Conditions before proceeding to payment.",
        );
        // Scroll to terms checkbox
        const termsCheckbox = document.querySelector(
          ".terms-checkbox-container",
        );
        if (termsCheckbox) {
          termsCheckbox.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        return;
      }
    }

    setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  };
  const prev = () => {
    // If we're on the paper submission summary (submitted/draft/revision), go back to step 3 to edit the paper.
    if (step === 3 && showPaperSubmissionSummary) {
      setShowPaperSubmissionSummary(false);
      setStep(3);
      return;
    }

    // Paper form should go back to the previous registration step.
    // Keep this explicit so step 3 never falls through to any other special navigation.
    if (step === 3) {
      setStep(2);
      return;
    }

    // Handle abstract step based on abstractStepNumber when going back
    if (abstractStepNumber === 0 || abstractStepNumber === 7) {
      // Abstract disabled or at step 7 - skip step 3 when going backwards
      if (step === 4) {
        setStep(2); // Skip step 3, go directly to step 2
        return;
      }
    }
    setStep((s) => Math.max(s - 1, 1));
  };

  const scrollToTermsCheckbox = () => {
    const termsCheckbox = document.querySelector(".terms-checkbox-container");
    if (termsCheckbox) {
      termsCheckbox.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const handleProceedToPayment = async () => {
    if (isPaymentBlockedPendingPaperApproval) {
      alert(paymentBlockedPaperStatusMessage);
      return;
    }

    // Force users to read terms before they can proceed.
    if (!hasReadTerms) {
      setPendingProceedToPayment(true);
      setTermsError(
        "Please read the Terms & Conditions before proceeding to payment.",
      );
      setShowTermsModal(true);
      scrollToTermsCheckbox();
      return;
    }

    if (!agreedToTerms) {
      setTermsError(
        "You must agree to the Terms & Conditions before proceeding to payment.",
      );
      scrollToTermsCheckbox();
      return;
    }

    const saveSuccess = await saveStep();
    if (saveSuccess) {
      setPendingProceedToPayment(false);
      setStep(7);
    }
  };

  const handleTermsAcknowledged = async () => {
    setHasReadTerms(true);

    if (isPaymentBlockedPendingPaperApproval) {
      setPendingProceedToPayment(false);
      setTermsError(paymentBlockedPaperStatusMessage);
      return;
    }

    // If user clicked "Proceed to Payment" first, continue flow right after reading terms.
    if (!pendingProceedToPayment) {
      return;
    }

    if (!agreedToTerms) {
      setPendingProceedToPayment(false);
      setTermsError(
        "Please check the Terms & Conditions agreement box to proceed to payment.",
      );
      scrollToTermsCheckbox();
      return;
    }

    const saveSuccess = await saveStep();
    if (saveSuccess) {
      setPendingProceedToPayment(false);
      setTermsError("");
      setStep(7);
    }
  };

  // Combined Save & Continue function
  const saveAndContinue = async () => {
    if (!validateCurrentStep(step)) {
      return;
    }

    // Save the registration and get success status
    const saveSuccess = await saveStep();

    // If save was successful, sync registration state from backend before proceeding
    if (saveSuccess) {
      try {
        // Use latest email and conferenceName for fetch
        const emailToFetch = formData.email;
        const confNameToFetch = formData.conference || conferenceName;
        if (emailToFetch && confNameToFetch) {
          // Fetch latest registration from backend
          const fetchUrl = `${REGISTRATION_BASE_URL}/registration/user/${encodeURIComponent(
            emailToFetch,
          )}/${encodeURIComponent(confNameToFetch)}`;
          const dataResponse = await fetch(fetchUrl);
          if (dataResponse.ok) {
            const registrations = await dataResponse.json();
            // Find the registration for this specific conference
            const existingReg = registrations.find(
              (reg) => reg.conference === confNameToFetch && !reg.cancelled,
            );
            if (existingReg) {
              setFormData((prev) => ({ ...prev, ...existingReg }));

              // CRITICAL: Don't overwrite accompanyingPersons state here!
              // The saveStep() function already updated it properly after reloading.
              // If we overwrite here, we'll restore deleted items from the database.
              // The state should remain as the user set it, and saveStep() already synced it.

              // Only sync focused sessions (not accompanying persons)
              const storedAdditionalFees =
                existingReg.additionalFees &&
                Array.isArray(existingReg.additionalFees)
                  ? existingReg.additionalFees
                  : [];
              const storedZeroPayments =
                existingReg.zeroPayments &&
                Array.isArray(existingReg.zeroPayments)
                  ? existingReg.zeroPayments
                  : [];

              // Focused sessions: build array of objects with paid/paymentId
              if (accompanyingPersonFee) {
                const focusedSessionsFromAdditionalFees = storedAdditionalFees
                  .filter((fee) => fee.feeTypeId !== accompanyingPersonFee.id)
                  .map((fee) => fee.feeTypeId); // Just the ID, not the whole object
                const focusedSessionsFromZeroPayments = storedZeroPayments
                  .filter((zp) => zp.feeTypeId !== accompanyingPersonFee.id)
                  .map((zp) => zp.feeTypeId); // Just the ID, not the whole object
                // Combine and deduplicate
                const focusedSessionsList = [
                  ...new Set([
                    ...focusedSessionsFromAdditionalFees,
                    ...focusedSessionsFromZeroPayments,
                  ]),
                ];
                setRegisterForFocusedSessions(
                  focusedSessionsList.length > 0 ? "Yes" : "No",
                );
                setSelectedFocusedSessions(focusedSessionsList);
                // Optionally, fetch fees for these sessions if needed
              }
            }
          }
        }
      } catch (err) {
        // If sync fails, proceed anyway
        console.error("Failed to sync registration after save:", err);
      }

      if (
        step === 3 &&
        abstractStepNumber === 3 &&
        wantsToSubmitAbstract === true
      ) {
        alert(
          "Please wait for your paper approval before proceeding to registration!!!",
        );
        setStep(4);
        return;
      }

      // Block navigation from step-4 if paper is still under review (SUBMITTED status)
      if (
        step === 4 &&
        wantsToSubmitAbstract === true &&
        paperApprovalStatus === "SUBMITTED"
      ) {
        alert(
          "Your paper is currently under review. You will receive an email once it has been approved. Please log in again to continue with your registration.",
        );
        return;
      }

      // Handle abstract step positioning based on abstractStepNumber
      if (step === 2 && abstractStepNumber === 0) {
        // Abstract disabled - skip to step 4
        setStep(4);
        return;
      } else if (step === 2 && abstractStepNumber === 7) {
        // Abstract at step 7 - skip step 3, go to step 4
        setStep(4);
        return;
      } else if (step === 2 && abstractStepNumber === 3) {
        // Abstract at step 3 - normal flow
        setStep(3);
        return;
      } else if (step === 2 && isAbstractRequired === false) {
        setStep(4); // Skip to step 4 (Affiliation)
      } else {
        setStep((s) => Math.min(s + 1, TOTAL_STEPS));
      }
    }
  };

  // Save current step
  const saveStep = async (paperActionOverride = null) => {
    // For partial save, validate basic fields but be flexible with mobile if no country selected
    const firstNameValid = validateField("firstName", formData.firstName);
    const lastNameValid = validateField("lastName", formData.lastName);
    const emailValid = validateField("email", formData.email);

    // For mobile validation during save, use generic validation if no country selected
    let mobileValid = true;
    if (formData.mobile && formData.mobile.trim()) {
      if (formData.country) {
        // If country is selected, do full validation
        mobileValid = validateField("mobile", formData.mobile, {
          country: formData.country,
          value: formData.mobile,
        });
      } else {
        // If no country selected, do basic mobile validation
        const normalizedMobile = formData.mobile.replace(/[\s()-+]/g, "");
        if (!normalizedMobile) {
          setErrors((prev) => ({
            ...prev,
            mobile: "Mobile number is required",
          }));
          mobileValid = false;
        } else if (
          normalizedMobile.length < 7 ||
          normalizedMobile.length > 15 ||
          !/^\d+$/.test(normalizedMobile)
        ) {
          setErrors((prev) => ({
            ...prev,
            mobile: "Please enter a valid mobile number (7-15 digits)",
          }));
          mobileValid = false;
        } else {
          setErrors((prev) => ({ ...prev, mobile: "" }));
          mobileValid = true;
        }
      }
    } else {
      // Mobile is required
      setErrors((prev) => ({ ...prev, mobile: "Mobile number is required" }));
      mobileValid = false;
    }

    if (!firstNameValid || !lastNameValid || !emailValid || !mobileValid) {
      alert("Please correct the highlighted contact details before saving.");
      setStep(1);
      return false; // Return false to indicate validation failure
    }

    try {
      setSaving(true);
      // Build additionalFees array from NEW accompanying persons and focused sessions only
      const additionalFeesArray = [];

      // Get existing additional fees AND zero payments from formData
      const existingAdditionalFees = formData.additionalFees || [];
      const existingZeroPayments = formData.zeroPayments || [];

      console.log("💾 Checking existing items before save:");
      console.log("  - additionalFees:", existingAdditionalFees);
      console.log("  - zeroPayments:", existingZeroPayments);

      // Add only NEW accompanying persons as additional fees (ones not already in DB)
      console.log("🔍 Checking accompanying persons:");
      console.log("  - accompanyingPersons:", accompanyingPersons);
      console.log(
        "  - accompanyingPersons.length:",
        accompanyingPersons.length,
      );
      console.log("  - accompanyingPersonFee:", accompanyingPersonFee);

      if (accompanyingPersonFee) {
        console.log("✅ Conditions met - processing accompanying persons");

        // CRITICAL FIX: Send ALL current accompanying persons (not just new ones)
        // Backend needs the complete list to know what to keep/remove
        accompanyingPersons.forEach((person, index) => {
          console.log(`  - Processing person ${index + 1}:`, person);

          // Only add if name is valid
          if (person.name && person.name.trim() !== "") {
            // Check if already PAID (has payment)
            const alreadyPaid = person.paid && person.paymentId;

            if (alreadyPaid) {
              console.log(
                "  ⏭️ Skipping PAID person (backend will preserve):",
                person.name,
              );
              // Don't send paid items - backend preserves them automatically
            } else {
              console.log(
                "  ➕ Adding UNPAID person (will sync to backend):",
                person.name,
              );
              additionalFeesArray.push({
                feeTypeId: accompanyingPersonFee.id,
                name: person.name,
                rate: parseFloat(accompanyingPersonFee.rate || 0), // Include rate for backend
              });
            }
          } else {
            console.log("  ⚠️ Skipping person with empty name");
          }
        });

        console.log(
          `  📊 Total unpaid persons sent to backend: ${
            additionalFeesArray.filter(
              (f) => f.feeTypeId === accompanyingPersonFee.id,
            ).length
          }`,
        );
      } else {
        console.log("⚠️ accompanyingPersonFee is null or undefined");
      }

      // Add only NEW focused sessions as additional fees (ones not already in DB)
      if (selectedFocusedSessions.length > 0) {
        selectedFocusedSessions.forEach((sessionId) => {
          // Skip if this session is already in the database (in additionalFees OR zeroPayments)
          const alreadyInAdditionalFees = existingAdditionalFees.some(
            (fee) => fee.feeTypeId === sessionId,
          );

          const alreadyInZeroPayments = existingZeroPayments.some(
            (zp) => zp.feeTypeId === sessionId,
          );

          const alreadyExists =
            alreadyInAdditionalFees || alreadyInZeroPayments;

          if (!alreadyExists) {
            const session = focusedSessions.find((s) => s.id === sessionId);
            const sessionFee = focusedSessionFees[sessionId];
            if (session && sessionFee) {
              console.log(
                "  ➕ Adding NEW focused session:",
                session.value || session.name,
                "rate:",
                sessionFee.rate,
              );
              additionalFeesArray.push({
                feeTypeId: sessionId, // Use sessionId as the feeTypeId (not sessionFee.id!)
                name:
                  session.value ||
                  session.name ||
                  `Focused Session ${sessionId}`,
                rate: parseFloat(sessionFee.rate || 0), // Include rate so backend knows if it's free
              });
            }
          } else {
            const session = focusedSessions.find((s) => s.id === sessionId);
            console.log(
              "  ⏭️ Skipping existing focused session:",
              session?.value || session?.name || sessionId,
            );
          }
        });
      }

      console.log("🆕 New additional fees to be added:", additionalFeesArray);
      console.log("📋 Existing additional fees:", existingAdditionalFees);
      console.log("🆓 Existing zero payments:", existingZeroPayments);

      console.log("═══════════════════════════════════════════");
      console.log("🎯 FINAL CHECK BEFORE SENDING TO BACKEND:");
      console.log(
        "  - additionalFeesArray.length:",
        additionalFeesArray.length,
      );
      console.log(
        "  - additionalFeesArray:",
        JSON.stringify(additionalFeesArray, null, 2),
      );
      console.log("═══════════════════════════════════════════");

      // Map to new v2 API format with user profile data for new user creation
      const conferenceRegistrationData = {
        // User identification
        email: formData.email,

        // User profile data (for new user creation if needed)
        title: formData.title,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.mobile,
        gender: formData.gender,
        country: formData.country,
        state: formData.state,
        city: formData.city,
        address: formData.address,
        zipCode: formData.pincode,

        // Conference details
        conference: formData.conference || conferenceName || currentHost,
        affiliation: formData.affiliation || "",
        institution: formData.institution || "",
        prefix: formData.prefix || "",
        fullNameCertificate: formData.fullNameCertificate,
        mciRegistered: formData.mciRegistered || "No",
        mciName: formData.mciName || "",
        mciNumber: formData.mciNumber || "",
        department: formData.department || "",
        departmentId: formData.departmentId,
        otherDepartment: formData.otherDepartment,
        category: formData.category || "TBD",
        categoryId: formData.categoryId,
        registrationType: formData.registrationType || "TBD",
        registrationTypeId: formData.registrationTypeId,
        membershipNumber: formData.membershipNumber || "",
        isaMembershipNumber: formData.isaMembershipNumber || "",
        // Don't send base64 data to studentIdURL - it's too large and handled separately
        studentIdURL:
          formData.studentIdCard && !formData.studentIdCard.startsWith("data:")
            ? formData.studentIdCard
            : null,
        specialRequirements: formData.specialRequirements || "",
        accommodationNeeded: formData.accommodationNeeded || false,
        notes: formData.notes || "",
        // Build additionalData map for JSON column fields and append history values
        additionalData: buildAdditionalDataPayload(),
        // NOTE: accompanyingPersons are sent via additionalFees array (backend DTO doesn't have accompanyingPersons field)
      };

      // CRITICAL: Always send additionalFees array (even if empty) to sync deletions
      // Backend will remove any unpaid items NOT in this list
      conferenceRegistrationData.additionalFees = additionalFeesArray;
      console.log("📤 Sending additionalFees to backend (will sync with DB):");
      console.log("   Total items:", additionalFeesArray.length);
      console.log(
        "   Accompanying persons:",
        additionalFeesArray.filter(
          (f) => f.feeTypeId === accompanyingPersonFee?.id,
        ).length,
      );
      console.log(
        "   Focused sessions:",
        additionalFeesArray.filter(
          (f) => f.feeTypeId !== accompanyingPersonFee?.id,
        ).length,
      );

      console.log("👥 Items being sent in additionalFees array:");
      conferenceRegistrationData.additionalFees.forEach((fee, idx) => {
        const itemType =
          fee.feeTypeId === accompanyingPersonFee?.id
            ? "Accompanying Person"
            : "Focused Session";
        console.log(`   [${idx}] ${itemType}: ${fee.name} - rate: ${fee.rate}`);
      });
      console.log(
        "   ⚠️ Backend will DELETE any UNPAID items NOT in this list!",
      );

      // Determine if this is an update or new registration
      const isUpdate = formData.id || formData.regId;
      const method = isUpdate ? "PUT" : "POST";
      // CRITICAL: Use formData.id (registrationId like "isa005"), NOT formData.regId (dbId like 8)
      const registrationIdForUpdate = formData.id || formData.regId;
      const url = isUpdate
        ? `${CONFERENCE_REGISTRATION_URL}/${registrationIdForUpdate}`
        : CONFERENCE_REGISTRATION_URL;

      console.log("💾 Save Registration:");
      console.log("  formData.id (registrationId):", formData.id);
      console.log("  formData.regId (dbId):", formData.regId);
      console.log("  Using registrationIdForUpdate:", registrationIdForUpdate);
      console.log("  Method:", method);
      console.log("  URL:", url);
      console.log("  Is Update?:", isUpdate);
      console.log("  Is New Conference Registration?:", !isUpdate);
      console.log("  Conference:", conferenceRegistrationData.conference);
      console.log("  Category:", conferenceRegistrationData.category);
      console.log("  CategoryId:", conferenceRegistrationData.categoryId);
      console.log(
        "  Registration Type:",
        conferenceRegistrationData.registrationType,
      );
      console.log(
        "  RegistrationTypeId:",
        conferenceRegistrationData.registrationTypeId,
      );
      console.log("═══════════════════════════════════════════");
      console.log("📤 PAYLOAD BEING SENT TO BACKEND:");
      console.log(JSON.stringify(conferenceRegistrationData, null, 2));
      console.log("═══════════════════════════════════════════");

      const res = await fetch(url, {
        method: method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(conferenceRegistrationData),
      });

      if (!res.ok) {
        const txt = await res.text();
        console.error("❌ Save failed!");
        console.error("  Status:", res.status, res.statusText);
        console.error("  Response:", txt);
        console.error(
          "  Was trying to save for existing user:",
          !!formData.email,
        );
        console.error("  Conference:", conferenceRegistrationData.conference);
        alert(`Save failed: ${txt}`);
        return false; // Return false to indicate save failure
      } else {
        const saved = await res.json();
        console.log("✅ Save successful!");
        console.log("  Saved data:", saved);
        console.log("  Registration ID:", saved.registrationId);
        console.log("  DB ID:", saved.dbId || saved.id);

        // Set flag to indicate registration was just updated successfully
        setRegistrationJustUpdated(true);

        // Update registration IDs
        if (saved.registrationId) {
          console.log("  Setting formData.id to:", saved.registrationId);
          setFormData((prev) => ({ ...prev, id: saved.registrationId }));
        }
        if (saved.dbId || saved.id) {
          console.log("  Setting formData.regId to:", saved.dbId || saved.id);
          setFormData((prev) => ({ ...prev, regId: saved.dbId || saved.id }));
        }

        // Upload student ID card file to database if present
        if (
          (saved.dbId || saved.id) &&
          formData.studentIdCard &&
          formData.studentIdCard.startsWith("data:")
        ) {
          try {
            const registrationId = saved.dbId || saved.id;
            console.log(
              "📤 Uploading student ID card to database for registration:",
              registrationId,
            );

            const base64Data = formData.studentIdCard.split(",")[1];
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }

            const fileData = {
              fileType: "id-card",
              fileData: base64Data,
              fileName: formData.studentIdCardName || "student-id.jpg",
              contentType: formData.studentIdCardType || "image/jpeg",
              fileSize: byteNumbers.length,
            };

            console.log("📤 File data prepared:", {
              fileName: fileData.fileName,
              contentType: fileData.contentType,
              fileSize: fileData.fileSize,
              base64Length: base64Data.length,
            });

            const uploadUrl = `${REGISTRATION_BASE_URL}/registration/${registrationId}/file/id-card`;
            console.log("📤 Uploading to:", uploadUrl);

            const fileUploadRes = await fetch(uploadUrl, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(fileData),
            });

            if (!fileUploadRes.ok) {
              const errorText = await fileUploadRes.text();
              console.error("❌ Failed to upload student ID card:", errorText);
              alert("Warning: File upload failed. Please try uploading again.");
              // Don't fail the entire save, just log it
            } else {
              const result = await fileUploadRes.json();
              console.log(
                "✅ Student ID card uploaded successfully to database:",
                result,
              );

              // Clear the base64 data and set a marker that file is in database
              setFormData((prev) => ({
                ...prev,
                studentIdCard: "DATABASE", // Marker to indicate file is stored in database
                studentIdCardUploaded: true,
              }));
            }
          } catch (fileErr) {
            console.error("Error uploading student ID card:", fileErr);
            // Don't fail the entire save, just log it
          }
        }

        // Save paper submission if on step 3 and paper data exists
        console.log("🔍 Checking paper submission conditions:", {
          step,
          isAbstractRequired,
          wantsToSubmitAbstract,
          submissionMethod,
          paperTitle: paperTitle?.trim(),
          selectedTrack,
          abstractText: abstractText?.trim(),
          paperFile: paperFile?.name,
          registrationId: saved.dbId || saved.id,
        });

        // Check if we should save paper based on submission method
        const hasRequiredPaperData =
          paperTitle?.trim() &&
          selectedTrack &&
          ((submissionMethod === "TEXTAREA" && abstractText?.trim()) ||
            (submissionMethod === "FILE" && paperFile) ||
            (submissionMethod === "BOTH" && abstractText?.trim() && paperFile));

        // Determine if we should save paper submission:
        // Step 3: Only if user chose to submit abstract (wantsToSubmitAbstract === true)
        // Step 7: Always save if abstractStepNumber is 7 (abstract after payment)
        const shouldSavePaper =
          hasRequiredPaperData &&
          ((step === 3 &&
            isAbstractRequired &&
            wantsToSubmitAbstract === true) ||
            (step === 7 && abstractStepNumber === 7));

        if (shouldSavePaper) {
          try {
            const registrationId = saved.dbId || saved.id;
            console.log(
              "📄 Starting paper submission for registration:",
              registrationId,
            );

            // First, upload the paper file if present
            if (paperFile) {
              console.log("📤 Uploading paper file:", paperFile.name);

              // Convert file to base64
              const reader = new FileReader();
              const base64Promise = new Promise((resolve, reject) => {
                reader.onload = () => {
                  const base64Data = reader.result.split(",")[1];
                  resolve(base64Data);
                };
                reader.onerror = reject;
                reader.readAsDataURL(paperFile);
              });

              const base64Data = await base64Promise;

              const fileData = {
                fileType: "paper",
                fileData: base64Data,
                fileName: paperFile.name,
                contentType: paperFile.type,
                fileSize: paperFile.size,
              };

              console.log("📤 File data prepared:", {
                fileName: fileData.fileName,
                contentType: fileData.contentType,
                fileSize: fileData.fileSize,
                base64Length: base64Data.length,
              });

              const fileUploadUrl = `${REGISTRATION_BASE_URL}/registration/${registrationId}/file/paper`;
              console.log("📤 Uploading to:", fileUploadUrl);

              const fileUploadRes = await fetch(fileUploadUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(fileData),
              });

              if (fileUploadRes.ok) {
                const fileResult = await fileUploadRes.json();
                console.log("✅ Paper file uploaded successfully:", fileResult);
                // The file is saved in registration_files table, we'll reference it via registrationId and fileType
              } else {
                const errorText = await fileUploadRes.text();
                console.error("❌ Failed to upload paper file:", errorText);
                throw new Error("Failed to upload paper file");
              }
            }

            // Create or update paper entity (file is stored via registrationId/fileType pattern)
            // Status logic priority:
            // 1) Explicit action from caller (submit/draft)
            // 2) State-based action (for existing flows)
            // 3) Preserve current paper status for autosaves to avoid accidental downgrade
            // 4) Backward-compatible fallback based on submissionMethod
            const effectivePaperAction =
              paperActionOverride || paperSubmissionAction;
            const currentPaperStatus = (paperApprovalStatus || "")
              .trim()
              .toUpperCase();
            const shouldPreserveCurrentStatus =
              !paperActionOverride &&
              !paperSubmissionAction &&
              !!currentPaperStatus;
            const paperStatus =
              effectivePaperAction === "submit"
                ? "SUBMITTED"
                : effectivePaperAction === "draft"
                  ? "DRAFT"
                  : shouldPreserveCurrentStatus
                    ? currentPaperStatus
                    : !submissionMethod || submissionMethod.trim() === ""
                      ? "DRAFT"
                      : "SUBMITTED";
            console.log(
              `🧭 Paper status transition: ${currentPaperStatus || "NONE"} -> ${paperStatus}`,
            );
            console.log(
              `📝 Paper status will be: ${paperStatus} (action: "${effectivePaperAction}")`,
            );

            const paperData = {
              registrationId: registrationId,
              trackId: parseInt(selectedTrack),
              title: paperTitle.trim(),
              abstractText: abstractText?.trim() || null, // Allow null for FILE-only submissions
              status: paperStatus, // DRAFT if saved as draft, SUBMITTED if submitted
              presentationMode: presentationMode || null, // User must explicitly select - no fallback to config
              fileId: null, // Not used - file accessed via registration/{id}/file/paper endpoint
            };

            let savedPaper;
            if (existingPaperId) {
              // Update existing paper
              console.log(
                "📝 Updating existing paper ID:",
                existingPaperId,
                "with data:",
                paperData,
              );
              const paperUrl = `${REGISTRATION_BASE_URL}/api/papers/${existingPaperId}`;
              const paperRes = await fetch(paperUrl, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(paperData),
              });

              if (!paperRes.ok) {
                const errorText = await paperRes.text();
                console.error("❌ Failed to update paper:", errorText);
                throw new Error(
                  "Failed to update paper submission: " + errorText,
                );
              }

              savedPaper = await paperRes.json();
              console.log("✅ Paper updated successfully:", savedPaper);
            } else {
              // Create new paper
              console.log("📄 Creating new paper entity:", paperData);
              const paperUrl = `${REGISTRATION_BASE_URL}/api/papers`;
              const paperRes = await fetch(paperUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(paperData),
              });

              if (!paperRes.ok) {
                const errorText = await paperRes.text();
                console.error("❌ Failed to create paper:", errorText);
                throw new Error(
                  "Failed to save paper submission: " + errorText,
                );
              }

              savedPaper = await paperRes.json();
              console.log("✅ Paper created successfully:", savedPaper);

              // Store the paper ID for future updates
              setExistingPaperId(savedPaper.id);
            }

            // Save co-authors if present
            if (coAuthors && coAuthors.length > 0) {
              console.log("👥 Saving co-authors for paper ID:", savedPaper.id);

              // If updating existing paper, delete old co-authors first
              if (existingPaperId) {
                try {
                  console.log(
                    "🗑️ Deleting existing co-authors for paper:",
                    savedPaper.id,
                  );
                  const deleteUrl = `${REGISTRATION_BASE_URL}/api/coauthors/paper/${savedPaper.id}`;
                  await fetch(deleteUrl, { method: "DELETE" });
                  console.log("✅ Existing co-authors deleted");
                } catch (deleteErr) {
                  console.error(
                    "⚠️ Failed to delete existing co-authors:",
                    deleteErr,
                  );
                  // Continue anyway - we'll add the new ones
                }
              }

              for (const coAuthor of coAuthors) {
                if (
                  coAuthor.name &&
                  coAuthor.name.trim() &&
                  coAuthor.email &&
                  coAuthor.email.trim()
                ) {
                  const coAuthorData = {
                    paperId: savedPaper.id,
                    name: coAuthor.name.trim(),
                    email: coAuthor.email.trim(),
                    phone: coAuthor.phone?.trim() || null,
                    specialization: coAuthor.specialization?.trim() || null,
                    designation: coAuthor.designation?.trim() || null,
                  };

                  console.log("  - Saving co-author:", coAuthorData);
                  const coAuthorUrl = `${REGISTRATION_BASE_URL}/api/coauthors`;
                  const coAuthorRes = await fetch(coAuthorUrl, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(coAuthorData),
                  });

                  if (coAuthorRes.ok) {
                    const savedCoAuthor = await coAuthorRes.json();
                    console.log("  ✅ Co-author saved:", savedCoAuthor);
                  } else {
                    const errorText = await coAuthorRes.text();
                    console.error("  ❌ Failed to save co-author:", errorText);
                  }
                }
              }
            } else if (existingPaperId) {
              // If no co-authors but updating existing paper, delete any existing co-authors
              try {
                console.log(
                  "🗑️ Deleting all co-authors for paper:",
                  savedPaper.id,
                );
                const deleteUrl = `${REGISTRATION_BASE_URL}/api/coauthors/paper/${savedPaper.id}`;
                await fetch(deleteUrl, { method: "DELETE" });
                console.log("✅ All co-authors deleted");
              } catch (deleteErr) {
                console.error("⚠️ Failed to delete co-authors:", deleteErr);
              }
            }

            console.log("✅ Paper submission completed successfully");
            setHasPaperSubmitted(true); // Update state to reflect paper has been submitted
            setPaperApprovalStatus(savedPaper?.status || paperStatus);
          } catch (paperErr) {
            console.error("❌ Error saving paper submission:", paperErr);
            alert(
              "Warning: Paper submission failed. " +
                paperErr.message +
                "\nPlease try again from the Paper/Abstract step.",
            );
            // Don't fail the entire save, just alert the user
          }
        } else {
          console.log("⚠️ Paper submission skipped - conditions not met");
        }

        // Reload full registration data to get updated additionalFees
        if (saved.dbId || saved.id) {
          try {
            const reloadRes = await fetch(
              `${CONFERENCE_REGISTRATION_URL}/${saved.dbId || saved.id}`,
            );
            if (reloadRes.ok) {
              const reloadedReg = await reloadRes.json();
              console.log(
                "📥 Reloaded registration data from backend:",
                reloadedReg,
              );
              console.log(
                "   Backend returned additionalFees count:",
                reloadedReg.additionalFees?.length || 0,
              );
              console.log(
                "   Backend returned zeroPayments count:",
                reloadedReg.zeroPayments?.length || 0,
              );

              // Update formData with additionalFees AND zeroPayments so the payment detection works
              if (
                reloadedReg.additionalFees &&
                Array.isArray(reloadedReg.additionalFees)
              ) {
                setFormData((prev) => ({
                  ...prev,
                  additionalFees: reloadedReg.additionalFees,
                  zeroPayments: reloadedReg.zeroPayments || [], // Also update zeroPayments
                }));
                console.log(
                  "✅ Updated formData.additionalFees:",
                  reloadedReg.additionalFees,
                );
                console.log(
                  "✅ Updated formData.zeroPayments:",
                  reloadedReg.zeroPayments,
                );

                // CRITICAL: Only update accompanyingPersons to ADD database IDs
                // Do NOT restore deleted items from database!
                if (accompanyingPersonFee) {
                  console.log(
                    "🔍 Current accompanyingPersons state (before mapping):",
                    accompanyingPersons,
                  );
                  console.log(
                    "   Current state count:",
                    accompanyingPersons.length,
                  );

                  const updatedAccompanyingPersons = accompanyingPersons.map(
                    (person) => {
                      // Find matching fee in reloaded data (check both additionalFees and zeroPayments)
                      const matchingFee = reloadedReg.additionalFees.find(
                        (fee) =>
                          fee.feeTypeId === accompanyingPersonFee.id &&
                          fee.name === person.name,
                      );

                      const matchingZeroPayment =
                        reloadedReg.zeroPayments?.find(
                          (zp) =>
                            zp.feeTypeId === accompanyingPersonFee.id &&
                            zp.name === person.name,
                        );

                      if (matchingFee) {
                        console.log(
                          `  ✓ Found match in additionalFees for "${person.name}":`,
                          matchingFee,
                        );
                        // Update with database info from additionalFees
                        return {
                          ...person,
                          paymentId: matchingFee.paymentId || null,
                          paid: !!matchingFee.paymentId,
                          feeTypeId: matchingFee.feeTypeId,
                          id: matchingFee.id, // Add the database ID
                        };
                      } else if (matchingZeroPayment) {
                        console.log(
                          `  ✓ Found match in zeroPayments for "${person.name}":`,
                          matchingZeroPayment,
                        );
                        // Update with database info from zeroPayments
                        return {
                          ...person,
                          paymentId: null,
                          paid: true, // Saved in zero_payments table
                          feeTypeId: matchingZeroPayment.feeTypeId,
                          id: matchingZeroPayment.id,
                        };
                      }
                      console.log(
                        `  ⚠️ No match found for "${person.name}" - keeping as is (new item)`,
                      );
                      return person;
                    },
                  );

                  setAccompanyingPersons(updatedAccompanyingPersons);
                  console.log(
                    "✅ Updated accompanyingPersons state after save (with DB IDs):",
                    updatedAccompanyingPersons,
                  );
                  console.log(
                    "   Updated state count:",
                    updatedAccompanyingPersons.length,
                  );
                  console.log(
                    "   If count decreased, deletions were preserved ✓",
                  );
                  console.log(
                    "   If count stayed same/increased, items may have been restored from DB ✗",
                  );
                }
              }
            }
          } catch (reloadErr) {
            console.error("Error reloading registration:", reloadErr);
            // Don't fail the save operation, just log it
          }
        }

        return true; // Return true to indicate successful save
      }
    } catch (err) {
      console.error("Save error:", err);
      alert(`Could not save. Error: ${err.message}`);
      return false; // Return false to indicate error
    } finally {
      setSaving(false);
    }
  };

  // Detect environment based on Razorpay key
  const isProduction =
    RAZORPAY_KEY_ID && RAZORPAY_KEY_ID.startsWith("rzp_live_");

  // Payment
  const startPayment = async () => {
    // Set payment initiated flag to prevent double clicks
    setPaymentInitiated(true);
    // Reset payment error flags for new payment attempt
    setPaymentErrorShown(false);
    paymentFailureHandledRef.current = false;
    razorpayOpenRef.current = false;

    const missingFields = [];
    if (!formData.title || !formData.title.trim()) missingFields.push("Title");
    if (!formData.firstName.trim()) missingFields.push("First Name");
    if (!formData.lastName.trim()) missingFields.push("Last Name");
    if (!formData.email.trim()) missingFields.push("Email");
    if (!formData.mobile.trim()) missingFields.push("Mobile");
    if (!formData.country.trim()) missingFields.push("Country");
    if (
      genderOptions.length > 0 &&
      (!formData.gender || !formData.gender.trim())
    )
      missingFields.push("Gender");

    if (missingFields.length > 0) {
      alert(
        `Please fill in the following required fields: ${missingFields.join(
          ", ",
        )}`,
      );
      setStep(1);
      setPaymentInitiated(false); // Reset flag on validation error
      return;
    }

    if (!validateAllMandatoryFields()) {
      alert("Please correct the highlighted contact details.");
      setStep(1);
      setPaymentInitiated(false); // Reset flag on validation error
      return;
    }

    setProcessingPayment(true);

    // MANDATORY: Ensure registration is saved successfully before proceeding to payment
    let registrationId = formData.id;
    let registrationDbId = formData.regId;

    console.log("💳 Starting payment process");
    console.log("  formData.id:", formData.id);
    console.log("  formData.regId:", formData.regId);
    console.log("  registrationId:", registrationId);
    console.log("  registrationDbId:", registrationDbId);

    if (!registrationId) {
      console.log(
        "⚠️ No registrationId found - need to save registration first",
      );

      try {
        // Map to new v2 API format with user profile data for new user creation
        const conferenceRegistrationData = {
          // User identification
          email: formData.email,

          // User profile data (for new user creation if needed)
          title: formData.title,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.mobile,
          country: formData.country,
          state: formData.state,
          city: formData.city,
          address: formData.address,
          zipCode: formData.pincode,
          institution: formData.institution,
          affiliation: formData.affiliation || "",
          // Conference details
          conference: formData.conference || conferenceName || currentHost,
          prefix: formData.prefix || "",
          fullNameCertificate: formData.fullNameCertificate,
          mciRegistered: formData.mciRegistered || "No",
          mciName: formData.mciName || "",
          mciNumber: formData.mciNumber || "",
          department: formData.department || "",
          departmentId: formData.departmentId,
          otherDepartment: formData.otherDepartment,
          category: formData.category || "TBD",
          categoryId: formData.categoryId,
          registrationType: formData.registrationType || "TBD",
          registrationTypeId: formData.registrationTypeId,
          membershipNumber: formData.membershipNumber || "",
          isaMembershipNumber: formData.isaMembershipNumber || "",
          // Don't send base64 data to studentIdURL - it's too large and handled separately
          studentIdURL:
            formData.studentIdCard &&
            !formData.studentIdCard.startsWith("data:")
              ? formData.studentIdCard
              : null,
          specialRequirements: formData.specialRequirements || "",
          additionalData: buildAdditionalDataPayload(),
          accommodationNeeded: formData.accommodationNeeded || false,
          notes: formData.notes || "",
          // Accompanying persons
          accompanyingPersons:
            accompanyingPersons.length > 0 ? accompanyingPersons : [],
        };

        // CRITICAL FIX: We're inside !registrationId block, meaning formData.id is NULL
        // This means we should ALWAYS do POST for new registration
        // DO NOT use formData.regId here as it's the database ID, not the user's registration ID
        const paymentMethod = "POST";
        const paymentUrl = CONFERENCE_REGISTRATION_URL;

        console.log("🔄 Payment flow registration save (NEW registration):");
        console.log(
          "  formData.id (registrationId):",
          formData.id,
          "← NULL, so doing POST",
        );
        console.log(
          "  formData.regId (dbId):",
          formData.regId,
          "← IGNORED in this flow",
        );
        console.log("  Method:", paymentMethod);
        console.log("  URL:", paymentUrl);

        const regSaveRes = await fetch(paymentUrl, {
          method: paymentMethod,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(conferenceRegistrationData),
        });

        if (!regSaveRes.ok) {
          const regError = await regSaveRes.text();
          console.error("Registration save failed:", regError);
          alert(
            `Registration failed: ${regError}. Please fix the issues and try again.`,
          );
          setProcessingPayment(false);
          setPaymentInitiated(false); // Reset flag on registration error
          setStep(1); // Go back to first step to fix issues
          return;
        }

        const savedReg = await regSaveRes.json();
        registrationId = savedReg.registrationId;
        registrationDbId = savedReg.dbId || savedReg.id;

        console.log("💾 Registration saved response:");
        console.log("  savedReg.registrationId:", savedReg.registrationId);
        console.log("  savedReg.dbId:", savedReg.dbId);
        console.log("  savedReg.id:", savedReg.id);
        console.log("  Using registrationId:", registrationId);
        console.log("  Using registrationDbId:", registrationDbId);

        // Update form data with the new ID
        setFormData((prev) => ({
          ...prev,
          id: savedReg.registrationId,
          regId: savedReg.dbId || savedReg.id,
        }));

        const paymentActionMessage = isPaymentUpdate ? "updated" : "saved";
        alert(
          `Registration ${paymentActionMessage} successfully! Registration ID: ${registrationId}. Proceeding to payment...`,
        );
      } catch (regError) {
        console.error("Error saving registration before payment:", regError);
        alert(`Registration failed: ${regError.message}. Please try again.`);
        setProcessingPayment(false);
        setPaymentInitiated(false); // Reset flag on registration error
        setStep(1); // Go back to first step to fix issues
        return;
      }
    } else {
      console.log("✅ Registration already exists");
      console.log("  registrationId:", registrationId);
      console.log("  registrationDbId:", registrationDbId);
    }

    // Determine currency and amount based on fetched fee or fallback to country-based defaults
    let currency, amountInSmallestUnit, actualAmount;

    console.log("💵 Starting fee calculation:");
    console.log("  formData.paid:", formData.paid);
    console.log("  registrationFee:", registrationFee);
    console.log("  accompanyingPersons.length:", accompanyingPersons.length);
    console.log(
      "  selectedFocusedSessions.length:",
      selectedFocusedSessions.length,
    );

    if (registrationFee && registrationFee.rate) {
      // Use the fetched fee from the backend
      currency = registrationFee.currency || "INR";

      // Check if this is an additional payment (already paid base fee)
      const isAdditionalPayment = formData.paid === true;
      console.log("  isAdditionalPayment:", isAdditionalPayment);

      if (isAdditionalPayment) {
        // For paid users, charge again only when category/type has changed.
        // Also charge for any newly added additional items.
        actualAmount = 0;

        if (hasPostPaymentCategoryOrTypeChange) {
          const changedBaseRate = parseFloat(registrationFee.rate || 0);
          const changedBaseDiscountAmount = shouldApplyPaperDiscount
            ? (changedBaseRate * paperDiscountPercentage) / 100
            : 0;
          const changedDiscountedBaseRate = Math.max(
            0,
            changedBaseRate - changedBaseDiscountAmount,
          );
          actualAmount += changedDiscountedBaseRate;
          console.log(
            "  🔁 Adding changed base fee for new category/type:",
            changedDiscountedBaseRate,
          );
        }

        // CRITICAL: Calculate existing PAID items from additionalFees
        // MUST filter to only items with paymentId (already paid)
        // Items with paymentId=null are NEW items that need payment
        const existingAdditionalFees = formData.additionalFees || [];

        console.log("  📊 Analyzing existing additional fees:");
        console.log(
          "    Total additionalFees count:",
          existingAdditionalFees.length,
        );
        existingAdditionalFees.forEach((fee) => {
          console.log(
            `    - Fee: ${fee.name}, feeTypeId: ${fee.feeTypeId}, paymentId: ${
              fee.paymentId || "NULL"
            }`,
          );
        });

        // Count ONLY paid accompanying persons (those with paymentId)
        const existingAccompanyingCount = existingAdditionalFees.filter(
          (fee) =>
            accompanyingPersonFee &&
            fee.feeTypeId === accompanyingPersonFee.id &&
            fee.paymentId,
        ).length;

        console.log(
          "    Existing PAID accompanying count:",
          existingAccompanyingCount,
        );

        // Get ONLY paid focused session IDs (those with paymentId)
        const existingFocusedSessionIds = existingAdditionalFees
          .filter((fee) => {
            // If no accompanyingPersonFee loaded, treat all fees as focused sessions
            if (!accompanyingPersonFee) return fee.paymentId; // Only if paid
            // Otherwise, exclude accompanying person fees and only include paid items
            return fee.feeTypeId !== accompanyingPersonFee.id && fee.paymentId;
          })
          .map((fee) => fee.feeTypeId);

        console.log(
          "    Existing PAID focused session IDs:",
          existingFocusedSessionIds,
        );

        // Add only NEW accompanying persons fees
        if (
          accompanyingPersons.length > existingAccompanyingCount &&
          accompanyingPersonFee &&
          accompanyingPersonFee.rate
        ) {
          const newAccompanyingCount =
            accompanyingPersons.length - existingAccompanyingCount;
          const accompanyingTotal =
            parseFloat(accompanyingPersonFee.rate) * newAccompanyingCount;
          actualAmount += accompanyingTotal;
        }

        // Add only NEW focused session fees (excluding free ones with rate = 0)
        if (selectedFocusedSessions.length > 0) {
          selectedFocusedSessions.forEach((sessionId) => {
            if (!existingFocusedSessionIds.includes(sessionId)) {
              const sessionFee = focusedSessionFees[sessionId];
              // Only add to amount if rate > 0 (exclude free items)
              if (
                sessionFee &&
                sessionFee.rate &&
                parseFloat(sessionFee.rate) > 0
              ) {
                actualAmount += parseFloat(sessionFee.rate);
              }
            }
          });
        }
      } else {
        // Initial payment - include base fee and all additional items
        const baseRate = parseFloat(registrationFee.rate || 0);
        const baseDiscountAmount = shouldApplyPaperDiscount
          ? (baseRate * paperDiscountPercentage) / 100
          : 0;
        const discountedBaseRate = Math.max(0, baseRate - baseDiscountAmount);
        actualAmount = discountedBaseRate;

        // Add accompanying persons fees if applicable
        if (
          accompanyingPersons.length > 0 &&
          accompanyingPersonFee &&
          accompanyingPersonFee.rate
        ) {
          const accompanyingTotal =
            parseFloat(accompanyingPersonFee.rate) * accompanyingPersons.length;
          actualAmount += accompanyingTotal;
        }

        // Add focused session fees (excluding free ones with rate = 0)
        if (selectedFocusedSessions.length > 0) {
          selectedFocusedSessions.forEach((sessionId) => {
            const sessionFee = focusedSessionFees[sessionId];
            // Only add to amount if rate > 0 (exclude free items)
            if (
              sessionFee &&
              sessionFee.rate &&
              parseFloat(sessionFee.rate) > 0
            ) {
              actualAmount += parseFloat(sessionFee.rate);
            }
          });
        }
      }

      // Add conveyance fee to actual amount (use dynamic percentage from API, skip if null)
      const conveyanceFeeRate =
        conveyanceFeePercentage !== null && conveyanceFeePercentage > 0
          ? conveyanceFeePercentage / 100
          : 0;
      const conveyanceFee = actualAmount * conveyanceFeeRate;
      const totalAmountWithFee = actualAmount + conveyanceFee;

      // Convert to smallest unit (paise for INR, cents for USD)
      amountInSmallestUnit = Math.round(totalAmountWithFee * 100);

      console.log("💰 Fee Calculation Results:");
      console.log("  registrationFee:", registrationFee);
      console.log("  paperDiscountPercentage:", paperDiscountPercentage);
      console.log("  currentPaperSequence:", currentPaperSequence);
      console.log("  actualAmount (before conveyance fee):", actualAmount);
      console.log("  conveyanceFeePercentage:", conveyanceFeePercentage, "%");
      console.log("  conveyanceFeeRate:", conveyanceFeeRate);
      console.log("  conveyanceFee:", conveyanceFee);
      console.log("  totalAmountWithFee:", totalAmountWithFee);
      console.log("  amountInSmallestUnit:", amountInSmallestUnit);
      console.log("  currency:", currency);
    } else {
      // Fallback to country-based defaults
      currency = formData.country === "India" ? "INR" : "USD";
      amountInSmallestUnit = formData.country === "India" ? 10000 : 100; // 100 INR or 1 USD
      actualAmount = amountInSmallestUnit / 100;

      console.log("💰 Fee Calculation (FALLBACK):");
      console.log("  No registrationFee found, using fallback");
      console.log("  actualAmount:", actualAmount);
      console.log("  amountInSmallestUnit:", amountInSmallestUnit);
      console.log("  currency:", currency);
    }

    try {
      // Build breakdown array for backend
      const breakdown = [];

      console.log("🔨 Building payment breakdown array...");
      console.log("  formData.paid:", formData.paid);
      console.log("  accompanyingPersons:", accompanyingPersons);
      console.log("  selectedFocusedSessions:", selectedFocusedSessions);

      // Check if this is an additional payment
      const isAdditionalPayment = formData.paid === true;
      console.log("  isAdditionalPayment:", isAdditionalPayment);

      if (isAdditionalPayment) {
        // Only include NEW items in breakdown
        // CRITICAL: Count only PAID items (those with paymentId)
        // Items with paymentId=null are NEW and should be included in breakdown
        const existingAdditionalFees = formData.additionalFees || [];

        if (
          registrationFee &&
          registrationFee.rate &&
          hasPostPaymentCategoryOrTypeChange
        ) {
          const changedBaseRate = parseFloat(registrationFee.rate || 0);
          const changedBaseDiscountAmount = shouldApplyPaperDiscount
            ? (changedBaseRate * paperDiscountPercentage) / 100
            : 0;
          const changedDiscountedBaseRate = Math.max(
            0,
            changedBaseRate - changedBaseDiscountAmount,
          );
          breakdown.push({
            type: "BASE_FEE",
            feeId: registrationFee.id || null,
            amount: changedDiscountedBaseRate,
            currency: registrationFee.currency || currency,
            description: `Registration Fee - ${formData.category} (${formData.registrationType})`,
          });
          console.log("    ✅ Added BASE_FEE for changed category/type");
        }

        console.log("  📊 Building breakdown for additional payment:");
        console.log("    Total additionalFees:", existingAdditionalFees.length);

        // Count ONLY paid accompanying persons (those with paymentId)
        const existingAccompanyingCount = existingAdditionalFees.filter(
          (fee) =>
            accompanyingPersonFee &&
            fee.feeTypeId === accompanyingPersonFee.id &&
            fee.paymentId,
        ).length;

        console.log(
          "    Existing PAID accompanying count:",
          existingAccompanyingCount,
        );
        console.log(
          "    Current accompanying persons:",
          accompanyingPersons.length,
        );

        // Get ONLY paid focused session IDs (those with paymentId)
        const existingFocusedSessionIds = existingAdditionalFees
          .filter((fee) => {
            // If no accompanyingPersonFee loaded, treat all fees as focused sessions
            if (!accompanyingPersonFee) return fee.paymentId; // Only if paid
            // Otherwise, exclude accompanying person fees and only include paid items
            return fee.feeTypeId !== accompanyingPersonFee.id && fee.paymentId;
          })
          .map((fee) => fee.feeTypeId);

        console.log(
          "    Existing PAID focused session IDs:",
          existingFocusedSessionIds,
        );
        console.log(
          "    Currently selected focused sessions:",
          selectedFocusedSessions,
        );

        // Add only NEW accompanying persons
        if (
          accompanyingPersons.length > existingAccompanyingCount &&
          accompanyingPersonFee &&
          accompanyingPersonFee.rate
        ) {
          const newPersons = accompanyingPersons.slice(
            existingAccompanyingCount,
          );
          newPersons.forEach((person, index) => {
            breakdown.push({
              type: "ADDITIONAL_FEE",
              feeId: accompanyingPersonFee.id || null,
              amount: parseFloat(accompanyingPersonFee.rate),
              currency: accompanyingPersonFee.currency || currency,
              description: `Accompanying Person - ${
                person.name || `Person ${existingAccompanyingCount + index + 1}`
              }`,
            });
          });
        }

        // Add only NEW focused sessions (exclude free items with rate = 0)
        if (selectedFocusedSessions.length > 0) {
          selectedFocusedSessions.forEach((sessionId) => {
            if (!existingFocusedSessionIds.includes(sessionId)) {
              const sessionFee = focusedSessionFees[sessionId];
              const session = focusedSessions.find((s) => s.id === sessionId);

              // Only add to breakdown if rate > 0 (exclude free items)
              if (
                sessionFee &&
                session &&
                sessionFee.rate &&
                parseFloat(sessionFee.rate) > 0
              ) {
                breakdown.push({
                  type: "ADDITIONAL_FEE",
                  feeId: sessionId, // Use sessionId as the feeTypeId (not sessionFee.id!)
                  amount: parseFloat(sessionFee.rate),
                  currency: sessionFee.currency || currency,
                  description: `Focused Session - ${
                    session.value || session.name || `Session ${sessionId}`
                  }`,
                });
              }
            }
          });
        }
      } else {
        // Initial payment - include base fee and all items
        console.log(
          "  📝 Building breakdown for INITIAL payment (includes base fee)",
        );

        // Get existing additional fees to check for duplicates
        const existingAdditionalFees = formData.additionalFees || [];

        // Add base registration fee
        if (registrationFee && registrationFee.rate) {
          const baseRate = parseFloat(registrationFee.rate || 0);
          const baseDiscountAmount = shouldApplyPaperDiscount
            ? (baseRate * paperDiscountPercentage) / 100
            : 0;
          const discountedBaseRate = Math.max(0, baseRate - baseDiscountAmount);
          console.log("    ✅ Adding BASE_FEE to breakdown");
          console.log("       feeId:", registrationFee.id);
          console.log("       amount:", discountedBaseRate);
          breakdown.push({
            type: "BASE_FEE",
            feeId: registrationFee.id || null,
            amount: discountedBaseRate,
            currency: registrationFee.currency || currency,
            description:
              shouldApplyPaperDiscount && paperDiscountPercentage > 0
                ? `Registration Fee (${paperDiscountPercentage}% paper discount, sequence ${currentPaperSequence})`
                : `Registration Fee - ${formData.category} (${formData.registrationType})`,
          });
        } else {
          console.warn(
            "    ⚠️ Cannot add BASE_FEE - registrationFee or rate is missing",
          );
          console.warn("       registrationFee:", registrationFee);
        }

        // Add accompanying persons fees (ONLY unpaid ones for additional payment)
        console.log("    Checking accompanying persons...");
        console.log(
          "      accompanyingPersons.length:",
          accompanyingPersons.length,
        );
        console.log("      accompanyingPersonFee:", accompanyingPersonFee);
        if (
          accompanyingPersons.length > 0 &&
          accompanyingPersonFee &&
          accompanyingPersonFee.rate
        ) {
          // For INITIAL payment: Include ALL persons who don't have a paymentId
          // (Don't filter by alreadyInDb - that's only for additional payments)
          const unpaidPersons = accompanyingPersons.filter((person, index) => {
            // Only exclude if already paid (has paymentId)
            const alreadyPaid = person.paid || person.paymentId;
            return !alreadyPaid;
          });

          console.log("      unpaidPersons.length:", unpaidPersons.length);
          unpaidPersons.forEach((person, index) => {
            console.log(
              `      ✅ Adding accompanying person ${index + 1}: ${person.name}`,
            );
            breakdown.push({
              type: "ADDITIONAL_FEE",
              feeId: accompanyingPersonFee.id || null,
              amount: parseFloat(accompanyingPersonFee.rate),
              currency: accompanyingPersonFee.currency || currency,
              description: `Accompanying Person - ${
                person.name || `Person ${index + 1}`
              }`,
            });
          });
        } else {
          console.log(
            "      ⏭️  Skipping accompanying persons (none added or fee not loaded)",
          );
        }

        // Add focused session fees (exclude free items with rate = 0)
        console.log("    Checking focused sessions...");
        console.log(
          "      selectedFocusedSessions.length:",
          selectedFocusedSessions.length,
        );
        if (selectedFocusedSessions.length > 0) {
          selectedFocusedSessions.forEach((sessionId) => {
            const sessionFee = focusedSessionFees[sessionId];
            const session = focusedSessions.find((s) => s.id === sessionId);

            // Only add to breakdown if rate > 0 (exclude free items)
            if (
              sessionFee &&
              session &&
              sessionFee.rate &&
              parseFloat(sessionFee.rate) > 0
            ) {
              console.log(
                `      ✅ Adding focused session: ${
                  session.value || session.name
                }`,
              );
              breakdown.push({
                type: "ADDITIONAL_FEE",
                feeId: sessionId, // Use sessionId as the feeTypeId (not sessionFee.id!)
                amount: parseFloat(sessionFee.rate),
                currency: sessionFee.currency || currency,
                description: `Focused Session - ${
                  session.value || session.name || `Session ${sessionId}`
                }`,
              });
            }
          });
        } else {
          console.log("      ⏭️  No focused sessions selected");
        }
      }

      // Add conveyance fee as a separate item in the breakdown
      // Calculate conveyance fee based on the sum of all items in breakdown (skip if null)
      if (
        breakdown.length > 0 &&
        conveyanceFeePercentage !== null &&
        conveyanceFeePercentage > 0
      ) {
        const subtotal = breakdown.reduce(
          (sum, item) => sum + parseFloat(item.amount),
          0,
        );
        const conveyanceFeeForBreakdown =
          subtotal * (conveyanceFeePercentage / 100);

        breakdown.push({
          type: "CONVEYANCE_FEE",
          feeId: null, // No specific fee ID for conveyance fee
          amount: parseFloat(conveyanceFeeForBreakdown.toFixed(2)),
          currency: currency,
          description: `Conveyance Fee (${conveyanceFeePercentage}%)`,
        });

        console.log("💳 Added conveyance fee to breakdown:");
        console.log("  Subtotal:", subtotal);
        console.log(
          "  Conveyance fee percentage:",
          conveyanceFeePercentage,
          "%",
        );
        console.log("  Conveyance fee amount:", conveyanceFeeForBreakdown);
        console.log("  Total breakdown items now:", breakdown.length);
      }

      // Special handling for zero-amount orders (free items or already paid)
      if (amountInSmallestUnit === 0 || actualAmount === 0) {
        console.log("🆓 Zero amount detected - no payment needed");
        console.log("  This can happen when:");
        console.log(
          "    - User already paid base fee (formData.paid === true)",
        );
        console.log(
          "    - No new accompanying persons or focused sessions added",
        );
        console.log("    - All selected items are free (rate = 0)");

        // For zero-amount orders, we don't need to process payment
        // User should just proceed without payment or save as zero-payment record
        alert(
          "✅ No payment required. All items are either already paid or free.",
        );
        setProcessingPayment(false);
        setPaymentInitiated(false);
        return;
      }

      console.log("📋 Payment Breakdown Summary:");
      console.log("  Total items in breakdown:", breakdown.length);
      console.log("  Breakdown details:", JSON.stringify(breakdown, null, 2));
      console.log("  Total amount:", actualAmount, currency);
      console.log("  Amount in smallest unit:", amountInSmallestUnit);

      // Detailed breakdown validation
      if (breakdown.length === 0) {
        console.error("❌ WARNING: Breakdown array is EMPTY!");
        console.error(
          "  This means no items will be saved to PaymentBreakdownTempEntity table",
        );
        console.error("  Check why breakdown items are not being added");
      } else {
        console.log("✅ Breakdown validation:");
        breakdown.forEach((item, index) => {
          console.log(`  [${index}] ${item.type}: ${item.description}`);
          console.log(
            `      feeId: ${item.feeId}, amount: ${item.amount} ${item.currency}`,
          );
        });
      }

      // Prepare order request payload
      // IMPORTANT: Use registrationId (from user table), NOT registrationDbId (from registration table)
      const orderRequestPayload = {
        amountInPaise: amountInSmallestUnit,
        currency: currency,
        registrationId: registrationId, // Use registrationId, not registrationDbId
        breakdown: breakdown,
      };

      console.log("🚀 Creating order at endpoint:", ORDER_URL);
      console.log(
        "📤 Order Request Payload:",
        JSON.stringify(orderRequestPayload, null, 2),
      );
      console.log("  - registrationId (user table ID):", registrationId);
      console.log(
        "  - registrationDbId (registration table ID):",
        registrationDbId,
      );
      console.log("  - amountInPaise:", amountInSmallestUnit);
      console.log("  - currency:", currency);
      console.log("  - breakdown.length:", breakdown.length);

      const orderResp = await fetch(ORDER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(orderRequestPayload),
      });

      console.log(
        "📥 Order Response Status:",
        orderResp.status,
        orderResp.statusText,
      );

      if (!orderResp.ok) {
        const errorText = await orderResp.text();
        console.error("Order creation failed:", errorText);
        alert(`Unable to create payment order: ${errorText}`);
        setProcessingPayment(false);
        setPaymentInitiated(false); // Reset flag on order creation error
        return;
      }

      const order = await orderResp.json();

      console.log("✅ Order created successfully");
      console.log("📦 Order Response:", JSON.stringify(order, null, 2));
      console.log("🔑 Order identifiers:");
      console.log("  order.id (backend DB):", order.id);
      console.log("  order.orderId (Razorpay):", order.orderId);
      console.log(
        "📊 Breakdown should now be in PaymentBreakdownTempEntity table with orderId:",
        order.id,
      );

      // Validate required data first
      if (!RAZORPAY_KEY_ID) {
        throw new Error(
          "Razorpay key is not configured. Please check your .env file.",
        );
      }

      // Check if order response is valid
      if (!order || typeof order !== "object") {
        throw new Error("Invalid order response format");
      }

      // Check for the specific error from Java controller
      if (
        order.mapType === "java.util.HashMap" &&
        order.empty === false &&
        !order.id
      ) {
        throw new Error(
          "Backend configuration error: The payment controller is not returning order data properly. Please check Razorpay keys in backend configuration.",
        );
      }

      // The PaymentController returns a JSONObject, check for common properties
      if (!order.id && !order.orderId) {
        console.error("Order response missing ID:", order);
        throw new Error(
          "Invalid order response - missing order ID. Response: " +
            JSON.stringify(order),
        );
      }

      await loadRazorpayScript();

      // Build Razorpay options based on environment
      const options = {
        key: RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency || currency,
        name: "Conference Registration",
        description: "Event registration fee",
        order_id: order.id || order.orderId,
        notes: {
          registrationId: registrationDbId || formData.regId || "",
        },
        ...(isProduction
          ? {
              // PRODUCTION: Webhook handles payment, handler just shows success UI
              handler: async function (response) {
                console.log(
                  "🏭 PRODUCTION: Payment completed, webhook will process",
                );

                // Just show success messages and redirect
                // Webhook already processed the payment (including paper status update)
                alert("Payment successful! Processing registration...");
                setTimeout(async () => {
                  alert("Registered successfully. Details Emailed");

                  // If abstractStepNumber is 7, reload registration to show abstract form
                  if (abstractStepNumber === 7) {
                    console.log(
                      "📝 Abstract at step 7 - reloading registration to show abstract form",
                    );
                    try {
                      // Reload registration to get updated paid status
                      await checkExistingRegistration(
                        formData.email,
                        conferenceName,
                      );
                      // Stay on step 7 to show abstract form
                      setStep(7);
                    } catch (error) {
                      console.error("Error reloading registration:", error);
                      // Fallback: manually update paid status and stay on step 7
                      setFormData((prev) => ({ ...prev, paid: true }));
                      setStep(7);
                    }
                  } else {
                    // Redirect to login for normal flow
                    window.location.href = "/";
                  }
                }, 500);
              },
            }
          : {
              // LOWER ENV: Use handler (current behavior)
              handler: async function (response) {
                try {
                  // Clean up Razorpay instance on success
                  if (razorpayInstanceRef.current) {
                    razorpayInstanceRef.current = null;
                  }
                  razorpayOpenRef.current = false; // Reset popup flag

                  // Basic validation - ensure we have all required fields
                  if (
                    !response.razorpay_payment_id ||
                    !response.razorpay_order_id ||
                    !response.razorpay_signature
                  ) {
                    throw new Error(
                      "Incomplete payment response from Razorpay",
                    );
                  }

                  // Step 1: Ensure registration is saved first to get an ID
                  let registrationId = formData.id;
                  let registrationDbId = formData.regId;

                  if (!registrationId) {
                    try {
                      // Map to new v2 API format with user profile data for new user creation
                      const conferenceRegistrationData = {
                        // User identification
                        email: formData.email,

                        // User profile data (for new user creation if needed)
                        title: formData.title,
                        firstName: formData.firstName,
                        lastName: formData.lastName,
                        phone: formData.mobile,
                        country: formData.country,
                        state: formData.state,
                        city: formData.city,
                        address: formData.address,
                        zipCode: formData.pincode,
                        affiliation: formData.affiliation,
                        institution: formData.institution,
                        // Conference details
                        conference:
                          formData.conference || conferenceName || currentHost,
                        prefix: formData.prefix || "",
                        fullNameCertificate: formData.fullNameCertificate,
                        mciRegistered: formData.mciRegistered || "No",
                        mciName: formData.mciName || "",
                        mciNumber: formData.mciNumber || "",
                        department: formData.department || "",
                        departmentId: formData.departmentId,
                        otherDepartment: formData.otherDepartment,
                        category: formData.category || "TBD",
                        categoryId: formData.categoryId,
                        registrationType: formData.registrationType || "TBD",
                        registrationTypeId: formData.registrationTypeId,
                        membershipNumber: formData.membershipNumber || "",
                        // Don't send base64 data to studentIdURL - it's too large and handled separately
                        studentIdURL:
                          formData.studentIdCard &&
                          !formData.studentIdCard.startsWith("data:")
                            ? formData.studentIdCard
                            : null,
                        specialRequirements: formData.specialRequirements || "",
                        additionalData: buildAdditionalDataPayload(),
                        accommodationNeeded:
                          formData.accommodationNeeded || false,
                        notes: formData.notes || "",
                      };

                      // Determine if this is an update or new registration in payment handler
                      const isHandlerUpdate = formData.id || formData.regId;
                      const handlerMethod = isHandlerUpdate ? "PUT" : "POST";
                      // Use regId if available, otherwise use id as fallback
                      const handlerRegistrationId =
                        formData.regId || formData.id;
                      const handlerUrl = isHandlerUpdate
                        ? `${CONFERENCE_REGISTRATION_URL}/${handlerRegistrationId}`
                        : CONFERENCE_REGISTRATION_URL;

                      const regSaveRes = await fetch(handlerUrl, {
                        method: handlerMethod,
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(conferenceRegistrationData),
                      });

                      if (regSaveRes.ok) {
                        const savedReg = await regSaveRes.json();
                        registrationId = savedReg.registrationId;
                        registrationDbId = savedReg.dbId || savedReg.id;

                        // Update form data with the new ID
                        setFormData((prev) => ({
                          ...prev,
                          id: savedReg.registrationId,
                          regId: savedReg.dbId || savedReg.id,
                        }));
                      } else {
                        const regError = await regSaveRes.text();
                        console.error("Failed to save registration:", regError);
                        throw new Error(
                          `Failed to save registration: ${regError}`,
                        );
                      }
                    } catch (regError) {
                      console.error(
                        "Error saving registration before payment:",
                        regError,
                      );
                      throw new Error(
                        `Could not save registration before payment: ${regError.message}`,
                      );
                    }
                  }

                  // Step 2: Save payment entity with registration ID (Lower env only - production uses callback_url)
                  try {
                    console.log(
                      "💳 Saving payment (backend will read breakdown from temp table)",
                    );
                    console.log("� Payment identifiers:");
                    console.log(
                      "  orderId (for temp table lookup):",
                      order.id || order.orderId,
                    );
                    console.log(
                      "  razorpayOrderId:",
                      response.razorpay_order_id,
                    );
                    console.log(
                      "  razorpayPaymentId:",
                      response.razorpay_payment_id,
                    );
                    console.log("  registrationDbId:", registrationDbId);

                    // Validate critical fields before sending
                    const orderIdForSave =
                      order.id || order.orderId || response.razorpay_order_id;
                    if (!orderIdForSave) {
                      console.error("❌ CRITICAL: orderId is null/undefined!");
                      console.error("  order:", JSON.stringify(order, null, 2));
                      console.error(
                        "  response:",
                        JSON.stringify(response, null, 2),
                      );
                      throw new Error(
                        "Cannot save payment: orderId is missing. Backend needs this to lookup breakdown from temp table.",
                      );
                    }

                    if (!registrationDbId) {
                      console.error(
                        "❌ CRITICAL: registrationDbId is null/undefined!",
                      );
                      console.error("  formData.regId:", formData.regId);
                      console.error("  formData.id:", formData.id);
                      throw new Error(
                        "Cannot save payment: registrationDbId is missing.",
                      );
                    }

                    const paymentSaveData = {
                      regId: registrationDbId,
                      orderId: orderIdForSave, // ✅ Backend uses this to lookup temp table
                      razorpayOrderId: response.razorpay_order_id,
                      razorpayPaymentId: response.razorpay_payment_id,
                      razorpaySignature: response.razorpay_signature,
                      status: "SUCCESS",
                      amountPaid: amountInSmallestUnit / 100, // ✅ Uses totalAmountWithFee (includes 2.9% convenience fee)
                      currency: order.currency || currency,
                      // ❌ NOT SENDING BREAKDOWN - Backend reads from temp table using orderId
                    };

                    console.log(
                      "📤 Payment save payload:",
                      JSON.stringify(paymentSaveData, null, 2),
                    );
                    console.log(
                      "✅ Payload validation passed - all critical fields present",
                    );
                    console.log("💰 Amount breakdown:");
                    console.log(
                      "  amountInSmallestUnit (with fee):",
                      amountInSmallestUnit,
                    );
                    console.log(
                      "  amountPaid (in main currency):",
                      paymentSaveData.amountPaid,
                    );
                    console.log("  This includes 2.9% convenience fee");

                    const paymentSaveRes = await fetch(PAYMENT_SAVE_URL, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify(paymentSaveData),
                    });

                    if (paymentSaveRes.ok) {
                      const paymentResult = await paymentSaveRes.json();
                    } else {
                      const paymentError = await paymentSaveRes.text();
                      console.error("❌ Payment entity save failed!");
                      console.error(
                        "  HTTP Status:",
                        paymentSaveRes.status,
                        paymentSaveRes.statusText,
                      );
                      console.error("  Response body:", paymentError);
                      console.error(
                        "  Payload sent:",
                        JSON.stringify(paymentSaveData, null, 2),
                      );
                      console.warn("Payment entity save failed:", paymentError);
                      // Continue anyway since we still want to update the registration with payment info
                    }
                  } catch (paymentError) {
                    console.warn("Error saving payment entity:", paymentError);
                    // Continue anyway since we still want to update the registration with payment info
                  }

                  // Step 3: Confirm registration (marks as paid and confirmed)
                  const confirmUrl = `${REGISTRATION_BASE_URL}/registration/${registrationId}/confirm`;

                  // console.log("Confirming registration:", confirmUrl);

                  const confirmRes = await fetch(confirmUrl, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                  });

                  // console.log("Registration confirm response status:", confirmRes.status);

                  if (!confirmRes.ok) {
                    const errorText = await confirmRes.text();
                    //console.error("Registration confirmation failed:", errorText);
                    alert(
                      `Payment verified but confirming registration failed: ${errorText}`,
                    );
                  } else {
                    // Show immediate success message
                    alert("Payment successful! Processing registration...");

                    // Process confirmation in background
                    const processConfirmation = async () => {
                      try {
                        const confirmed = await confirmRes.json();

                        setFormData((prev) => ({
                          ...prev,
                          paid: true,
                          id: confirmed.registrationId,
                          regId: confirmed.id,
                        }));

                        // Show final success message
                        setTimeout(() => {
                          alert("Registered successfully. Details Emailed");

                          // If abstractStepNumber is 7, stay on step 7 to show abstract form
                          if (abstractStepNumber === 7) {
                            console.log(
                              "📝 Abstract at step 7 - staying on step 7 to show abstract form",
                            );
                            setStep(7); // Stay on step 7 to show abstract form (formData.paid is already set to true above)
                          } else {
                            // Otherwise redirect to step 1
                            setStep(1);
                          }
                        }, 500);
                      } catch (confirmError) {
                        console.error(
                          "Background confirmation error:",
                          confirmError,
                        );
                        alert(
                          "Payment successful but confirmation pending. Check your email.",
                        );
                      }
                    };

                    processConfirmation();
                  }
                } catch (err) {
                  console.error("Error in payment handler:", err);

                  // Capture this processing error as a failed payment
                  try {
                    await captureFailedPayment(
                      {
                        code: "PROCESSING_ERROR",
                        description: err.message,
                        source: "payment_handler",
                        step: "payment_processing",
                        reason: "processing_failed",
                      },
                      order,
                    );
                  } catch (captureError) {
                    console.warn(
                      "Failed to capture payment processing error:",
                      captureError,
                    );
                  }

                  // Reset payment initiated flag so user can try again
                  setPaymentInitiated(false);

                  // Show user-friendly message and redirect
                  alert(
                    "Payment processing failed. Please try again after sometime.",
                  );

                  // Redirect to summary step
                  setTimeout(() => {
                    setStep(5);
                  }, 1000);
                }
              },
            }), // Close handler only for lower env
        prefill: {
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          contact: formData.mobile,
        },
        theme: { color: "#1976d2" },
      };

      const rzp = new window.Razorpay(options);
      razorpayInstanceRef.current = rzp; // Store reference

      rzp.on("payment.failed", async function (response) {
        console.error("Payment failed:", response.error);

        // Use ref to prevent any duplicate handling - this is crucial
        if (paymentFailureHandledRef.current) {
          return;
        }
        paymentFailureHandledRef.current = true;
        razorpayOpenRef.current = false; // Reset popup flag

        // Close the Razorpay popup immediately and clean up
        try {
          if (razorpayInstanceRef.current) {
            razorpayInstanceRef.current.close();
            razorpayInstanceRef.current = null;
          }
        } catch (closeError) {
          console.warn("Could not close Razorpay popup:", closeError);
        }

        // Additional state-based prevention
        if (paymentErrorShown) {
          return;
        }
        setPaymentErrorShown(true);

        try {
          // Capture failed payment details
          await captureFailedPayment(response.error, order);
        } catch (captureError) {
          console.warn(
            "Failed to capture failed payment details:",
            captureError,
          );
        }

        // Reset payment initiated flag so user can try again
        setPaymentInitiated(false);

        // Show user-friendly message and redirect - use longer delay to prevent race conditions
        setTimeout(() => {
          if (paymentFailureHandledRef.current) {
            // Double check before showing alert
            alert(
              "Payment failed. Please try again after sometime or use a different payment method.",
            );

            // Redirect to summary step
            setTimeout(() => {
              setStep(5);
            }, 500);
          }
        }, 200);
      });

      // Prevent multiple popups from opening
      if (razorpayOpenRef.current) {
        setProcessingPayment(false);
        setPaymentInitiated(false);
        return;
      }

      razorpayOpenRef.current = true;
      rzp.open();
    } catch (err) {
      console.error("Payment error", err);

      // Prevent duplicate error alerts - check both ref and state
      if (!paymentFailureHandledRef.current && !paymentErrorShown) {
        paymentFailureHandledRef.current = true;
        setPaymentErrorShown(true);
        razorpayOpenRef.current = false; // Reset popup flag

        // Capture payment initialization failure
        try {
          await captureFailedPayment(
            {
              code: "INITIALIZATION_ERROR",
              description: err.message,
              source: "payment_initialization",
              step: "payment_setup",
              reason: "initialization_failed",
            },
            {
              currency: currency,
              amount: amountInSmallestUnit,
            },
          );
        } catch (captureError) {
          console.warn(
            "Failed to capture payment initialization error:",
            captureError,
          );
        }

        // Show user-friendly message and redirect
        setTimeout(() => {
          alert(
            "Payment setup failed. Please try again after sometime or check your details.",
          );

          // Redirect to step 1 to check details
          setTimeout(() => {
            setStep(1);
          }, 500);
        }, 200);
      } else {
      }

      setPaymentInitiated(false); // Reset flag on error
    } finally {
      setProcessingPayment(false);
      // Ensure popup flag is reset in finally block
      if (razorpayOpenRef.current) {
        razorpayOpenRef.current = false;
      }
    }
  };

  // Function to capture failed payment details
  const captureFailedPayment = async (error, orderData) => {
    try {
      // Ensure we have a registration ID
      let registrationId = formData.id;
      let registrationDbId = formData.regId;

      if (!registrationId) {
        try {
          // Map to new v2 API format with user profile data for new user creation
          const conferenceRegistrationData = {
            // User identification
            email: formData.email,

            // User profile data (for new user creation if needed)
            title: formData.title,
            firstName: formData.firstName,
            lastName: formData.lastName,
            phone: formData.mobile,
            country: formData.country,
            state: formData.state,
            city: formData.city,
            address: formData.address,
            zipCode: formData.pincode,
            affiliation: formData.affiliation,
            institution: formData.institution,
            // Conference details
            conference: formData.conference || conferenceName || currentHost,
            prefix: formData.prefix || "",
            fullNameCertificate: formData.fullNameCertificate,
            mciRegistered: formData.mciRegistered || "No",
            mciName: formData.mciName || "",
            mciNumber: formData.mciNumber || "",
            department: formData.department || "",
            departmentId: formData.departmentId,
            otherDepartment: formData.otherDepartment,
            category: formData.category || "TBD",
            categoryId: formData.categoryId,
            registrationType: formData.registrationType || "TBD",
            registrationTypeId: formData.registrationTypeId,
            membershipNumber: formData.membershipNumber || "",
            // Don't send base64 data to studentIdURL - it's too large and handled separately
            studentIdURL:
              formData.studentIdCard &&
              !formData.studentIdCard.startsWith("data:")
                ? formData.studentIdCard
                : null,
            specialRequirements: formData.specialRequirements || "",
            additionalData: buildAdditionalDataPayload(),
            accommodationNeeded: formData.accommodationNeeded || false,
            notes: formData.notes || "",
          };

          // Determine if this is an update or create operation
          const isUpdate = !!(formData.id || formData.regId);
          const method = isUpdate ? "PUT" : "POST";
          // Use regId if available, otherwise use id as fallback
          const updateRegistrationId = formData.regId || formData.id;
          const url = isUpdate
            ? `${CONFERENCE_REGISTRATION_URL}/${updateRegistrationId}`
            : CONFERENCE_REGISTRATION_URL;

          const regSaveRes = await fetch(url, {
            method: method,
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(conferenceRegistrationData),
          });

          if (regSaveRes.ok) {
            const savedReg = await regSaveRes.json();
            registrationId = savedReg.registrationId;
            registrationDbId = savedReg.dbId || savedReg.id;

            setFormData((prev) => ({
              ...prev,
              id: savedReg.registrationId,
              regId: savedReg.dbId || savedReg.id,
            }));
          }
        } catch (regError) {
          console.warn(
            "Could not save registration for failed payment:",
            regError,
          );
        }
      }

      const failedPaymentData = {
        regId: registrationDbId,
        razorpayOrderId: orderData?.id || orderData?.orderId || "unknown",
        razorpayPaymentId: null,
        razorpaySignature: null,
        status: "FAILED",
        paymentStatus: "FAILED",
        amount: orderData?.amount || 0,
        amountPaid: 0,
        currency:
          orderData?.currency || (formData.country === "India" ? "INR" : "USD"),
        paymentMethod: "razorpay",
        paymentGateway: "Razorpay",
        transactionTime: new Date().toISOString(),
        failureTime: new Date().toISOString(),
        orderAmount: orderData?.amount ? orderData.amount / 100 : 0,
        gatewayTransactionId: null,
        errorCode: error?.code || "UNKNOWN_ERROR",
        errorDescription:
          error?.description || error?.message || "Unknown error",
        errorSource: error?.source || "razorpay",
        errorStep: error?.step || "payment_gateway",
        errorReason: error?.reason || "payment_failed",
        metadata: JSON.stringify({
          userAgent: navigator.userAgent,
          timestamp: Date.now(),
          errorDetails: {
            code: error?.code,
            description: error?.description,
            source: error?.source,
            step: error?.step,
            reason: error?.reason,
          },
          orderInfo: {
            orderId: orderData?.id || orderData?.orderId,
            amount: orderData?.amount,
            currency: orderData?.currency,
          },
          userInfo: {
            email: formData.email,
            mobile: formData.mobile,
            country: formData.country,
            name: `${formData.firstName} ${formData.lastName}`.trim(),
          },
        }),
      };

      const failedPaymentRes = await fetch(PAYMENT_SAVE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(failedPaymentData),
      });

      if (failedPaymentRes.ok) {
        const result = await failedPaymentRes.json();
      } else {
        const errorText = await failedPaymentRes.text();
        console.warn("Failed to capture failed payment:", errorText);
      }
    } catch (captureError) {
      console.error("Error capturing failed payment:", captureError);
    }
  };

  const loadRazorpayScript = () =>
    new Promise((resolve, reject) => {
      if (window.Razorpay) return resolve(true);
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        console.error("Failed to load Razorpay script");
        reject(new Error("Razorpay SDK failed to load."));
      };
      document.body.appendChild(script);
    });

  // Check if registration has been saved (has ID)
  const hasRegistrationId = !!(formData.id || formData.regId);

  // Step fields
  const renderStepFields = () => {
    console.log(
      "renderStepFields - step:",
      step,
      "isAbstractRequired:",
      isAbstractRequired,
    );

    // Show paper submission summary page (step 3.x) if user just submitted paper
    if (
      step === 3 &&
      showPaperSubmissionSummary &&
      wantsToSubmitAbstract === true &&
      hasPaperSubmitted
    ) {
      return (
        <>
          <div
            className="grid-item grid-item--full"
            style={{ marginBottom: "20px" }}
          >
            <div
              style={{
                padding: "16px",
                backgroundColor: "#e3f2fd",
                border: "2px solid #2196f3",
                borderRadius: "8px",
              }}
            >
              <h3
                style={{
                  marginTop: 0,
                  marginBottom: "15px",
                  fontSize: "18px",
                  color: "#1565c0",
                }}
              >
                👤 Registration Details
              </h3>
              <div
                style={{ lineHeight: "1.8", fontSize: "15px", color: "#333" }}
              >
                <div style={{ marginBottom: "12px" }}>
                  <strong>Name:</strong> {formData.title} {formData.firstName}{" "}
                  {formData.lastName}
                </div>
                <div style={{ marginBottom: "12px" }}>
                  <strong>Email:</strong> {formData.email}
                </div>
                <div style={{ marginBottom: "12px" }}>
                  <strong>Phone:</strong> {formData.mobile}
                </div>
                <div style={{ marginBottom: "12px" }}>
                  <strong>Country:</strong> {formData.country}
                </div>
                <div style={{ marginBottom: "12px" }}>
                  <strong>City:</strong> {formData.city}
                </div>
              </div>
            </div>
          </div>

          <div
            className="grid-item grid-item--full"
            style={{ marginBottom: "20px" }}
          >
            <div
              style={{
                padding: "16px",
                backgroundColor: "#e8f5e9",
                border: "2px solid #4caf50",
                borderRadius: "8px",
              }}
            >
              <h3
                style={{
                  marginTop: 0,
                  marginBottom: "15px",
                  fontSize: "18px",
                  color: "#212529",
                }}
              >
                ✅ Paper Submission Summary
              </h3>
              <div
                style={{ lineHeight: "1.8", fontSize: "15px", color: "#333" }}
              >
                <div style={{ marginBottom: "12px" }}>
                  <strong>Paper Title:</strong> {paperTitle}
                </div>
                <div style={{ marginBottom: "12px" }}>
                  <strong>Track:</strong> {selectedTrackName}
                </div>
                <div style={{ marginBottom: "12px" }}>
                  <strong>Presentation Mode:</strong> {presentationMode}
                </div>
                <div style={{ marginBottom: "12px" }}>
                  <strong>Submission Method:</strong> {submissionMethod}
                </div>
                <div
                  style={{
                    marginTop: "20px",
                    padding: "12px",
                    backgroundColor: "#f0f0f0",
                    borderRadius: "4px",
                    fontSize: "13px",
                    color: "#666",
                  }}
                >
                  <strong>Abstract Status:</strong>{" "}
                  {normalizedPaperApprovalStatus === "SUBMITTED"
                    ? "📤 Under Review"
                    : normalizedPaperApprovalStatus === "DRAFT"
                      ? "💾 Saved as Draft"
                      : isRevisionPaperStatus
                        ? "📝 Revision Requested"
                        : normalizedPaperApprovalStatus}
                </div>
              </div>
            </div>
          </div>

          {(paperApprovalStatus === "SUBMITTED" || isRevisionPaperStatus) && (
            <div
              className="grid-item grid-item--full"
              style={{ marginBottom: "20px" }}
            >
              <div
                style={{
                  padding: "16px",
                  backgroundColor: "#fff3cd",
                  border: "2px solid #ffc107",
                  borderRadius: "8px",
                  lineHeight: "1.7",
                }}
              >
                <div
                  style={{
                    marginBottom: "12px",
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#856404",
                  }}
                >
                  {isRevisionPaperStatus
                    ? "📝 Revision Requested"
                    : "⏳ Paper Under Review"}
                </div>
                {isRevisionPaperStatus ? (
                  <>
                    <div
                      style={{
                        fontSize: "15px",
                        color: "#856404",
                        marginBottom: "10px",
                      }}
                    >
                      Your paper requires revision. Please update the paper
                      according to reviewer feedback and resubmit.
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#856404",
                        fontStyle: "italic",
                      }}
                    >
                      You can review the submission summary, go back to edit,
                      and then resubmit the revised paper.
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      style={{
                        fontSize: "15px",
                        color: "#856404",
                        marginBottom: "10px",
                      }}
                    >
                      Your paper has been submitted for review. The review team
                      will evaluate your submission and provide feedback.
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#856404",
                        fontStyle: "italic",
                      }}
                    >
                      You will receive an email notification once your paper has
                      been reviewed and approved. Please log in again to
                      continue with your registration.
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {paperApprovalStatus === "DRAFT" && (
            <div
              className="grid-item grid-item--full"
              style={{ marginBottom: "20px" }}
            >
              <div
                style={{
                  padding: "16px",
                  backgroundColor: "#e8f4f8",
                  border: "2px solid #00bcd4",
                  borderRadius: "8px",
                  lineHeight: "1.7",
                }}
              >
                <div
                  style={{
                    marginBottom: "12px",
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#00838f",
                  }}
                >
                  📝 Draft Saved
                </div>
                <div
                  style={{
                    fontSize: "15px",
                    color: "#00838f",
                    marginBottom: "10px",
                  }}
                >
                  Your paper has been saved as draft. You can submit it anytime
                  you're ready.
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    color: "#00838f",
                    fontStyle: "italic",
                  }}
                >
                  You can continue with your registration now and submit the
                  paper later, or submit it immediately before proceeding.
                </div>
              </div>
            </div>
          )}
        </>
      );
    }

    switch (step) {
      case 1:
        return (
          <React.Fragment>
            <div className="grid-item">
              <select
                name="title"
                id="title"
                value={formData.title}
                onChange={handleChange}
                required
                style={{ borderColor: errors.title ? "#dc3545" : "" }}
              >
                <option value="" disabled hidden>
                  Title *
                </option>
                <option value="Dr.">Dr.</option>
                <option value="Mr.">Mr.</option>
                <option value="Ms.">Ms.</option>
                <option value="Mrs.">Mrs.</option>
                <option value="Prof.">Prof.</option>
              </select>
              {errors.title && <p className="error-text">{errors.title}</p>}
            </div>
            <div className="grid-item">
              <input
                name="firstName"
                placeholder="First Name *"
                value={formData.firstName}
                onChange={handleChange}
              />
              {errors.firstName && (
                <p className="error-text">{errors.firstName}</p>
              )}
            </div>
            <div className="grid-item">
              <input
                name="lastName"
                placeholder="Last Name *"
                value={formData.lastName}
                onChange={handleChange}
              />
              {errors.lastName && (
                <p className="error-text">{errors.lastName}</p>
              )}
            </div>
            <div className="grid-item">
              <input
                name="email"
                placeholder="Email *"
                value={formData.email}
                onChange={handleChange}
              />
              {errors.email && <p className="error-text">{errors.email}</p>}
            </div>
            <div className="grid-item">
              <input
                type="tel"
                name="mobile"
                placeholder="Phone/Mobile Number *"
                value={formData.mobile}
                onChange={handleChange}
                maxLength={MOBILE_MAX_LENGTH}
                inputMode="numeric"
                pattern="[0-9]*"
              />
              {errors.mobile && <p className="error-text">{errors.mobile}</p>}
            </div>
            {genderOptions.length > 0 && (
              <div className="grid-item">
                <select
                  name="gender"
                  id="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  required
                  style={{ borderColor: errors.gender ? "#dc3545" : "" }}
                >
                  <option value="" disabled hidden>
                    Gender *
                  </option>
                  {genderOptions.map((option) => (
                    <option key={option.id} value={option.value}>
                      {option.value}
                    </option>
                  ))}
                </select>
                {errors.gender && <p className="error-text">{errors.gender}</p>}
              </div>
            )}
          </React.Fragment>
        );
      case 2:
        return (
          <React.Fragment>
            <div className="grid-item">
              <Select
                classNamePrefix="country-select"
                options={countries}
                value={
                  countries.find((c) => c.value === formData.country) || null
                }
                onChange={handleCountryChange}
                placeholder="Country *"
                isClearable
              />
              {errors.country && <p className="error-text">{errors.country}</p>}
            </div>
            <div className="grid-item">
              <input
                name="state"
                placeholder="State"
                value={formData.state}
                onChange={handleChange}
              />
            </div>
            <div className="grid-item">
              <input
                name="city"
                placeholder="City *"
                value={formData.city}
                onChange={handleChange}
                required
              />
            </div>
            <div className="grid-item">
              <input
                name="pincode"
                placeholder="Pincode *"
                value={formData.pincode}
                onChange={handleChange}
                required
              />
            </div>
            <div className="grid-item grid-item--full">
              <input
                name="address"
                placeholder="Address *"
                value={formData.address}
                onChange={handleChange}
                required
              />
            </div>
            {/* <div className="grid-item grid-item--full">
              <label style={{
                display: 'block',
                marginBottom: '5px',
                color: '#000',
                fontWeight: '500',
                fontSize: '14px'
              }}>
                Dietary Restrictions <span style={{ color: '#666', fontWeight: '400' }}>(Optional)</span>
              </label>
              <select
                name="dietaryRestrictions"
                value={formData.dietaryRestrictions || ""}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="">None</option>
                <option value="Vegetarian">Vegetarian</option>
                <option value="Vegan">Vegan</option>
                <option value="Gluten-Free">Gluten-Free</option>
                <option value="Halal">Halal</option>
                <option value="Kosher">Kosher</option>
                <option value="Other">Other</option>
              </select>
            </div> */}
          </React.Fragment>
        );
      case 3:
        // Paper/Abstract Upload Step - only show if abstractStepNumber is 3
        // If abstractStepNumber is not 3, the useEffect will automatically redirect to step 4
        if (abstractStepNumber !== 3) {
          return null;
        }
        // Paper/Abstract Upload Step - always show with choice buttons
        return (
          <>
            <div
              className="grid-item grid-item--full"
              style={{ marginBottom: "20px" }}
            >
              <div
                style={{
                  padding: "16px",
                  backgroundColor: "#f8f9fa",
                  border: "2px solid #dee2e6",
                  borderRadius: "8px",
                }}
              >
                <h3
                  style={{
                    marginTop: 0,
                    marginBottom: "15px",
                    fontSize: "18px",
                    color: "#212529",
                  }}
                >
                  {hasPaperSubmitted
                    ? "Paper/Abstract Submission"
                    : "Do you want to submit an abstract?"}
                </h3>
                <div style={{ display: "flex", gap: "15px" }}>
                  <button
                    type="button"
                    onClick={() => setWantsToSubmitAbstract(true)}
                    style={{
                      flex: 1,
                      padding: "12px 20px",
                      fontSize: "15px",
                      fontWeight: "500",
                      border:
                        wantsToSubmitAbstract === true
                          ? "2px solid #28a745"
                          : "1px solid #dee2e6",
                      backgroundColor:
                        wantsToSubmitAbstract === true ? "#d4edda" : "#fff",
                      color:
                        wantsToSubmitAbstract === true ? "#155724" : "#495057",
                      borderRadius: "6px",
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                  >
                    {hasPaperSubmitted
                      ? "📄 View/Edit Abstract"
                      : "✅ Yes, I want to submit an abstract"}
                  </button>
                  {!hasPaperSubmitted && (
                    <button
                      type="button"
                      onClick={() => {
                        setWantsToSubmitAbstract(false);
                        // Move to step 4 when user chooses not to submit abstract
                        setStep(4);
                      }}
                      style={{
                        flex: 1,
                        padding: "12px 20px",
                        fontSize: "15px",
                        fontWeight: "500",
                        border:
                          wantsToSubmitAbstract === false
                            ? "2px solid #007bff"
                            : "1px solid #dee2e6",
                        backgroundColor:
                          wantsToSubmitAbstract === false ? "#cce5ff" : "#fff",
                        color:
                          wantsToSubmitAbstract === false
                            ? "#004085"
                            : "#495057",
                        borderRadius: "6px",
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                    >
                      ℹ️ Proceed with Registration
                    </button>
                  )}
                </div>
              </div>
            </div>

            {wantsToSubmitAbstract === true && (
              <>
                {isPaperLocked && (
                  <div
                    className="grid-item grid-item--full"
                    style={{ marginBottom: "15px" }}
                  >
                    <div
                      style={{
                        padding: "12px 16px",
                        backgroundColor: "#fff3cd",
                        border: "1px solid #ffc107",
                        borderRadius: "6px",
                        fontSize: "14px",
                        color: "#856404",
                      }}
                    >
                      <strong>🔒 Paper Locked:</strong> Your paper is currently
                      in review and cannot be edited. If you need to make
                      changes, please contact the conference organizers.
                    </div>
                  </div>
                )}

                <div
                  className="grid-item grid-item--full"
                  style={{ marginBottom: "15px" }}
                >
                  <div
                    style={{
                      padding: "12px 16px",
                      backgroundColor: "#e3f2fd",
                      border: "1px solid #2196f3",
                      borderRadius: "6px",
                      fontSize: "14px",
                      color: "#0d47a1",
                    }}
                  >
                    <strong>📄 Paper Submission:</strong> Please provide your
                    paper title, track, abstract (max {maxAbstractChars}{" "}
                    characters), and upload your paper file.
                    {abstractConfig?.submissionDeadline && (
                      <div
                        style={{
                          marginTop: "8px",
                          fontSize: "13px",
                          color: "#1565c0",
                          fontWeight: "bold",
                        }}
                      >
                        📅 Submission Deadline:{" "}
                        {new Date(
                          abstractConfig.submissionDeadline,
                        ).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid-item grid-item--full">
                  <input
                    type="text"
                    placeholder="Paper Title *"
                    value={paperTitle}
                    onChange={(e) => setPaperTitle(e.target.value)}
                    disabled={isPaperLocked}
                    readOnly={isPaperLocked}
                    style={{
                      width: "100%",
                      padding: "10px 14px",
                      border: "1px solid #666",
                      borderRadius: "8px",
                      fontSize: "15px",
                      backgroundColor: isPaperLocked ? "#f5f5f5" : "#fff",
                      cursor: isPaperLocked ? "not-allowed" : "text",
                    }}
                  />
                </div>

                <div className="grid-item grid-item--full">
                  {(() => {
                    console.log("Tracks dropdown state:", {
                      tracks,
                      tracksLength: tracks?.length || 0,
                      selectedTrack,
                      conferenceId,
                    });
                    return null;
                  })()}
                  <select
                    value={selectedTrack}
                    onChange={(e) => setSelectedTrack(e.target.value)}
                    disabled={isPaperLocked}
                    style={{
                      width: "100%",
                      padding: "10px 14px",
                      border: "1px solid #666",
                      borderRadius: "8px",
                      fontSize: "15px",
                      color: selectedTrack ? "#212529" : "#6c757d",
                      backgroundColor: isPaperLocked ? "#f5f5f5" : "#fff",
                      cursor: isPaperLocked ? "not-allowed" : "pointer",
                    }}
                  >
                    <option value="" disabled>
                      Select Track *
                    </option>
                    {tracks && tracks.length > 0 ? (
                      tracks.map((track) => (
                        <option key={track.id} value={track.id}>
                          {track.name}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>
                        Loading tracks...
                      </option>
                    )}
                  </select>
                </div>

                <div className="grid-item grid-item--full">
                  <label
                    style={{
                      display: "block",
                      marginBottom: "10px",
                      fontWeight: "500",
                      color: "#000",
                    }}
                  >
                    Presentation Mode *
                  </label>
                  {(() => {
                    // Parse presentation modes from abstractConfig.mode
                    // Can be single value like "ORAL" or comma-separated like "ORAL,POSTER,VIRTUAL"
                    const modes = abstractConfig?.mode
                      ? abstractConfig.mode.split(",").map((m) => m.trim())
                      : ["ORAL", "POSTER"];

                    // Display icons for common modes
                    const getModeIcon = (mode) => {
                      const modeUpper = mode.toUpperCase();
                      if (modeUpper === "ORAL") return "🎤";
                      if (modeUpper === "POSTER") return "📊";
                      if (modeUpper === "VIRTUAL") return "💻";
                      if (modeUpper === "HYBRID") return "🔄";
                      return "📋";
                    };

                    return (
                      <>
                        <div
                          style={{
                            display: "flex",
                            gap: "15px",
                            flexWrap: "wrap",
                          }}
                        >
                          {modes.map((mode) => (
                            <label
                              key={mode}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                cursor: isPaperLocked
                                  ? "not-allowed"
                                  : "pointer",
                                padding: "10px 15px",
                                border:
                                  presentationMode === mode
                                    ? "2px solid #007bff"
                                    : "1px solid #dee2e6",
                                borderRadius: "6px",
                                backgroundColor: isPaperLocked
                                  ? "#f5f5f5"
                                  : presentationMode === mode
                                    ? "#e7f3ff"
                                    : "#fff",
                                flex:
                                  modes.length === 1
                                    ? "1"
                                    : "1 1 calc(50% - 10px)",
                                minWidth: "150px",
                                transition: "all 0.2s",
                                opacity: isPaperLocked ? 0.6 : 1,
                              }}
                            >
                              <input
                                type="radio"
                                name="presentationMode"
                                value={mode}
                                checked={presentationMode === mode}
                                onChange={(e) =>
                                  setPresentationMode(e.target.value)
                                }
                                disabled={isPaperLocked}
                                style={{
                                  marginRight: "8px",
                                  width: "18px",
                                  height: "18px",
                                  cursor: isPaperLocked
                                    ? "not-allowed"
                                    : "pointer",
                                }}
                              />
                              <span
                                style={{
                                  fontSize: "16px",
                                  fontWeight: "400",
                                  color: "#000",
                                }}
                              >
                                {getModeIcon(mode)} {mode}
                              </span>
                            </label>
                          ))}
                        </div>
                        <div
                          style={{
                            marginTop: "12px",
                            padding: "12px 14px",
                            backgroundColor: "#eaf4ff",
                            border: "1px solid #b6dcff",
                            borderLeft: "4px solid #0b63c7",
                            borderRadius: "8px",
                            color: "#0a3d73",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              marginBottom: "6px",
                              fontSize: "14px",
                              fontWeight: "600",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "18px",
                                height: "18px",
                                borderRadius: "50%",
                                backgroundColor: "#0b63c7",
                                color: "#fff",
                                fontSize: "12px",
                                fontWeight: "700",
                              }}
                            >
                              i
                            </span>
                            Presentation Mode Guidance
                          </div>
                          <div style={{ fontSize: "13px", lineHeight: "1.6" }}>
                            The decision regarding oral or poster presentation
                            will be made by the reviewers. Applicants should be
                            prepared to present in either format.
                          </div>
                        </div>
                        {!presentationMode && modes.length > 1 && (
                          <div
                            style={{
                              marginTop: "8px",
                              padding: "8px 12px",
                              backgroundColor: "#fff3cd",
                              border: "1px solid #ffc107",
                              borderRadius: "4px",
                              fontSize: "13px",
                              color: "#856404",
                            }}
                          >
                            ⚠️ Please select one presentation mode from the
                            options above
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>

                <div className="grid-item grid-item--full">
                  <label
                    style={{
                      display: "block",
                      marginBottom: "10px",
                      fontWeight: "500",
                      color: "#000",
                    }}
                  >
                    Submission Method *
                  </label>
                  <div
                    style={{ display: "flex", gap: "15px", flexWrap: "wrap" }}
                  >
                    {/* <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        cursor: isPaperLocked ? "not-allowed" : "pointer",
                        padding: "10px 15px",
                        border:
                          submissionMethod === "TEXTAREA"
                            ? "2px solid #007bff"
                            : "1px solid #dee2e6",
                        borderRadius: "6px",
                        backgroundColor: isPaperLocked
                          ? "#f5f5f5"
                          : submissionMethod === "TEXTAREA"
                            ? "#e7f3ff"
                            : "#fff",
                        flex: "1",
                        minWidth: "150px",
                        transition: "all 0.2s",
                        opacity: isPaperLocked ? 0.6 : 1,
                      }}
                    >
                      <input
                        type="radio"
                        name="submissionMethod"
                        value="TEXTAREA"
                        checked={submissionMethod === "TEXTAREA"}
                        onChange={(e) => setSubmissionMethod(e.target.value)}
                        disabled={isPaperLocked}
                        style={{
                          marginRight: "8px",
                          width: "18px",
                          height: "18px",
                          cursor: isPaperLocked ? "not-allowed" : "pointer",
                        }}
                      />
                      <span style={{ fontSize: "15px", color: "#000" }}>
                        📝 Text Only
                      </span>
                    </label>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        cursor: isPaperLocked ? "not-allowed" : "pointer",
                        padding: "10px 15px",
                        border:
                          submissionMethod === "FILE"
                            ? "2px solid #007bff"
                            : "1px solid #dee2e6",
                        borderRadius: "6px",
                        backgroundColor: isPaperLocked
                          ? "#f5f5f5"
                          : submissionMethod === "FILE"
                            ? "#e7f3ff"
                            : "#fff",
                        flex: "1",
                        minWidth: "150px",
                        transition: "all 0.2s",
                        opacity: isPaperLocked ? 0.6 : 1,
                      }}
                    >
                      <input
                        type="radio"
                        name="submissionMethod"
                        value="FILE"
                        checked={submissionMethod === "FILE"}
                        onChange={(e) => setSubmissionMethod(e.target.value)}
                        disabled={isPaperLocked}
                        style={{
                          marginRight: "8px",
                          width: "18px",
                          height: "18px",
                          cursor: isPaperLocked ? "not-allowed" : "pointer",
                        }}
                      />
                      <span style={{ fontSize: "15px", color: "#000" }}>
                        📄 File Only
                      </span>
                    </label> */}

                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        cursor: isPaperLocked ? "not-allowed" : "pointer",
                        padding: "10px 15px",
                        border:
                          submissionMethod === "BOTH"
                            ? "2px solid #007bff"
                            : "1px solid #dee2e6",
                        borderRadius: "6px",
                        backgroundColor: isPaperLocked
                          ? "#f5f5f5"
                          : submissionMethod === "BOTH"
                            ? "#e7f3ff"
                            : "#fff",
                        flex: "1",
                        minWidth: "150px",
                        transition: "all 0.2s",
                        opacity: isPaperLocked ? 0.6 : 1,
                      }}
                    >
                      <input
                        type="radio"
                        name="submissionMethod"
                        value="BOTH"
                        checked={submissionMethod === "BOTH"}
                        onChange={(e) => setSubmissionMethod(e.target.value)}
                        disabled={isPaperLocked}
                        style={{
                          marginRight: "8px",
                          width: "18px",
                          height: "18px",
                          cursor: isPaperLocked ? "not-allowed" : "pointer",
                        }}
                      />
                      <span style={{ fontSize: "15px", color: "#000" }}>
                        📝📄 Text & File
                      </span>
                    </label>
                  </div>
                </div>

                {/* Abstract Textarea - Show only if TEXTAREA or BOTH is selected */}
                {(submissionMethod === "TEXTAREA" ||
                  submissionMethod === "BOTH") && (
                  <div className="grid-item grid-item--full">
                    <div style={{ position: "relative" }}>
                      <textarea
                        placeholder={`Structured Abstract * (Maximum ${maxAbstractChars} characters)`}
                        value={abstractText}
                        onChange={(e) => {
                          const text = e.target.value;
                          console.log(
                            "Textarea change - length:",
                            text.length,
                            "max:",
                            maxAbstractChars,
                          );
                          setAbstractText(text);
                        }}
                        maxLength={maxAbstractChars}
                        rows={8}
                        disabled={isPaperLocked}
                        readOnly={isPaperLocked}
                        style={{
                          width: "100%",
                          padding: "10px 14px",
                          border:
                            abstractText.length > maxAbstractChars
                              ? "2px solid #dc3545"
                              : "1px solid #666",
                          borderRadius: "8px",
                          fontSize: "15px",
                          backgroundColor: isPaperLocked ? "#f5f5f5" : "#fff",
                          cursor: isPaperLocked ? "not-allowed" : "text",
                          resize: "vertical",
                          fontFamily: "inherit",
                        }}
                      />
                      <div
                        style={{
                          marginTop: "5px",
                          fontSize: "13px",
                          color:
                            abstractText.length > maxAbstractChars
                              ? "#dc3545"
                              : abstractText.length > maxAbstractChars * 0.9
                                ? "#ffc107"
                                : "#6c757d",
                          textAlign: "right",
                        }}
                      >
                        {abstractText.length} / {maxAbstractChars} characters
                        {abstractText.length > maxAbstractChars &&
                          " (exceeds limit!)"}
                      </div>
                    </div>
                  </div>
                )}

                {/* Paper File Upload - Show only if FILE or BOTH is selected */}
                {(submissionMethod === "FILE" ||
                  submissionMethod === "BOTH") && (
                  <div className="grid-item grid-item--full">
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "500",
                        color: "#000",
                      }}
                    >
                      Upload Paper File * (
                      {abstractConfig?.acceptedDocTypes === "PDF_ONLY"
                        ? "PDF only"
                        : abstractConfig?.acceptedDocTypes === "DOC_ONLY"
                          ? "DOC/DOCX only"
                          : "PDF, DOC, DOCX"}{" "}
                      - Max {maxFileSizeMb}MB)
                    </label>
                    {paperFileUploaded && !paperFile && (
                      <div
                        style={{
                          marginBottom: "12px",
                          padding: "12px",
                          backgroundColor: "#d1ecf1",
                          border: "1px solid #bee5eb",
                          borderRadius: "6px",
                          color: "#0c5460",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontSize: "14px",
                              fontWeight: "500",
                              marginBottom: "4px",
                            }}
                          >
                            📎 Previously Uploaded File
                          </div>
                          <div style={{ fontSize: "13px" }}>
                            {paperFileName || "Paper file uploaded"}
                          </div>
                          {isPaperLocked && (
                            <div
                              style={{
                                fontSize: "12px",
                                marginTop: "6px",
                                fontStyle: "italic",
                              }}
                            >
                              File upload is locked while paper is under review
                            </div>
                          )}
                        </div>
                        {!isPaperLocked && (
                          <button
                            type="button"
                            onClick={() => {
                              if (
                                window.confirm(
                                  "Are you sure you want to delete this file? This action cannot be undone.",
                                )
                              ) {
                                setPaperFileUploaded(false);
                                setPaperFileName("");
                                setPaperFile(null);
                                // Clear the file input
                                const fileInput = document.querySelector(
                                  'input[type="file"][accept*=".pdf"]',
                                );
                                if (fileInput) fileInput.value = "";
                              }
                            }}
                            style={{
                              padding: "6px 12px",
                              backgroundColor: "#dc3545",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: "13px",
                              fontWeight: "500",
                              marginLeft: "10px",
                            }}
                          >
                            🗑️ Delete
                          </button>
                        )}
                      </div>
                    )}
                    <input
                      type="file"
                      accept={
                        abstractConfig?.acceptedDocTypes === "PDF_ONLY"
                          ? ".pdf"
                          : abstractConfig?.acceptedDocTypes === "DOC_ONLY"
                            ? ".doc,.docx"
                            : ".pdf,.doc,.docx"
                      }
                      disabled={isPaperLocked}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          let allowedTypes = [];
                          let errorMessage = "";

                          if (abstractConfig?.acceptedDocTypes === "PDF_ONLY") {
                            allowedTypes = ["application/pdf"];
                            errorMessage = "Please upload a PDF file only";
                          } else if (
                            abstractConfig?.acceptedDocTypes === "DOC_ONLY"
                          ) {
                            allowedTypes = [
                              "application/msword",
                              "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                            ];
                            errorMessage =
                              "Please upload a DOC or DOCX file only";
                          } else {
                            allowedTypes = [
                              "application/pdf",
                              "application/msword",
                              "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                            ];
                            errorMessage =
                              "Please upload a PDF, DOC, or DOCX file only";
                          }

                          if (!allowedTypes.includes(file.type)) {
                            setPaperFileError(errorMessage);
                            setPaperFile(null);
                            e.target.value = "";
                          } else if (file.size > maxFileSizeMb * 1024 * 1024) {
                            setPaperFileError(
                              `File size must be less than ${maxFileSizeMb}MB`,
                            );
                            setPaperFile(null);
                            e.target.value = "";
                          } else {
                            setPaperFileError("");
                            setPaperFile(file);
                          }
                        }
                      }}
                      style={{
                        width: "100%",
                        padding: "10px",
                        border: "1px solid #666",
                        borderRadius: "8px",
                        fontSize: "15px",
                        cursor: "pointer",
                      }}
                    />
                    {paperFileError && (
                      <div
                        style={{
                          marginTop: "8px",
                          padding: "8px 12px",
                          backgroundColor: "#fee",
                          border: "1px solid #fcc",
                          borderRadius: "4px",
                          color: "#c33",
                          fontSize: "13px",
                        }}
                      >
                        ⚠️ {paperFileError}
                      </div>
                    )}
                    {paperFile && !paperFileError && (
                      <div
                        style={{
                          marginTop: "10px",
                          padding: "12px",
                          backgroundColor: "#d4edda",
                          border: "1px solid #c3e6cb",
                          borderRadius: "6px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontSize: "14px",
                              color: "#155724",
                              fontWeight: "500",
                            }}
                          >
                            ✓ File uploaded successfully
                          </div>
                          <div
                            style={{
                              fontSize: "13px",
                              color: "#155724",
                              marginTop: "4px",
                            }}
                          >
                            {paperFile.name} (
                            {(paperFile.size / 1024).toFixed(2)} KB)
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setPaperFile(null);
                            const fileInput = document.querySelector(
                              'input[type="file"][accept*=".pdf"]',
                            );
                            if (fileInput) fileInput.value = "";
                          }}
                          style={{
                            padding: "6px 12px",
                            backgroundColor: "#dc3545",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "13px",
                            marginLeft: "10px",
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Co-Authors Section - Only show if coAuthors is enabled in abstract config */}
                {(() => {
                  console.log("Checking co-authors display:", {
                    abstractConfig,
                    coAuthors: abstractConfig?.coAuthors,
                    type: typeof abstractConfig?.coAuthors,
                    shouldShow: abstractConfig?.coAuthors === true,
                  });
                  return null;
                })()}
                {abstractConfig && abstractConfig.coAuthors === true && (
                  <div
                    className="grid-item grid-item--full"
                    style={{ marginTop: "20px" }}
                  >
                    <div
                      style={{
                        padding: "15px",
                        backgroundColor: "#f8f9fa",
                        border: "1px solid #dee2e6",
                        borderRadius: "8px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "15px",
                        }}
                      >
                        <label
                          style={{
                            fontWeight: "600",
                            color: "#000",
                            fontSize: "15px",
                          }}
                        >
                          Co-Authors (Optional)
                        </label>
                        <button
                          type="button"
                          onClick={() => {
                            setCoAuthors([
                              ...coAuthors,
                              {
                                name: "",
                                email: "",
                                phone: "",
                                specialization: "",
                                designation: "",
                              },
                            ]);
                            setCoAuthorErrors([
                              ...coAuthorErrors,
                              { email: "", phone: "" },
                            ]);
                          }}
                          disabled={isPaperLocked}
                          style={{
                            padding: "6px 12px",
                            backgroundColor: isPaperLocked
                              ? "#6c757d"
                              : "#28a745",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: isPaperLocked ? "not-allowed" : "pointer",
                            fontSize: "13px",
                            fontWeight: "500",
                            opacity: isPaperLocked ? 0.6 : 1,
                          }}
                        >
                          + Add Co-Author
                        </button>
                      </div>

                      {coAuthors.length === 0 && (
                        <div
                          style={{
                            textAlign: "center",
                            padding: "20px",
                            color: "#6c757d",
                            fontSize: "14px",
                          }}
                        >
                          No co-authors added yet. Click "Add Co-Author" to add
                          one.
                        </div>
                      )}

                      {coAuthors.map((coAuthor, index) => (
                        <div
                          key={index}
                          style={{
                            marginBottom: "15px",
                            padding: "15px",
                            backgroundColor: "white",
                            borderRadius: "6px",
                            border: "1px solid #dee2e6",
                            position: "relative",
                          }}
                        >
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: "10px",
                            }}
                          >
                            <input
                              type="text"
                              placeholder="Co-Author Full Name"
                              value={coAuthor.name}
                              onChange={(e) => {
                                const updated = [...coAuthors];
                                updated[index].name = e.target.value;
                                setCoAuthors(updated);
                              }}
                              disabled={isPaperLocked}
                              readOnly={isPaperLocked}
                              style={{
                                padding: "8px 12px",
                                border: "1px solid #ced4da",
                                borderRadius: "4px",
                                fontSize: "14px",
                                backgroundColor: isPaperLocked
                                  ? "#f5f5f5"
                                  : "#fff",
                                cursor: isPaperLocked ? "not-allowed" : "text",
                              }}
                            />
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              <input
                                type="email"
                                placeholder="Co-Author Email"
                                value={coAuthor.email}
                                onChange={(e) => {
                                  const updated = [...coAuthors];
                                  updated[index].email = e.target.value;
                                  setCoAuthors(updated);

                                  // Validate email
                                  const errors = [...coAuthorErrors];
                                  if (!errors[index])
                                    errors[index] = { email: "", phone: "" };
                                  errors[index].email = validateCoAuthorEmail(
                                    e.target.value,
                                  );
                                  setCoAuthorErrors(errors);
                                }}
                                disabled={isPaperLocked}
                                readOnly={isPaperLocked}
                                style={{
                                  padding: "8px 12px",
                                  border: coAuthorErrors[index]?.email
                                    ? "1px solid #dc3545"
                                    : "1px solid #ced4da",
                                  borderRadius: "4px",
                                  fontSize: "14px",
                                  backgroundColor: isPaperLocked
                                    ? "#f5f5f5"
                                    : "#fff",
                                  cursor: isPaperLocked
                                    ? "not-allowed"
                                    : "text",
                                }}
                              />
                              {coAuthorErrors[index]?.email && (
                                <span
                                  style={{
                                    color: "#dc3545",
                                    fontSize: "12px",
                                    marginTop: "4px",
                                  }}
                                >
                                  {coAuthorErrors[index].email}
                                </span>
                              )}
                            </div>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              <input
                                type="tel"
                                placeholder="Phone Number"
                                value={coAuthor.phone || ""}
                                onChange={(e) => {
                                  const updated = [...coAuthors];
                                  updated[index].phone = e.target.value;
                                  setCoAuthors(updated);

                                  // Validate phone
                                  const errors = [...coAuthorErrors];
                                  if (!errors[index])
                                    errors[index] = { email: "", phone: "" };
                                  errors[index].phone = validateCoAuthorPhone(
                                    e.target.value,
                                  );
                                  setCoAuthorErrors(errors);
                                }}
                                disabled={isPaperLocked}
                                readOnly={isPaperLocked}
                                style={{
                                  padding: "8px 12px",
                                  border: coAuthorErrors[index]?.phone
                                    ? "1px solid #dc3545"
                                    : "1px solid #ced4da",
                                  borderRadius: "4px",
                                  fontSize: "14px",
                                  backgroundColor: isPaperLocked
                                    ? "#f5f5f5"
                                    : "#fff",
                                  cursor: isPaperLocked
                                    ? "not-allowed"
                                    : "text",
                                }}
                              />
                              {coAuthorErrors[index]?.phone && (
                                <span
                                  style={{
                                    color: "#dc3545",
                                    fontSize: "12px",
                                    marginTop: "4px",
                                  }}
                                >
                                  {coAuthorErrors[index].phone}
                                </span>
                              )}
                            </div>
                            <input
                              type="text"
                              placeholder="Specialization"
                              value={coAuthor.specialization || ""}
                              onChange={(e) => {
                                const updated = [...coAuthors];
                                updated[index].specialization = e.target.value;
                                setCoAuthors(updated);
                              }}
                              disabled={isPaperLocked}
                              readOnly={isPaperLocked}
                              style={{
                                padding: "8px 12px",
                                border: "1px solid #ced4da",
                                borderRadius: "4px",
                                fontSize: "14px",
                                backgroundColor: isPaperLocked
                                  ? "#f5f5f5"
                                  : "#fff",
                                cursor: isPaperLocked ? "not-allowed" : "text",
                              }}
                            />
                            <input
                              type="text"
                              placeholder="Designation"
                              value={coAuthor.designation || ""}
                              onChange={(e) => {
                                const updated = [...coAuthors];
                                updated[index].designation = e.target.value;
                                setCoAuthors(updated);
                              }}
                              disabled={isPaperLocked}
                              readOnly={isPaperLocked}
                              style={{
                                padding: "8px 12px",
                                border: "1px solid #ced4da",
                                borderRadius: "4px",
                                fontSize: "14px",
                                backgroundColor: isPaperLocked
                                  ? "#f5f5f5"
                                  : "#fff",
                                cursor: isPaperLocked ? "not-allowed" : "text",
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const updated = coAuthors.filter(
                                  (_, i) => i !== index,
                                );
                                const updatedErrors = coAuthorErrors.filter(
                                  (_, i) => i !== index,
                                );
                                setCoAuthors(updated);
                                setCoAuthorErrors(updatedErrors);
                              }}
                              disabled={isPaperLocked}
                              style={{
                                padding: "8px 12px",
                                backgroundColor: isPaperLocked
                                  ? "#6c757d"
                                  : "#dc3545",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                cursor: isPaperLocked
                                  ? "not-allowed"
                                  : "pointer",
                                fontSize: "13px",
                                opacity: isPaperLocked ? 0.6 : 1,
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        );

      case 4:
        // Always show affiliation form (summary is shown at step 3.x after submission)
        return (
          <>
            <div className="grid-item">
              <input
                name="affiliation"
                placeholder="Affiliation *"
                value={formData.affiliation}
                onChange={handleChange}
                required
              />
            </div>
            <div className="grid-item">
              <input
                name="institution"
                placeholder="Institution or Hospital Name *"
                value={formData.institution}
                onChange={handleChange}
                required
              />
            </div>
            <div className="grid-item">
              <input
                name="specialization"
                placeholder="Specialization"
                value={formData.specialization || ""}
                onChange={handleChange}
              />
            </div>
            <div className="grid-item">
              <input
                name="designation"
                placeholder="Designation"
                value={formData.designation || ""}
                onChange={handleChange}
              />
            </div>
            <div className="grid-item grid-item--radio">
              <label className="radio-label">
                Do you have NMC Registration Number? (National Medical
                Commission)
              </label>
              <div className="radio-row">
                <label>
                  <input
                    type="radio"
                    name="mciRegistered"
                    value="Yes"
                    checked={formData.mciRegistered === "Yes"}
                    onChange={handleChange}
                  />{" "}
                  Yes
                </label>
                <label>
                  <input
                    type="radio"
                    name="mciRegistered"
                    value="No"
                    checked={formData.mciRegistered === "No"}
                    onChange={handleChange}
                  />{" "}
                  No
                </label>
              </div>
              {formData.mciRegistered === "Yes" && (
                <input
                  name="mciNumber"
                  placeholder="Enter NMC Number *"
                  value={formData.mciNumber}
                  onChange={handleChange}
                  required
                />
              )}
            </div>
          </>
        );
      case 5:
      case 6:
        return (
          <>
            {/* STEP 5 - Input Fields */}
            {step === 5 && (
              <>
                {/* Mandatory Fields Notice - Only show if fields exist */}
                {((categories.length > 0 && !formData.category) ||
                  (registrationTypes.length > 0 &&
                    !formData.registrationType)) && (
                  <div
                    className="grid-item grid-item--full"
                    style={{ marginBottom: "15px" }}
                  >
                    <div
                      style={{
                        padding: "12px 16px",
                        backgroundColor: "#fff3cd",
                        border: "1px solid #ffc107",
                        borderRadius: "6px",
                        fontSize: "14px",
                        color: "#856404",
                      }}
                    >
                      <strong>⚠️ Required Information:</strong> Please select{" "}
                      {categories.length > 0 && registrationTypes.length > 0 ? (
                        <>
                          both <strong>Category</strong> and{" "}
                          <strong>Registration Type</strong>
                        </>
                      ) : categories.length > 0 ? (
                        <strong>Category</strong>
                      ) : (
                        <strong>Registration Type</strong>
                      )}{" "}
                      to proceed.{" "}
                      {categories.length > 0 && registrationTypes.length > 0
                        ? "These fields are"
                        : "This field is"}{" "}
                      mandatory.
                    </div>
                  </div>
                )}

                {departments.length > 0 && (
                  <>
                    <div className="grid-item">
                      <label
                        style={{
                          display: "block",
                          marginBottom: "5px",
                          color: "#000",
                          fontWeight: "500",
                          fontSize: "14px",
                        }}
                      >
                        Department
                      </label>
                      <select
                        name="department"
                        value={formData.departmentId || ""}
                        onChange={(e) => {
                          const selectedId = e.target.value;
                          const selectedDept = departments.find(
                            (d) => d.id === parseInt(selectedId),
                          );
                          setFormData((prev) => ({
                            ...prev,
                            departmentId: selectedId
                              ? parseInt(selectedId)
                              : null,
                            department: selectedDept ? selectedDept.value : "",
                          }));
                        }}
                      >
                        <option value="">Department</option>
                        {departments.map((dept) => (
                          <option key={dept.id} value={dept.id}>
                            {dept.value}
                          </option>
                        ))}
                      </select>
                    </div>
                    {formData.department === "Others" && (
                      <div className="grid-item">
                        <input
                          name="otherDepartment"
                          placeholder="Please specify department"
                          value={formData.otherDepartment}
                          onChange={handleChange}
                        />
                      </div>
                    )}
                  </>
                )}
                {categories.length > 0 && (
                  <>
                    {/* Show category history if user has already paid and there are previous selections */}
                    {formData.paid &&
                      previousCategoriesHistory.length > 0 &&
                      renderHistoryPills(
                        previousCategoriesHistory,
                        "Categories",
                      )}

                    <div className="grid-item grid-item--full">
                      <label
                        style={{
                          display: "block",
                          marginBottom: "5px",
                          color: "#000",
                          fontWeight: "500",
                          fontSize: "14px",
                        }}
                      >
                        Category <span style={{ color: "#dc3545" }}>*</span>
                        {formData.paid &&
                          previousCategoriesHistory.length > 0 && (
                            <span
                              style={{
                                fontWeight: "400",
                                color: "#666",
                                fontSize: "13px",
                                marginLeft: "8px",
                              }}
                            >
                              (Add another)
                            </span>
                          )}
                      </label>
                      <select
                        name="category"
                        value={formData.categoryId || ""}
                        onChange={(e) => {
                          const selectedId = e.target.value;
                          const selectedCat = categories.find(
                            (c) => c.id === parseInt(selectedId),
                          );

                          setFormData((prev) => {
                            // Save current values before switching
                            if (
                              prev.category === "ISCCM(Members)" &&
                              prev.membershipNumber
                            ) {
                              setSavedMembershipNumber(prev.membershipNumber);
                            }
                            if (
                              (prev.category === "Student" ||
                                prev.category === "student") &&
                              prev.studentIdCard
                            ) {
                              setSavedStudentIdCard(prev.studentIdCard);
                              setSavedStudentIdCardName(prev.studentIdCardName);
                              setSavedStudentIdCardType(prev.studentIdCardType);
                            }

                            // Determine values for new category
                            let membershipNumber = "";
                            let studentIdCard = null;
                            let studentIdCardName = "";
                            let studentIdCardType = "";

                            if (
                              selectedCat &&
                              selectedCat.value === "ISCCM(Members)"
                            ) {
                              // Restore saved membership number if available
                              membershipNumber =
                                savedMembershipNumber ||
                                prev.membershipNumber ||
                                "";
                            } else if (
                              selectedCat &&
                              (selectedCat.value === "Student" ||
                                selectedCat.value === "student")
                            ) {
                              // Restore saved student ID card if available
                              studentIdCard =
                                savedStudentIdCard ||
                                prev.studentIdCard ||
                                null;
                              studentIdCardName =
                                savedStudentIdCardName ||
                                prev.studentIdCardName ||
                                "";
                              studentIdCardType =
                                savedStudentIdCardType ||
                                prev.studentIdCardType ||
                                "";
                            }

                            return {
                              ...prev,
                              categoryId: selectedId
                                ? parseInt(selectedId)
                                : null,
                              category: selectedCat ? selectedCat.value : "",
                              membershipNumber,
                              studentIdCard,
                              studentIdCardName,
                              studentIdCardType,
                            };
                          });

                          // Clear file input and errors when not Student category or registration type
                          const isStudentSelected =
                            selectedCat &&
                            selectedCat.value.toLowerCase().includes("student");
                          const isStudentRegType =
                            formData.registrationType &&
                            formData.registrationType
                              .toLowerCase()
                              .includes("student");
                          if (!isStudentSelected && !isStudentRegType) {
                            setStudentIdCardFile(null);
                            setStudentIdCardError("");
                          }
                        }}
                        required
                        style={{
                          borderColor: !formData.category ? "#dc3545" : "",
                        }}
                      >
                        <option value="" disabled hidden>
                          Select Category
                        </option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.value}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
                {registrationTypes.length > 0 && (
                  <>
                    {/* Show registration type history if user has already paid and there are previous selections */}
                    {formData.paid &&
                      previousRegistrationTypesHistory.length > 0 &&
                      renderHistoryPills(
                        previousRegistrationTypesHistory,
                        "Registration Types",
                      )}

                    <div className="grid-item">
                      <label
                        style={{
                          display: "block",
                          marginBottom: "5px",
                          color: "#000",
                          fontWeight: "500",
                          fontSize: "14px",
                        }}
                      >
                        Registration Type{" "}
                        <span style={{ color: "#dc3545" }}>*</span>
                        {formData.paid &&
                          previousRegistrationTypesHistory.length > 0 && (
                            <span
                              style={{
                                fontWeight: "400",
                                color: "#666",
                                fontSize: "13px",
                                marginLeft: "8px",
                              }}
                            >
                              (Add another)
                            </span>
                          )}
                      </label>
                      <select
                        name="registrationType"
                        value={formData.registrationTypeId || ""}
                        onChange={(e) => {
                          const selectedId = e.target.value;
                          const selectedType = registrationTypes.find(
                            (rt) => rt.id === parseInt(selectedId),
                          );
                          setFormData((prev) => ({
                            ...prev,
                            registrationTypeId: selectedId
                              ? parseInt(selectedId)
                              : null,
                            registrationType: selectedType
                              ? selectedType.value
                              : "",
                          }));

                          // Clear file input and errors when not Student registration type or category
                          const isStudentRegType =
                            selectedType &&
                            selectedType.value
                              .toLowerCase()
                              .includes("student");
                          const isStudentCategory =
                            formData.category &&
                            formData.category.toLowerCase().includes("student");
                          if (!isStudentRegType && !isStudentCategory) {
                            setStudentIdCardFile(null);
                            setStudentIdCardError("");
                          }
                        }}
                        required
                        style={{
                          borderColor: !formData.registrationType
                            ? "#dc3545"
                            : "",
                        }}
                      >
                        <option value="" disabled hidden>
                          Select Registration Type
                        </option>
                        {registrationTypes.map((rt) => (
                          <option key={rt.id} value={rt.id}>
                            {rt.value}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                {/* Membership Number - show before Accompanying Persons */}
                {formData.category === "ISCCM(Members)" && (
                  <div className="grid-item">
                    <input
                      name="membershipNumber"
                      placeholder="Enter Membership Number *"
                      value={formData.membershipNumber}
                      onChange={handleChange}
                      required
                      style={{
                        borderColor: !formData.membershipNumber
                          ? "#dc3545"
                          : "",
                      }}
                    />
                  </div>
                )}

                {/* ISA Membership Number - Only for ISA MEMBER, not NON-ISA MEMBER */}
                {formData.category &&
                  formData.category.toString().toLowerCase().includes("isa") &&
                  !formData.category.toString().toLowerCase().includes("non") &&
                  !formData.category
                    .toString()
                    .toLowerCase()
                    .includes("isccm") && (
                    <div className="grid-item grid-item--full">
                      <input
                        name="isaMembershipNumber"
                        placeholder="Enter ISA Membership Number *"
                        value={formData.isaMembershipNumber || ""}
                        onChange={handleChange}
                        required
                        style={{
                          borderColor: !formData.isaMembershipNumber
                            ? "#dc3545"
                            : "",
                        }}
                      />
                    </div>
                  )}

                {/* Student ID Card Upload */}
                {((formData.category &&
                  formData.category.toLowerCase().includes("student")) ||
                  (formData.registrationType &&
                    formData.registrationType
                      .toLowerCase()
                      .includes("student"))) && (
                  <div className="grid-item" style={{ gridColumn: "1 / -1" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "500",
                      }}
                    >
                      Student ID Card * (Single file: JPEG or PDF, max 5MB)
                    </label>
                    <input
                      type="file"
                      name="studentIdCard"
                      accept=".jpg,.jpeg,.pdf,image/jpeg,application/pdf"
                      onChange={handleStudentIdCardUpload}
                      multiple={false}
                      style={{
                        width: "100%",
                        padding: "10px",
                        border: studentIdCardError
                          ? "1px solid #dc3545"
                          : "1px solid #ddd",
                        borderRadius: "4px",
                        fontSize: "14px",
                      }}
                    />
                    {studentIdCardError && (
                      <div
                        style={{
                          color: "#dc3545",
                          fontSize: "12px",
                          marginTop: "5px",
                        }}
                      >
                        {studentIdCardError}
                      </div>
                    )}
                    {/* Show success message for newly uploaded file */}
                    {formData.studentIdCardName && !studentIdCardError && (
                      <div
                        style={{
                          color: "#28a745",
                          fontSize: "12px",
                          marginTop: "5px",
                        }}
                      >
                        ✓ {formData.studentIdCardName}
                      </div>
                    )}
                    {/* Show success message for previously uploaded file from database */}
                    {!formData.studentIdCardName &&
                      formData.studentIdCard === "DATABASE" &&
                      !studentIdCardError && (
                        <div
                          style={{
                            color: "#28a745",
                            fontSize: "12px",
                            marginTop: "5px",
                          }}
                        >
                          ✓ Student ID card previously uploaded (on file)
                        </div>
                      )}
                    {/* Show info message when studentIdCard has a URL from database */}
                    {!formData.studentIdCardName &&
                      formData.studentIdCard &&
                      formData.studentIdCard !== "DATABASE" &&
                      !formData.studentIdCard.startsWith("data:") &&
                      !studentIdCardError && (
                        <div
                          style={{
                            color: "#28a745",
                            fontSize: "12px",
                            marginTop: "5px",
                          }}
                        >
                          ✓ Student ID card on file
                        </div>
                      )}
                  </div>
                )}

                {/* Accompanying Persons Section - Only show if fee is configured */}
                {accompanyingPersonFee && (
                  <div className="grid-item grid-item--full">
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#000000",
                      }}
                    >
                      Accompanying Persons{" "}
                      <span style={{ fontWeight: "400" }}>(Optional)</span>
                      <span
                        style={{
                          fontWeight: "400",
                          color: "#000000",
                          fontSize: "13px",
                        }}
                      >
                        {" "}
                        (Fee: {accompanyingPersonFee.currency}{" "}
                        {accompanyingPersonFee.rate} per person)
                      </span>
                    </label>

                    {accompanyingPersons.map((person, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          gap: "8px",
                          marginBottom: "8px",
                          padding: person.paid ? "8px" : "0",
                          backgroundColor: person.paid
                            ? "#f0f9ff"
                            : "transparent",
                          border: person.paid ? "2px solid #28a745" : "none",
                          borderRadius: person.paid ? "6px" : "0",
                          position: "relative",
                        }}
                      >
                        {person.paid && (
                          <div
                            style={{
                              position: "absolute",
                              top: "-1px",
                              right: "-1px",
                              backgroundColor: "#28a745",
                              color: "white",
                              padding: "2px 8px",
                              fontSize: "11px",
                              fontWeight: "bold",
                              borderTopRightRadius: "4px",
                              borderBottomLeftRadius: "4px",
                            }}
                          >
                            🔒 PAID
                          </div>
                        )}
                        <input
                          type="text"
                          placeholder={`Accompanying Person ${index + 1} Name *`}
                          value={person.name}
                          onChange={(e) =>
                            handleAccompanyingPersonNameChange(
                              index,
                              e.target.value,
                            )
                          }
                          required
                          style={{
                            flex: 1,
                            backgroundColor: person.paid ? "#e8f5e9" : "white",
                            cursor: person.paid ? "not-allowed" : "text",
                            color: person.paid ? "#2e7d32" : "#000000",
                            fontWeight: person.paid ? "500" : "normal",
                            fontSize: "14px",
                            padding: "8px 12px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                          }}
                          disabled={person.paid}
                          title={
                            person.paid
                              ? "⚠️ Payment completed - Cannot modify"
                              : ""
                          }
                          readOnly={person.paid}
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveAccompanyingPerson(index)}
                          disabled={person.paid}
                          style={{
                            padding: "8px 12px",
                            backgroundColor: person.paid
                              ? "#e0e0e0"
                              : "#ef4444",
                            color: person.paid ? "#9e9e9e" : "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: person.paid ? "not-allowed" : "pointer",
                            fontSize: "13px",
                            opacity: person.paid ? 0.5 : 1,
                            textDecoration: person.paid
                              ? "line-through"
                              : "none",
                          }}
                          title={
                            person.paid
                              ? "⚠️ Cannot remove - Payment completed and cannot be reversed"
                              : "Remove"
                          }
                        >
                          Remove
                        </button>
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={handleAddAccompanyingPerson}
                      style={{
                        padding: "8px 16px",
                        backgroundColor: "#1976d2",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "13px",
                        marginTop: "4px",
                      }}
                    >
                      + Add Accompanying Person
                    </button>

                    {accompanyingPersons.some((p) => p.paid) && (
                      <div
                        style={{
                          marginTop: "8px",
                          padding: "10px",
                          backgroundColor: "#fff3cd",
                          border: "1px solid #ffc107",
                          borderRadius: "4px",
                          fontSize: "13px",
                          color: "#856404",
                        }}
                      >
                        <strong>⚠️ Important:</strong> Items marked as "PAID"
                        are locked because payment has been completed and cannot
                        be reversed. You can add new items which will require
                        additional payment.
                      </div>
                    )}

                    {formData.paid && accompanyingPersons.length === 0 && (
                      <p
                        style={{
                          fontSize: "13px",
                          color: "#059669",
                          fontStyle: "italic",
                          marginTop: "8px",
                        }}
                      >
                        ✓ Payment completed. You can still add more accompanying
                        persons if needed (additional payment required).
                      </p>
                    )}
                  </div>
                )}

                {/* Focused Sessions Section - Only show if there are focused sessions available */}
                {focusedSessions.length > 0 && (
                  <div className="grid-item grid-item--full">
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "10px",
                          fontWeight: "500",
                          color: "#000",
                        }}
                      >
                        Focussed Section
                      </label>
                      <div
                        className="grid-item grid-item--radio"
                        style={{ marginBottom: "10px" }}
                      >
                        <div
                          className="radio-row"
                          style={{ display: "flex", gap: "20px" }}
                        >
                          <label
                            style={{
                              display: "flex",
                              alignItems: "center",
                              cursor: "pointer",
                            }}
                          >
                            <input
                              type="radio"
                              name="focusedSessions"
                              value="Yes"
                              checked={registerForFocusedSessions === "Yes"}
                              onChange={(e) =>
                                handleFocusedSessionsChange(e.target.value)
                              }
                              style={{ marginRight: "8px" }}
                            />
                            <span
                              style={{ fontSize: "14px", color: "#000000" }}
                            >
                              Yes
                            </span>
                          </label>
                          <label
                            style={{
                              display: "flex",
                              alignItems: "center",
                              cursor: "pointer",
                            }}
                          >
                            <input
                              type="radio"
                              name="focusedSessions"
                              value="No"
                              checked={registerForFocusedSessions === "No"}
                              onChange={(e) =>
                                handleFocusedSessionsChange(e.target.value)
                              }
                              style={{ marginRight: "8px" }}
                            />
                            <span
                              style={{ fontSize: "14px", color: "#000000" }}
                            >
                              No
                            </span>
                          </label>
                        </div>
                      </div>
                      {registerForFocusedSessions === "Yes" && (
                        <div>
                          <div style={{ marginTop: "10px" }}>
                            {focusedSessions.length > 0 ? (
                              <div>
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "15px",
                                  }}
                                >
                                  {(() => {
                                    // Group sessions by sessionDate (from MasterDefinitionDetailsEntity)
                                    const sessionsByDate =
                                      focusedSessions.reduce(
                                        (groups, session) => {
                                          // Use sessionDate field, fallback to dateGroup, then 'Other Sessions'
                                          const dateGroup =
                                            session.sessionDate ||
                                            session.dateGroup ||
                                            "Other Sessions";
                                          if (!groups[dateGroup]) {
                                            groups[dateGroup] = [];
                                          }
                                          groups[dateGroup].push(session);
                                          return groups;
                                        },
                                        {},
                                      );
                                    // Sort date groups in ascending order
                                    // Put 'Other Sessions' at the end
                                    const sortedDateGroups = Object.keys(
                                      sessionsByDate,
                                    ).sort((a, b) => {
                                      if (a === "Other Sessions") return 1;
                                      if (b === "Other Sessions") return -1;
                                      return a.localeCompare(b);
                                    });
                                    return sortedDateGroups.map((dateGroup) => (
                                      <div
                                        key={dateGroup}
                                        style={{ marginBottom: "10px" }}
                                      >
                                        <div
                                          style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "8px",
                                            paddingLeft: "10px",
                                          }}
                                        >
                                          {sessionsByDate[dateGroup].map(
                                            (session) => {
                                              // Check if this session is already paid for
                                              const isPaid =
                                                formData.additionalFees &&
                                                formData.additionalFees.some(
                                                  (fee) =>
                                                    fee.feeTypeId ===
                                                      session.id &&
                                                    fee.paymentId,
                                                );
                                              return (
                                                <label
                                                  key={session.id}
                                                  style={{
                                                    display: "flex",
                                                    alignItems: "flex-start",
                                                    cursor: isPaid
                                                      ? "not-allowed"
                                                      : "pointer",
                                                    padding: isPaid
                                                      ? "8px"
                                                      : "4px",
                                                    backgroundColor: isPaid
                                                      ? "#f0f9ff"
                                                      : "transparent",
                                                    border: isPaid
                                                      ? "2px solid #28a745"
                                                      : "none",
                                                    borderRadius: isPaid
                                                      ? "6px"
                                                      : "0",
                                                    position: "relative",
                                                  }}
                                                  title={
                                                    isPaid
                                                      ? "⚠️ Payment completed - Cannot be unchecked or reversed"
                                                      : "Select this session"
                                                  }
                                                >
                                                  {isPaid && (
                                                    <div
                                                      style={{
                                                        position: "absolute",
                                                        top: "-1px",
                                                        right: "-1px",
                                                        backgroundColor:
                                                          "#28a745",
                                                        color: "white",
                                                        padding: "2px 8px",
                                                        fontSize: "11px",
                                                        fontWeight: "bold",
                                                        borderTopRightRadius:
                                                          "4px",
                                                        borderBottomLeftRadius:
                                                          "4px",
                                                      }}
                                                    >
                                                      🔒 PAID
                                                    </div>
                                                  )}
                                                  <input
                                                    type="checkbox"
                                                    checked={selectedFocusedSessions.includes(
                                                      session.id,
                                                    )}
                                                    onChange={() =>
                                                      handleFocusedSessionCheckboxChange(
                                                        session.id,
                                                      )
                                                    }
                                                    disabled={isPaid}
                                                    style={{
                                                      marginRight: "8px",
                                                      marginTop: "2px",
                                                      cursor: isPaid
                                                        ? "not-allowed"
                                                        : "pointer",
                                                    }}
                                                  />
                                                  <span
                                                    style={{
                                                      fontSize: "14px",
                                                      color: isPaid
                                                        ? "#2e7d32"
                                                        : "#000000",
                                                      fontWeight: isPaid
                                                        ? "500"
                                                        : "normal",
                                                    }}
                                                  >
                                                    {session.value}
                                                  </span>
                                                </label>
                                              );
                                            },
                                          )}
                                        </div>
                                      </div>
                                    ));
                                  })()}
                                </div>
                                {formData.additionalFees &&
                                  formData.additionalFees.some(
                                    (fee) =>
                                      fee.feeTypeId !==
                                        accompanyingPersonFee?.id &&
                                      fee.paymentId,
                                  ) && (
                                    <div
                                      style={{
                                        marginTop: "12px",
                                        padding: "10px",
                                        backgroundColor: "#fff3cd",
                                        border: "1px solid #ffc107",
                                        borderRadius: "4px",
                                        fontSize: "13px",
                                        color: "#856404",
                                      }}
                                    >
                                      <strong>⚠️ Important:</strong> Sessions
                                      marked as "PAID" are locked because
                                      payment has been completed and cannot be
                                      reversed. You can select new sessions
                                      which will require additional payment.
                                    </div>
                                  )}
                              </div>
                            ) : (
                              <p
                                style={{
                                  fontSize: "13px",
                                  color: "#666",
                                  fontStyle: "italic",
                                }}
                              >
                                No focused sessions available.
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* STEP 6 - Summary/Review */}
            {step === 6 && (
              <div className="summary-container">
                {/* Personal Information Section */}
                <div className="summary-section">
                  <h4>Personal Information</h4>
                  {formData.title && (
                    <div className="summary-row">
                      <span className="summary-label">Title:</span>
                      <span className="summary-value">{formData.title}</span>
                    </div>
                  )}
                  <div className="summary-row">
                    <span className="summary-label">Name:</span>
                    <span className="summary-value">
                      {formData.firstName} {formData.lastName}
                    </span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Email:</span>
                    <span className="summary-value">{formData.email}</span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Mobile:</span>
                    <span className="summary-value">{formData.mobile}</span>
                  </div>
                </div>

                {/* Address Information Section */}
                <div className="summary-section">
                  <h4>Address Information</h4>
                  <div className="summary-row">
                    <span className="summary-label">Country:</span>
                    <span className="summary-value">{formData.country}</span>
                  </div>
                  {formData.state && (
                    <div className="summary-row">
                      <span className="summary-label">State:</span>
                      <span className="summary-value">{formData.state}</span>
                    </div>
                  )}
                  {formData.city && (
                    <div className="summary-row">
                      <span className="summary-label">City:</span>
                      <span className="summary-value">{formData.city}</span>
                    </div>
                  )}
                  {formData.pincode && (
                    <div className="summary-row">
                      <span className="summary-label">Pincode:</span>
                      <span className="summary-value">{formData.pincode}</span>
                    </div>
                  )}
                  {formData.address && (
                    <div className="summary-row">
                      <span className="summary-label">Address:</span>
                      <span className="summary-value">{formData.address}</span>
                    </div>
                  )}
                  {formData.dietaryRestrictions && (
                    <div className="summary-row">
                      <span className="summary-label">
                        Dietary Restrictions:
                      </span>
                      <span className="summary-value">
                        {formData.dietaryRestrictions}
                        <span
                          style={{
                            marginLeft: "8px",
                            padding: "2px 8px",
                            backgroundColor: "#d4edda",
                            color: "#155724",
                            fontSize: "11px",
                            borderRadius: "3px",
                            fontWeight: "600",
                          }}
                        >
                          JSON
                        </span>
                      </span>
                    </div>
                  )}
                </div>

                <div className="summary-section">
                  <h4>Professional Information</h4>
                  {formData.affiliation && (
                    <div className="summary-row">
                      <span className="summary-label">Affiliation:</span>
                      <span className="summary-value">
                        {formData.affiliation}
                      </span>
                    </div>
                  )}
                  {formData.institution && (
                    <div className="summary-row">
                      <span className="summary-label">Institution:</span>
                      <span className="summary-value">
                        {formData.institution}
                      </span>
                    </div>
                  )}
                  {formData.specialization && (
                    <div className="summary-row">
                      <span className="summary-label">Specialization:</span>
                      <span className="summary-value">
                        {formData.specialization}
                      </span>
                    </div>
                  )}
                  {formData.designation && (
                    <div className="summary-row">
                      <span className="summary-label">Designation:</span>
                      <span className="summary-value">
                        {formData.designation}
                      </span>
                    </div>
                  )}
                  <div className="summary-row">
                    <span className="summary-label">MCI Registered:</span>
                    <span className="summary-value">
                      {formData.mciRegistered}
                    </span>
                  </div>
                  {formData.mciRegistered === "Yes" && formData.mciNumber && (
                    <div className="summary-row">
                      <span className="summary-label">MCI Number:</span>
                      <span className="summary-value">
                        {formData.mciNumber}
                      </span>
                    </div>
                  )}
                </div>

                <div className="summary-section">
                  <h4>Registration Details</h4>

                  {formData.department && (
                    <div className="summary-row">
                      <span className="summary-label">Department:</span>
                      <span className="summary-value">
                        {formData.department === "Others"
                          ? formData.otherDepartment
                          : formData.department}
                      </span>
                    </div>
                  )}
                  {formData.category && (
                    <div className="summary-row">
                      <span className="summary-label">Category:</span>
                      <span className="summary-value">{formData.category}</span>
                    </div>
                  )}
                  {formData.paid === true &&
                    effectiveCategoriesHistory.length > 1 && (
                      <div className="summary-row">
                        <span className="summary-label">All Categories:</span>
                        <span
                          className="summary-value"
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "6px",
                            justifyContent: "flex-end",
                          }}
                        >
                          {effectiveCategoriesHistory
                            .map((entry) => entry?.value)
                            .filter(Boolean)
                            .map((value, idx) => (
                              <span
                                key={`${value}-${idx}`}
                                style={{
                                  padding: "3px 10px",
                                  borderRadius: "999px",
                                  backgroundColor: "#e8f1ff",
                                  color: "#0b63c7",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {value}
                              </span>
                            ))}
                        </span>
                      </div>
                    )}
                  {formData.category === "ISCCM(Members)" &&
                    formData.membershipNumber && (
                      <div className="summary-row">
                        <span className="summary-label">
                          Membership Number:
                        </span>
                        <span className="summary-value">
                          {formData.membershipNumber}
                        </span>
                      </div>
                    )}
                  {formData.category &&
                    formData.category
                      .toString()
                      .toLowerCase()
                      .includes("isa") &&
                    !formData.category
                      .toString()
                      .toLowerCase()
                      .includes("isccm") &&
                    formData.isaMembershipNumber && (
                      <div className="summary-row">
                        <span className="summary-label">
                          ISA Membership Number:
                        </span>
                        <span className="summary-value">
                          {formData.isaMembershipNumber}
                        </span>
                      </div>
                    )}
                  {((formData.category &&
                    formData.category.toLowerCase().includes("student")) ||
                    (formData.registrationType &&
                      formData.registrationType
                        .toLowerCase()
                        .includes("student"))) &&
                    (formData.studentIdCard ||
                      formData.studentIdCardUploaded) && (
                      <div className="summary-row">
                        <span className="summary-label">Student ID Card:</span>
                        <span className="summary-value">
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              let fileUrl;

                              // Priority 1: If we have a registration ID and file was uploaded or marked as DATABASE
                              if (
                                formData.regId &&
                                (formData.studentIdCardUploaded ||
                                  formData.studentIdCard === "DATABASE" ||
                                  !formData.studentIdCard.startsWith("data:"))
                              ) {
                                fileUrl = `${REGISTRATION_BASE_URL}/registration/${formData.regId}/file/id-card`;
                                console.log(
                                  "📥 Opening from database:",
                                  fileUrl,
                                );
                              }
                              // Priority 2: If student ID card is base64 data (preview before save)
                              else if (
                                formData.studentIdCard &&
                                formData.studentIdCard.startsWith("data:")
                              ) {
                                fileUrl = formData.studentIdCard;
                                console.log("📥 Opening base64 preview");
                              }
                              // Priority 3: Legacy - If it's an old URL path
                              else if (
                                formData.studentIdCard &&
                                formData.studentIdCard.startsWith("http")
                              ) {
                                fileUrl = formData.studentIdCard;
                                console.log("📥 Opening legacy URL:", fileUrl);
                              }
                              // Priority 4: Legacy - If it's a path (old file system storage)
                              else if (formData.studentIdCard) {
                                const path = formData.studentIdCard.startsWith(
                                  "/",
                                )
                                  ? formData.studentIdCard
                                  : `/${formData.studentIdCard}`;
                                fileUrl = `${REGISTRATION_BASE_URL}${path}`;
                                console.log("📥 Opening legacy path:", fileUrl);
                              }

                              if (fileUrl) {
                                window.open(
                                  fileUrl,
                                  "_blank",
                                  "noopener,noreferrer",
                                );
                              } else {
                                console.error("❌ No file URL available");
                                alert(
                                  "Unable to open document. Please try uploading again.",
                                );
                              }
                            }}
                            style={{
                              color: "#0066cc",
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                          >
                            View Uploaded Document
                          </a>
                        </span>
                      </div>
                    )}
                  {formData.registrationType &&
                    formData.registrationType.trim() !== "" &&
                    formData.registrationType !== "TBD" && (
                      <div className="summary-row">
                        <span className="summary-label">
                          Registration Type:
                        </span>
                        <span className="summary-value">
                          {formData.registrationType}
                        </span>
                      </div>
                    )}
                  {formData.paid === true &&
                    effectiveRegistrationTypesHistory.length > 1 && (
                      <div className="summary-row">
                        <span className="summary-label">
                          All Registration Types:
                        </span>
                        <span
                          className="summary-value"
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "6px",
                            justifyContent: "flex-end",
                          }}
                        >
                          {effectiveRegistrationTypesHistory
                            .map((entry) => entry?.value)
                            .filter(Boolean)
                            .map((value, idx) => (
                              <span
                                key={`${value}-${idx}`}
                                style={{
                                  padding: "3px 10px",
                                  borderRadius: "999px",
                                  backgroundColor: "#e9f8ef",
                                  color: "#137a3d",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {value}
                              </span>
                            ))}
                        </span>
                      </div>
                    )}
                  <div className="summary-row">
                    <span className="summary-label">Conference:</span>
                    <span className="summary-value">
                      {formData.conference || conferenceName || currentHost}
                    </span>
                  </div>
                </div>

                {/* Registration Fee Section */}
                {formData.categoryId && (
                  <div className="summary-section">
                    <h4>Registration Fee</h4>
                    {feeLoading ? (
                      <div className="summary-row">
                        <span className="summary-label">Fee:</span>
                        <span className="summary-value">⏳ Calculating...</span>
                      </div>
                    ) : feeError ? (
                      <div className="summary-row fee-error-row">
                        <span className="summary-label">Fee:</span>
                        <span className="summary-value">⚠️ {feeError}</span>
                      </div>
                    ) : registrationFee ? (
                      <>
                        {(() => {
                          const baseRate = parseFloat(
                            registrationFee.rate || 0,
                          );
                          const discountAmount = shouldApplyPaperDiscount
                            ? (baseRate * paperDiscountPercentage) / 100
                            : 0;
                          const payableBase = Math.max(
                            0,
                            baseRate - discountAmount,
                          );
                          return (
                            <>
                              <div className="summary-row">
                                <span className="summary-label">
                                  Base Amount:
                                </span>
                                <span className="summary-value">
                                  {formData.country === "India" ? "₹" : "₹"}
                                  {baseRate.toFixed(2)} (
                                  {registrationFee.currency})
                                </span>
                              </div>
                              {shouldApplyPaperDiscount &&
                                paperDiscountPercentage > 0 && (
                                  <>
                                    <div className="summary-row">
                                      <span className="summary-label">
                                        Paper Sequence:
                                      </span>
                                      <span className="summary-value">
                                        #{currentPaperSequence}
                                      </span>
                                    </div>
                                    <div className="summary-row">
                                      <span className="summary-label">
                                        Paper Discount:
                                      </span>
                                      <span className="summary-value">
                                        {paperDiscountPercentage.toFixed(2)}% (-
                                        {formData.country === "India"
                                          ? "₹"
                                          : "₹"}
                                        {discountAmount.toFixed(2)})
                                      </span>
                                    </div>
                                  </>
                                )}
                              <div className="summary-row">
                                <span className="summary-label">
                                  Payable Registration Fee:
                                </span>
                                <span
                                  className="summary-value"
                                  style={{ fontWeight: "bold" }}
                                >
                                  {formData.country === "India" ? "₹" : "₹"}
                                  {payableBase.toFixed(2)} (
                                  {registrationFee.currency})
                                </span>
                              </div>
                            </>
                          );
                        })()}
                        {registrationFee.feeCategory && (
                          <div className="summary-row">
                            <span className="summary-label">Fee Type:</span>
                            <span className="summary-value">
                              {registrationFee.feeCategory.value}
                            </span>
                          </div>
                        )}
                        {registrationFee.validFrom &&
                          registrationFee.validTo && (
                            <div className="summary-row">
                              <span className="summary-label">
                                Valid Period:
                              </span>
                              <span className="summary-value">
                                {new Date(
                                  registrationFee.validFrom,
                                ).toLocaleDateString()}{" "}
                                -{" "}
                                {new Date(
                                  registrationFee.validTo,
                                ).toLocaleDateString()}
                              </span>
                            </div>
                          )}
                      </>
                    ) : null}
                  </div>
                )}

                {/* Accompanying Persons Section */}
                {(() => {
                  // Use accompanyingPersons state as the source of truth
                  // This ensures that if user removes someone in step 4, it won't show in step 5

                  // Map state items and enrich with payment status from database
                  const allAccompanyingPersons = accompanyingPersons.map(
                    (person) => {
                      // Check if this person is already paid (has paymentId)
                      const isPaid = person.paid || !!person.paymentId;

                      // Try to get payment details from additionalFees if available
                      const paidFee = formData.additionalFees?.find(
                        (fee) =>
                          accompanyingPersonFee &&
                          fee.feeTypeId === accompanyingPersonFee.id &&
                          fee.name === person.name &&
                          fee.paymentId,
                      );

                      // Try to get from zeroPayments if not paid
                      const savedZero = formData.zeroPayments?.find(
                        (zp) =>
                          accompanyingPersonFee &&
                          zp.feeTypeId === accompanyingPersonFee.id &&
                          zp.name === person.name,
                      );

                      return {
                        name: person.name,
                        paid: isPaid || !!paidFee,
                        saved: !!savedZero && !isPaid && !paidFee,
                        paymentId: person.paymentId || paidFee?.paymentId,
                        source: paidFee ? "paid" : savedZero ? "saved" : "new",
                        rate: paidFee?.rate || accompanyingPersonFee?.rate || 0,
                        currency:
                          paidFee?.currency ||
                          accompanyingPersonFee?.currency ||
                          registrationFee?.currency ||
                          "INR",
                      };
                    },
                  );

                  console.log("🔍 Accompanying Persons Display (from state):");
                  console.log(
                    "  - accompanyingPersons state:",
                    accompanyingPersons,
                  );
                  console.log(
                    "  - Final list with payment status:",
                    allAccompanyingPersons,
                  );

                  if (allAccompanyingPersons.length === 0) return null;

                  // Get rate and currency from accompanyingPersonFee or from the first item
                  const displayRate =
                    accompanyingPersonFee?.rate ||
                    allAccompanyingPersons[0]?.rate ||
                    0;
                  const displayCurrency =
                    accompanyingPersonFee?.currency ||
                    allAccompanyingPersons[0]?.currency ||
                    registrationFee?.currency ||
                    "INR";

                  return (
                    <div className="summary-section">
                      <h4>Accompanying Persons</h4>
                      {allAccompanyingPersons.map((person, index) => (
                        <div className="summary-row" key={index}>
                          <span className="summary-label">
                            Person {index + 1}:
                          </span>
                          <span className="summary-value">
                            {person.name}
                            {person.paid && (
                              <span
                                style={{
                                  marginLeft: "8px",
                                  color: "#28a745",
                                  fontSize: "12px",
                                }}
                              >
                                ✓ Paid
                              </span>
                            )}
                            {person.saved && !person.paid && (
                              <span
                                style={{
                                  marginLeft: "8px",
                                  color: "#0d6efd",
                                  fontSize: "12px",
                                }}
                              >
                                ✓ Saved
                              </span>
                            )}
                          </span>
                        </div>
                      ))}
                      {accompanyingPersonFee && (
                        <>
                          <div className="summary-row">
                            <span className="summary-label">
                              Fee per Person:
                            </span>
                            <span className="summary-value">
                              {formData.country === "India" ? "₹" : "₹"}
                              {parseFloat(displayRate).toFixed(2)} (
                              {displayCurrency})
                            </span>
                          </div>
                          <div className="summary-row">
                            <span className="summary-label">
                              Subtotal ({allAccompanyingPersons.length} person
                              {allAccompanyingPersons.length > 1 ? "s" : ""}):
                            </span>
                            <span
                              className="summary-value"
                              style={{ fontWeight: "bold" }}
                            >
                              {formData.country === "India" ? "₹" : "₹"}
                              {(
                                parseFloat(displayRate) *
                                allAccompanyingPersons.length
                              ).toFixed(2)}{" "}
                              ({displayCurrency})
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })()}

                {/* Focused Sessions Section - PAID SESSIONS ONLY */}
                {(() => {
                  // Get accompanyingFeeId if available
                  const accompanyingFeeId = accompanyingPersonFee?.id;

                  // Get ONLY PAID focused sessions from additionalFees (exclude free ones and accompanying persons)
                  const paidFocusedSessions = formData.additionalFees
                    ? accompanyingFeeId
                      ? formData.additionalFees.filter(
                          (fee) => fee.feeTypeId !== accompanyingFeeId,
                        )
                      : formData.additionalFees // If no accompanyingFeeId, show all as potential sessions
                    : [];

                  console.log(
                    "📊 Display logic - Focused Sessions (Paid Only):",
                  );
                  console.log("  - accompanyingFeeId:", accompanyingFeeId);
                  console.log(
                    "  - Paid sessions from additionalFees:",
                    paidFocusedSessions,
                  );
                  console.log(
                    "  - Currently selected (state):",
                    selectedFocusedSessions,
                  );

                  // Get IDs of paid sessions from database
                  const paidSessionIds = paidFocusedSessions.map(
                    (fee) => fee.feeTypeId,
                  );

                  // Add newly selected sessions that are NOT yet in database
                  const newlySelectedSessionIds =
                    selectedFocusedSessions.filter((sessionId) => {
                      // Check if NOT in paid sessions
                      const notInPaid = !paidSessionIds.includes(sessionId);
                      // Check if NOT in zero payments (we don't want free ones here)
                      const notInFree = !formData.zeroPayments?.some(
                        (zp) => zp.feeTypeId === sessionId,
                      );
                      // Check if NOT the accompanying person fee
                      const notAccompanying =
                        !accompanyingFeeId || sessionId !== accompanyingFeeId;
                      return notInPaid && notInFree && notAccompanying;
                    });

                  // Combine: paid sessions from DB + newly selected (not yet saved, not free)
                  const allPaidSessionIds = [
                    ...paidSessionIds,
                    ...newlySelectedSessionIds,
                  ];

                  console.log("  - Paid session IDs:", paidSessionIds);
                  console.log(
                    "  - Newly selected (not saved, not free):",
                    newlySelectedSessionIds,
                  );
                  console.log("  - Final list (paid only):", allPaidSessionIds);

                  if (allPaidSessionIds.length === 0) return null;

                  return (
                    <div className="summary-section">
                      <h4>Focused Sessions</h4>
                      {allPaidSessionIds.map((sessionId, index) => {
                        // Try to get name and rate from additionalFees first (most accurate for paid items)
                        const paidSession = paidFocusedSessions.find(
                          (ps) => ps.feeTypeId === sessionId,
                        );

                        // Get session name from multiple sources - ENSURE we get a string, not an object
                        let sessionName = null;

                        // 1. Try from paidSession (additionalFees)
                        if (
                          paidSession &&
                          paidSession.name &&
                          typeof paidSession.name === "string"
                        ) {
                          sessionName = paidSession.name;
                        }

                        // 2. Try from focusedSessions array
                        if (!sessionName) {
                          const sessionObj = focusedSessions.find(
                            (s) => s.id === sessionId,
                          );
                          if (
                            sessionObj &&
                            sessionObj.value &&
                            typeof sessionObj.value === "string"
                          ) {
                            sessionName = sessionObj.value;
                          }
                        }

                        // 3. Try from zeroPayments
                        if (!sessionName && formData.zeroPayments) {
                          const zeroPayment = formData.zeroPayments.find(
                            (zp) => zp.feeTypeId === sessionId,
                          );
                          if (
                            zeroPayment &&
                            zeroPayment.name &&
                            typeof zeroPayment.name === "string"
                          ) {
                            sessionName = zeroPayment.name;
                          }
                        }

                        // 4. Fallback to session ID
                        if (!sessionName) {
                          sessionName = `Session ${sessionId}`;
                        }

                        // For display: use rate from paidSession (from DB) if available, otherwise from state
                        const sessionFee = focusedSessionFees[sessionId];
                        // CRITICAL: Only mark as "paid" if paymentId exists (not just saved in additionalFees)
                        const isPaid = paidSession && !!paidSession.paymentId;
                        const isSaved = paidSession && !paidSession.paymentId; // Saved but not paid yet

                        // Get the actual rate and currency
                        let displayRate, displayCurrency;
                        if ((isPaid || isSaved) && paidSession) {
                          // Use rate from database (additionalFees), fallback to API fee if null
                          displayRate =
                            paidSession.rate ||
                            (sessionFee ? sessionFee.rate : 0);
                          displayCurrency =
                            paidSession.currency ||
                            registrationFee?.currency ||
                            "INR";
                        } else if (sessionFee) {
                          // Use rate from fetched fee (for new selections)
                          displayRate = sessionFee.rate;
                          displayCurrency = sessionFee.currency || "INR";
                        } else {
                          // Fallback to 0
                          displayRate = 0;
                          displayCurrency = registrationFee?.currency || "INR";
                        }

                        // If session name already contains amount info, don't add it again
                        const nameContainsAmount =
                          /\([A-Z]{3}\s+[\d,]+\)/i.test(sessionName);

                        return (
                          <div className="summary-row" key={sessionId}>
                            <span
                              className="summary-label"
                              style={{ width: "100%" }}
                            >
                              {
                                nameContainsAmount
                                  ? sessionName // Name already has amount, just show it
                                  : `${sessionName} (${displayCurrency} ${parseFloat(
                                      displayRate || 0,
                                    ).toFixed(2)})` // Add amount to name with fixed decimals
                              }
                              {isPaid && (
                                <span
                                  style={{
                                    marginLeft: "8px",
                                    color: "#28a745",
                                    fontSize: "12px",
                                  }}
                                >
                                  ✓ Paid
                                </span>
                              )}
                              {isSaved && !isPaid && (
                                <span
                                  style={{
                                    marginLeft: "8px",
                                    color: "#0d6efd",
                                    fontSize: "12px",
                                  }}
                                >
                                  ✓ Saved
                                </span>
                              )}
                            </span>
                          </div>
                        );
                      })}
                      <div className="summary-row">
                        <span className="summary-label">
                          Subtotal ({allPaidSessionIds.length} session
                          {allPaidSessionIds.length > 1 ? "s" : ""}):
                        </span>
                        <span
                          className="summary-value"
                          style={{ fontWeight: "bold" }}
                        >
                          {(() => {
                            const focusedTotal = allPaidSessionIds.reduce(
                              (sum, sessionId) => {
                                const paidSession = paidFocusedSessions.find(
                                  (ps) => ps.feeTypeId === sessionId,
                                );

                                // Use rate from paidSession (DB) if available, otherwise from state
                                let rate = 0;
                                if (paidSession && paidSession.rate) {
                                  // Use rate from database
                                  rate = parseFloat(paidSession.rate);
                                } else {
                                  // Use rate from fetched fee (for new selections)
                                  const sessionFee =
                                    focusedSessionFees[sessionId];
                                  if (sessionFee && sessionFee.rate) {
                                    rate = parseFloat(sessionFee.rate);
                                  }
                                }

                                return sum + rate;
                              },
                              0,
                            );
                            const currency = registrationFee
                              ? registrationFee.currency
                              : "INR";
                            const symbol =
                              formData.country === "India" ? "₹" : "₹";
                            return `${symbol}${focusedTotal.toFixed(
                              2,
                            )} (${currency})`;
                          })()}
                        </span>
                      </div>
                    </div>
                  );
                })()}

                {/* Zero Payments Section */}
                {(() => {
                  // Collect all free items (rate = 0)
                  const freeItems = [];

                  // First, add items from formData.zeroPayments (already saved in database)
                  if (
                    formData.zeroPayments &&
                    Array.isArray(formData.zeroPayments)
                  ) {
                    formData.zeroPayments.forEach((zeroPayment) => {
                      freeItems.push({
                        name: zeroPayment.name,
                        type: "Saved",
                        isPaid: true, // Already saved in zero_payments table
                        createdDate: zeroPayment.createdDate,
                      });
                    });
                  }

                  // Check for free accompanying persons (not yet saved)
                  if (
                    accompanyingPersonFee &&
                    parseFloat(accompanyingPersonFee.rate) === 0
                  ) {
                    const paidAccompanyingPersons = formData.additionalFees
                      ? formData.additionalFees.filter(
                          (fee) => fee.feeTypeId === accompanyingPersonFee.id,
                        )
                      : [];
                    const allAccompanyingPersons = [
                      ...paidAccompanyingPersons.map((paid) => ({
                        name: paid.name,
                        isPaid: true,
                      })),
                      ...accompanyingPersons
                        .filter(
                          (person) =>
                            !paidAccompanyingPersons.some(
                              (paid) => paid.name === person.name,
                            ),
                        )
                        .map((person) => ({
                          name: person.name || "Accompanying Person",
                          isPaid: false,
                        })),
                    ];

                    allAccompanyingPersons.forEach((person) => {
                      // Don't duplicate if already in zeroPayments
                      const alreadyInZeroPayments =
                        formData.zeroPayments &&
                        formData.zeroPayments.some(
                          (zp) => zp.name === person.name,
                        );
                      if (!alreadyInZeroPayments) {
                        freeItems.push({
                          name: person.name,
                          type: "Accompanying Person",
                          isPaid: person.isPaid,
                        });
                      }
                    });
                  }

                  // Check for free focused sessions (not yet saved)
                  const paidFocusedSessions = formData.additionalFees
                    ? formData.additionalFees.filter(
                        (fee) =>
                          !accompanyingPersonFee ||
                          fee.feeTypeId !== accompanyingPersonFee.id,
                      )
                    : [];
                  const allFocusedSessionIds = [
                    ...paidFocusedSessions.map((fee) => fee.feeTypeId),
                    ...selectedFocusedSessions.filter(
                      (sessionId) =>
                        !paidFocusedSessions.some(
                          (paid) => paid.feeTypeId === sessionId,
                        ),
                    ),
                  ];

                  allFocusedSessionIds.forEach((sessionId) => {
                    const session = focusedSessions.find(
                      (s) => s.id === sessionId,
                    );
                    const sessionFee = focusedSessionFees[sessionId];
                    const isPaid = paidFocusedSessions.some(
                      (paid) => paid.feeTypeId === sessionId,
                    );
                    const sessionName = session
                      ? session.value
                      : `Session ${sessionId}`;

                    // Only include if rate is 0
                    if (sessionFee && parseFloat(sessionFee.rate) === 0) {
                      // Don't duplicate if already in zeroPayments
                      const alreadyInZeroPayments =
                        formData.zeroPayments &&
                        formData.zeroPayments.some(
                          (zp) => zp.name === sessionName,
                        );
                      if (!alreadyInZeroPayments) {
                        freeItems.push({
                          name: sessionName,
                          type: "Focused Session",
                          isPaid: isPaid,
                        });
                      }
                    }
                  });

                  if (freeItems.length === 0) return null;

                  return (
                    <div
                      className="summary-section"
                      style={{
                        backgroundColor: "#e8f5e9",
                        padding: "15px",
                        marginTop: "10px",
                        borderRadius: "5px",
                        border: "1px solid #4caf50",
                      }}
                    >
                      <h4 style={{ color: "#2e7d32", marginBottom: "10px" }}>
                        <span style={{ marginRight: "8px" }}>🆓</span>
                        Zero Payments (Free Items)
                      </h4>
                      {freeItems.map((item, index) => (
                        <div
                          className="summary-row"
                          key={index}
                          style={{ marginBottom: "8px" }}
                        >
                          <span className="summary-label">
                            {item.name}
                            <span
                              style={{
                                marginLeft: "8px",
                                fontSize: "11px",
                                color: "#666",
                                fontStyle: "italic",
                              }}
                            >
                              ({item.type})
                            </span>
                          </span>
                          <span
                            className="summary-value"
                            style={{ color: "#2e7d32", fontWeight: "bold" }}
                          >
                            FREE
                            {item.isPaid && (
                              <span
                                style={{
                                  marginLeft: "8px",
                                  color: "#28a745",
                                  fontSize: "12px",
                                }}
                              >
                                ✓ Saved
                              </span>
                            )}
                          </span>
                        </div>
                      ))}
                      <div
                        className="summary-row"
                        style={{
                          marginTop: "10px",
                          paddingTop: "10px",
                          borderTop: "1px solid #4caf50",
                        }}
                      >
                        <span
                          className="summary-label"
                          style={{ fontWeight: "bold" }}
                        >
                          Total Free Items:
                        </span>
                        <span
                          className="summary-value"
                          style={{ fontWeight: "bold", color: "#2e7d32" }}
                        >
                          {freeItems.length} item
                          {freeItems.length > 1 ? "s" : ""}
                        </span>
                      </div>
                    </div>
                  );
                })()}

                {/* Total Fee Section */}
                {registrationFee && (
                  <div
                    className="summary-section"
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "15px",
                      marginTop: "10px",
                      borderRadius: "5px",
                    }}
                  >
                    <h4 style={{ marginBottom: "15px" }}>Total Amount</h4>
                    {(() => {
                      const regFee = parseFloat(registrationFee.rate);
                      const currency = registrationFee.currency || "INR";
                      const currencySymbol =
                        currency === "INR"
                          ? "₹"
                          : currency === "USD"
                            ? "₹"
                            : currency;

                      // Calculate PAID amounts (prefer persisted payment records)
                      const existingAdditionalFees =
                        formData.additionalFees || [];

                      // Registration fee - check if paid
                      const registrationPaid = formData.paid ? regFee : 0;

                      // Paid accompanying persons (only those with paymentId)
                      const paidAccompanyingFees = existingAdditionalFees
                        .filter(
                          (fee) =>
                            accompanyingPersonFee &&
                            fee.feeTypeId === accompanyingPersonFee.id &&
                            fee.paymentId !== null &&
                            fee.paymentId !== undefined,
                        )
                        .reduce((sum, fee) => {
                          let itemRate = fee.rate ? parseFloat(fee.rate) : 0;
                          // If rate is null/0, get from accompanyingPersonFee
                          if (itemRate === 0 && accompanyingPersonFee) {
                            itemRate = parseFloat(
                              accompanyingPersonFee.rate || 0,
                            );
                          }
                          return sum + itemRate;
                        }, 0);

                      // Paid focused sessions (only those with paymentId)
                      const paidFocusedFees = existingAdditionalFees
                        .filter(
                          (fee) =>
                            fee.paymentId !== null &&
                            fee.paymentId !== undefined &&
                            (!accompanyingPersonFee ||
                              fee.feeTypeId !== accompanyingPersonFee.id),
                        )
                        .reduce((sum, fee) => {
                          let itemRate = fee.rate ? parseFloat(fee.rate) : 0;
                          // If rate is null/0, try to get from focusedSessionFees
                          if (
                            itemRate === 0 &&
                            focusedSessionFees[fee.feeTypeId]
                          ) {
                            itemRate = parseFloat(
                              focusedSessionFees[fee.feeTypeId].rate || 0,
                            );
                          }
                          return sum + itemRate;
                        }, 0);

                      // For PAID amounts: The convenience fee is already stored in the database as part of the payment
                      // So we should retrieve it from the payment records, not calculate it here
                      // TODO: Backend should return payment breakdown including convenience fee
                      // For now, we'll sum up what we have from additionalFees (which should include the convenience fee row)

                      // Check if we have payment data with breakdown
                      const payments = formData.payment || [];
                      let totalPaidAmount = 0;
                      let paidConvenienceFee = 0;

                      if (payments.length > 0 && payments[0].breakdown) {
                        // If we have payment breakdown from DB, sum all items including convenience fee
                        totalPaidAmount = payments[0].breakdown.reduce(
                          (sum, item) => {
                            const amount = parseFloat(item.amount || 0);
                            if (item.type === "CONVENIENCE_FEE") {
                              paidConvenienceFee += amount;
                            }
                            return sum + amount;
                          },
                          0,
                        );
                        console.log(
                          "✅ Using payment breakdown from DB:",
                          totalPaidAmount,
                        );
                      } else if (payments.length > 0) {
                        // Prefer payment.amount from DB to avoid recomputing from current selection
                        totalPaidAmount = payments.reduce((sum, p) => {
                          if (
                            p.status &&
                            ["SUCCESS", "COMPLETED"].includes(
                              String(p.status).toUpperCase(),
                            )
                          ) {
                            return sum + parseFloat(p.amount || 0);
                          }
                          return sum;
                        }, 0);
                        paidConvenienceFee = 0;
                        console.log(
                          "✅ Using payment.amount records from DB:",
                          totalPaidAmount,
                        );
                      } else {
                        // Fallback: Calculate from individual fees (legacy mode)
                        // This means convenience fee was not stored separately, so we calculate it
                        const totalPaidAmountBeforeFee =
                          registrationPaid +
                          paidAccompanyingFees +
                          paidFocusedFees;
                        paidConvenienceFee =
                          conveyanceFeePercentage !== null &&
                          conveyanceFeePercentage > 0
                            ? totalPaidAmountBeforeFee *
                              (conveyanceFeePercentage / 100)
                            : 0;
                        totalPaidAmount =
                          totalPaidAmountBeforeFee + paidConvenienceFee;
                        console.log(
                          "⚠️  Using calculated paid amount (no breakdown in DB):",
                          totalPaidAmount,
                        );
                      }

                      const outstandingSummary = calculateOutstandingAmounts();
                      const {
                        registrationOutstanding,
                        outstandingItems,
                        outstandingFromAdditionalFees,
                        newAccompanyingCount,
                        newAccompanyingOutstanding,
                        newFocusedSessionIds,
                        newFocusedOutstanding,
                        totalOutstandingBeforeFee,
                        conveyanceFeeAmount,
                        totalOutstanding,
                      } = outstandingSummary;
                      const convenienceFee = conveyanceFeeAmount;

                      console.log(
                        "🔍 Outstanding items from additionalFees:",
                        outstandingItems,
                        "Total outstanding:",
                        outstandingFromAdditionalFees,
                      );

                      const grandTotal = totalPaidAmount + totalOutstanding;
                      const payableNow = totalOutstanding;

                      // Debug logging
                      console.log("💰 Total Amount Calculation:", {
                        "formData.paid": formData.paid,
                        registrationFee: regFee,
                        registrationPaid: registrationPaid,
                        registrationOutstanding: registrationOutstanding,
                        existingAdditionalFees: existingAdditionalFees,
                        outstandingFromAdditionalFees:
                          outstandingFromAdditionalFees,
                        newAccompanyingCount: newAccompanyingCount,
                        newAccompanyingOutstanding: newAccompanyingOutstanding,
                        newFocusedSessionIds: newFocusedSessionIds,
                        newFocusedOutstanding: newFocusedOutstanding,
                        totalPaidAmount: totalPaidAmount,
                        totalOutstanding: totalOutstanding,
                        grandTotal: grandTotal,
                      });

                      return (
                        <>
                          {/* If both paid and outstanding exist, show breakdown */}
                          {totalPaidAmount > 0 && totalOutstanding > 0 && (
                            <>
                              <div className="summary-row">
                                <span
                                  className="summary-label"
                                  style={{
                                    fontSize: "1em",
                                    fontWeight: "500",
                                    color: "#28a745",
                                  }}
                                >
                                  Paid Amount (incl. 2.9% fee):
                                </span>
                                <span
                                  className="summary-value"
                                  style={{
                                    fontSize: "1.1em",
                                    fontWeight: "bold",
                                    color: "#28a745",
                                  }}
                                >
                                  {currencySymbol}
                                  {totalPaidAmount.toFixed(2)} ({currency})
                                </span>
                              </div>
                              <div className="summary-row">
                                <span
                                  className="summary-label"
                                  style={{
                                    fontSize: "1em",
                                    fontWeight: "500",
                                    color: "#666",
                                  }}
                                >
                                  Outstanding (before fee):
                                </span>
                                <span
                                  className="summary-value"
                                  style={{
                                    fontSize: "1em",
                                    fontWeight: "500",
                                    color: "#666",
                                  }}
                                >
                                  {currencySymbol}
                                  {totalOutstandingBeforeFee.toFixed(2)} (
                                  {currency})
                                </span>
                              </div>
                              {/* Only show conveyance fee if configured */}
                              {conveyanceFeePercentage !== null &&
                                conveyanceFeePercentage > 0 && (
                                  <div className="summary-row">
                                    <span
                                      className="summary-label"
                                      style={{
                                        fontSize: "0.95em",
                                        fontWeight: "400",
                                        color: "#666",
                                        fontStyle: "italic",
                                      }}
                                    >
                                      Conveyance Fee ({conveyanceFeePercentage}
                                      %):
                                    </span>
                                    <span
                                      className="summary-value"
                                      style={{
                                        fontSize: "0.95em",
                                        fontWeight: "400",
                                        color: "#666",
                                        fontStyle: "italic",
                                      }}
                                    >
                                      {currencySymbol}
                                      {convenienceFee.toFixed(2)} ({currency})
                                    </span>
                                  </div>
                                )}
                              <div className="summary-row">
                                <span
                                  className="summary-label"
                                  style={{
                                    fontSize: "1em",
                                    fontWeight: "500",
                                    color: "#dc3545",
                                  }}
                                >
                                  Outstanding Amount:
                                </span>
                                <span
                                  className="summary-value"
                                  style={{
                                    fontSize: "1.1em",
                                    fontWeight: "bold",
                                    color: "#dc3545",
                                  }}
                                >
                                  {currencySymbol}
                                  {totalOutstanding.toFixed(2)} ({currency})
                                </span>
                              </div>
                              <div
                                className="summary-row"
                                style={{
                                  marginTop: "10px",
                                  paddingTop: "10px",
                                  borderTop: "2px solid #dee2e6",
                                }}
                              >
                                <span
                                  className="summary-label"
                                  style={{
                                    fontSize: "1.1em",
                                    fontWeight: "bold",
                                  }}
                                >
                                  Total Payable Now:
                                </span>
                                <span
                                  className="summary-value"
                                  style={{
                                    fontSize: "1.2em",
                                    fontWeight: "bold",
                                    color: "#007bff",
                                  }}
                                >
                                  {currencySymbol}
                                  {payableNow.toFixed(2)} ({currency})
                                </span>
                              </div>
                              <div className="summary-row">
                                <span className="summary-label">
                                  Cumulative Paid + Outstanding:
                                </span>
                                <span className="summary-value">
                                  {currencySymbol}
                                  {grandTotal.toFixed(2)} ({currency})
                                </span>
                              </div>
                            </>
                          )}

                          {/* If only paid (fully paid scenario) */}
                          {totalPaidAmount > 0 && totalOutstanding === 0 && (
                            <>
                              {paidConvenienceFee > 0 && (
                                <>
                                  <div className="summary-row">
                                    <span
                                      className="summary-label"
                                      style={{
                                        fontSize: "1em",
                                        fontWeight: "500",
                                        color: "#28a745",
                                      }}
                                    >
                                      Subtotal:
                                    </span>
                                    <span
                                      className="summary-value"
                                      style={{
                                        fontSize: "1em",
                                        fontWeight: "500",
                                        color: "#28a745",
                                      }}
                                    >
                                      {currencySymbol}
                                      {(
                                        totalPaidAmount - paidConvenienceFee
                                      ).toFixed(2)}{" "}
                                      ({currency})
                                    </span>
                                  </div>
                                  {/* Only show conveyance fee if configured */}
                                  {conveyanceFeePercentage !== null &&
                                    conveyanceFeePercentage > 0 && (
                                      <div className="summary-row">
                                        <span
                                          className="summary-label"
                                          style={{
                                            fontSize: "0.95em",
                                            fontWeight: "400",
                                            color: "#28a745",
                                            fontStyle: "italic",
                                          }}
                                        >
                                          Conveyance Fee (
                                          {conveyanceFeePercentage}%):
                                        </span>
                                        <span
                                          className="summary-value"
                                          style={{
                                            fontSize: "0.95em",
                                            fontWeight: "400",
                                            color: "#28a745",
                                            fontStyle: "italic",
                                          }}
                                        >
                                          {currencySymbol}
                                          {paidConvenienceFee.toFixed(2)} (
                                          {currency})
                                        </span>
                                      </div>
                                    )}
                                </>
                              )}
                              <div
                                className="summary-row"
                                style={{
                                  marginTop: "10px",
                                  paddingTop: "10px",
                                  borderTop:
                                    paidConvenienceFee > 0
                                      ? "2px solid #dee2e6"
                                      : "none",
                                }}
                              >
                                <span
                                  className="summary-label"
                                  style={{
                                    fontSize: "1.1em",
                                    fontWeight: "bold",
                                    color: "#28a745",
                                  }}
                                >
                                  Total Amount:
                                </span>
                                <span
                                  className="summary-value"
                                  style={{
                                    fontSize: "1.2em",
                                    fontWeight: "bold",
                                    color: "#28a745",
                                  }}
                                >
                                  {currencySymbol}
                                  {totalPaidAmount.toFixed(2)} ({currency}) ✓
                                  Fully Paid
                                </span>
                              </div>
                            </>
                          )}

                          {/* If only outstanding (first-time user or no payment yet) */}
                          {totalPaidAmount === 0 && totalOutstanding > 0 && (
                            <>
                              <div className="summary-row">
                                <span
                                  className="summary-label"
                                  style={{
                                    fontSize: "1em",
                                    fontWeight: "500",
                                    color: "#666",
                                  }}
                                >
                                  Subtotal:
                                </span>
                                <span
                                  className="summary-value"
                                  style={{
                                    fontSize: "1em",
                                    fontWeight: "500",
                                    color: "#666",
                                  }}
                                >
                                  {currencySymbol}
                                  {totalOutstandingBeforeFee.toFixed(2)} (
                                  {currency})
                                </span>
                              </div>
                              {/* Only show conveyance fee if configured */}
                              {conveyanceFeePercentage !== null &&
                                conveyanceFeePercentage > 0 && (
                                  <div className="summary-row">
                                    <span
                                      className="summary-label"
                                      style={{
                                        fontSize: "0.95em",
                                        fontWeight: "400",
                                        color: "#666",
                                        fontStyle: "italic",
                                      }}
                                    >
                                      Conveyance Fee ({conveyanceFeePercentage}
                                      %):
                                    </span>
                                    <span
                                      className="summary-value"
                                      style={{
                                        fontSize: "0.95em",
                                        fontWeight: "400",
                                        color: "#666",
                                        fontStyle: "italic",
                                      }}
                                    >
                                      {currencySymbol}
                                      {convenienceFee.toFixed(2)} ({currency})
                                    </span>
                                  </div>
                                )}
                              <div
                                className="summary-row"
                                style={{
                                  marginTop: "10px",
                                  paddingTop: "10px",
                                  borderTop: "2px solid #dee2e6",
                                }}
                              >
                                <span
                                  className="summary-label"
                                  style={{
                                    fontSize: "1.1em",
                                    fontWeight: "bold",
                                    color: "#dc3545",
                                  }}
                                >
                                  Outstanding Amount:
                                </span>
                                <span
                                  className="summary-value"
                                  style={{
                                    fontSize: "1.2em",
                                    fontWeight: "bold",
                                    color: "#dc3545",
                                  }}
                                >
                                  {currencySymbol}
                                  {totalOutstanding.toFixed(2)} ({currency})
                                </span>
                              </div>
                            </>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Payment Status Section */}
                <div className="summary-section">
                  <h4>Payment Status</h4>

                  {formData.paid ? (
                    <div className="payment-status completed">
                      <span className="status-icon">✅</span>
                      <span className="status-text">
                        Payment completed and registration confirmed
                      </span>
                    </div>
                  ) : (
                    <div className="payment-status pending">
                      <span className="status-icon">⏳</span>
                      <span className="status-text">
                        Payment pending - Please complete payment to confirm
                        registration
                      </span>
                    </div>
                  )}
                </div>

                {/* Terms and Conditions Checkbox */}
                <div
                  className={`terms-checkbox-container ${
                    termsError ? "error" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    id="terms-checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => {
                      setAgreedToTerms(e.target.checked);
                      if (e.target.checked && hasReadTerms) {
                        setTermsError("");
                      }
                    }}
                  />
                  <label
                    htmlFor="terms-checkbox"
                    className="terms-checkbox-label"
                  >
                    I agree to the{" "}
                    <span
                      className="terms-link"
                      onClick={(e) => {
                        e.preventDefault();
                        setPendingProceedToPayment(false);
                        setShowTermsModal(true);
                      }}
                    >
                      Terms & Conditions
                    </span>
                  </label>
                </div>
                {termsError && (
                  <div className="terms-error-message">{termsError}</div>
                )}

                <div className="summary-actions">
                  {isPaymentBlockedPendingPaperApproval && (
                    <div
                      className="registration-status warning"
                      style={{ marginBottom: "10px" }}
                    >
                      ⏳ {paymentBlockedPaperStatusMessage}
                    </div>
                  )}

                  {/* Registration Status */}
                  {formData.id || formData.regId ? (
                    <div className="registration-status success">
                      ✅ Registration saved successfully!
                      {formData.id && <span> (ID: {formData.id})</span>}
                    </div>
                  ) : (
                    <div className="registration-status warning">
                      ⚠️ Registration not saved yet. Please save before
                      proceeding to payment.
                    </div>
                  )}

                  <div className="summary-buttons">
                    <button
                      type="button"
                      className="back-btn"
                      onClick={() => setStep(5)}
                    >
                      Back
                    </button>
                    {(() => {
                      const { totalOutstanding } =
                        calculateOutstandingAmounts();

                      // Only show payment button if there's an outstanding amount
                      if (totalOutstanding > 0) {
                        return (
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleProceedToPayment}
                            disabled={
                              saving || isPaymentBlockedPendingPaperApproval
                            }
                          >
                            {saving ? "Saving..." : "Proceed to Payment →"}
                          </button>
                        );
                      } else {
                        // No outstanding amount - show a completion message instead
                        return (
                          <div className="registration-status success">
                            ✅ All payments completed - No outstanding balance
                          </div>
                        );
                      }
                    })()}

                    {/* Paper/Abstract Submission Button for Pay & Register Mode */}
                    {abstractStepNumber === 7 &&
                      formData.paid === true &&
                      !hasPaperSubmitted && (
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => {
                            console.log(
                              "📝 Navigating to step 7 for paper submission",
                            );
                            setStep(7);
                          }}
                          style={{
                            marginTop: "10px",
                            padding: "12px 20px",
                            fontSize: "15px",
                            fontWeight: "600",
                            backgroundColor: "#28a745",
                            border: "none",
                            borderRadius: "6px",
                            color: "white",
                            cursor: "pointer",
                          }}
                        >
                          📄 Submit Paper/Abstract →
                        </button>
                      )}
                  </div>
                </div>
              </div>
            )}
          </>
        );
      case 7: {
        // If abstractStepNumber is 7 AND payment is complete, show abstract form
        if (abstractStepNumber === 7 && formData.paid === true) {
          // Render abstract form after payment is complete
          return (
            <>
              <div
                className="grid-item grid-item--full"
                style={{ marginBottom: "20px" }}
              >
                <div
                  style={{
                    padding: "16px",
                    backgroundColor: "#d4edda",
                    border: "2px solid #c3e6cb",
                    borderRadius: "8px",
                    marginBottom: "20px",
                  }}
                >
                  <h3
                    style={{
                      marginTop: 0,
                      marginBottom: "10px",
                      fontSize: "18px",
                      color: "#155724",
                    }}
                  >
                    ✓ Payment Completed Successfully
                  </h3>
                  <p style={{ margin: 0, color: "#155724", fontSize: "15px" }}>
                    Thank you for completing your payment. You can now submit
                    your abstract below.
                  </p>
                </div>
              </div>

              {isPaperLocked && (
                <div
                  className="grid-item grid-item--full"
                  style={{ marginBottom: "15px" }}
                >
                  <div
                    style={{
                      padding: "12px 16px",
                      backgroundColor: "#fff3cd",
                      border: "1px solid #ffc107",
                      borderRadius: "6px",
                      fontSize: "14px",
                      color: "#856404",
                    }}
                  >
                    <strong>🔒 Paper Locked:</strong> Your paper is currently in
                    review and cannot be edited. If you need to make changes,
                    please contact the conference organizers.
                  </div>
                </div>
              )}

              <div
                className="grid-item grid-item--full"
                style={{ marginBottom: "15px" }}
              >
                <div
                  style={{
                    padding: "12px 16px",
                    backgroundColor: "#e3f2fd",
                    border: "1px solid #2196f3",
                    borderRadius: "6px",
                    fontSize: "14px",
                    color: "#0d47a1",
                  }}
                >
                  <strong>📄 Paper Submission:</strong> Please provide your
                  paper title, track, abstract (max {maxAbstractChars}{" "}
                  characters), and upload your paper file.
                  {abstractConfig?.submissionDeadline && (
                    <div
                      style={{
                        marginTop: "8px",
                        fontSize: "13px",
                        color: "#1565c0",
                        fontWeight: "bold",
                      }}
                    >
                      📅 Submission Deadline:{" "}
                      {new Date(
                        abstractConfig.submissionDeadline,
                      ).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid-item grid-item--full">
                <input
                  type="text"
                  placeholder="Paper Title *"
                  value={paperTitle}
                  onChange={(e) => setPaperTitle(e.target.value)}
                  disabled={isPaperLocked}
                  readOnly={isPaperLocked}
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    border: "1px solid #666",
                    borderRadius: "8px",
                    fontSize: "15px",
                    backgroundColor: isPaperLocked ? "#f5f5f5" : "#fff",
                    cursor: isPaperLocked ? "not-allowed" : "text",
                  }}
                />
              </div>

              <div className="grid-item grid-item--full">
                <select
                  value={selectedTrack}
                  onChange={(e) => setSelectedTrack(e.target.value)}
                  disabled={isPaperLocked}
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    border: "1px solid #666",
                    borderRadius: "8px",
                    fontSize: "15px",
                    color: selectedTrack ? "#212529" : "#6c757d",
                    backgroundColor: isPaperLocked ? "#f5f5f5" : "#fff",
                    cursor: isPaperLocked ? "not-allowed" : "pointer",
                  }}
                >
                  <option value="" disabled>
                    Select Track *
                  </option>
                  {tracks && tracks.length > 0 ? (
                    tracks.map((track) => (
                      <option key={track.id} value={track.id}>
                        {track.name}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>
                      Loading tracks...
                    </option>
                  )}
                </select>
              </div>

              <div className="grid-item grid-item--full">
                <label
                  style={{
                    display: "block",
                    marginBottom: "10px",
                    fontWeight: "500",
                    color: "#000",
                  }}
                >
                  Presentation Mode *
                </label>
                {(() => {
                  // Parse presentation modes from abstractConfig.mode
                  // Can be single value like "ORAL" or comma-separated like "ORAL,POSTER,VIRTUAL"
                  const modes = abstractConfig?.mode
                    ? abstractConfig.mode.split(",").map((m) => m.trim())
                    : ["ORAL", "POSTER"];

                  // Display icons for common modes
                  const getModeIcon = (mode) => {
                    const modeUpper = mode.toUpperCase();
                    if (modeUpper === "ORAL") return "🎤";
                    if (modeUpper === "POSTER") return "📊";
                    if (modeUpper === "VIRTUAL") return "💻";
                    if (modeUpper === "HYBRID") return "🔄";
                    return "📋";
                  };

                  return (
                    <>
                      <div
                        style={{
                          display: "flex",
                          gap: "15px",
                          flexWrap: "wrap",
                        }}
                      >
                        {modes.map((mode) => (
                          <label
                            key={mode}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              cursor: isPaperLocked ? "not-allowed" : "pointer",
                              padding: "10px 15px",
                              border:
                                presentationMode === mode
                                  ? "2px solid #007bff"
                                  : "1px solid #dee2e6",
                              borderRadius: "6px",
                              backgroundColor: isPaperLocked
                                ? "#f5f5f5"
                                : presentationMode === mode
                                  ? "#e7f3ff"
                                  : "#fff",
                              flex:
                                modes.length === 1
                                  ? "1"
                                  : "1 1 calc(50% - 10px)",
                              minWidth: "150px",
                              transition: "all 0.2s",
                              opacity: isPaperLocked ? 0.6 : 1,
                            }}
                          >
                            <input
                              type="radio"
                              name="presentationMode"
                              value={mode}
                              checked={presentationMode === mode}
                              onChange={(e) =>
                                setPresentationMode(e.target.value)
                              }
                              disabled={isPaperLocked}
                              style={{
                                marginRight: "8px",
                                width: "18px",
                                height: "18px",
                                cursor: isPaperLocked
                                  ? "not-allowed"
                                  : "pointer",
                              }}
                            />
                            <span
                              style={{
                                fontSize: "16px",
                                fontWeight: "400",
                                color: "#000",
                              }}
                            >
                              {getModeIcon(mode)} {mode}
                            </span>
                          </label>
                        ))}
                      </div>
                      <div
                        style={{
                          marginTop: "12px",
                          padding: "12px 14px",
                          backgroundColor: "#eaf4ff",
                          border: "1px solid #b6dcff",
                          borderLeft: "4px solid #0b63c7",
                          borderRadius: "8px",
                          color: "#0a3d73",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "6px",
                            fontSize: "14px",
                            fontWeight: "600",
                          }}
                        >
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "18px",
                              height: "18px",
                              borderRadius: "50%",
                              backgroundColor: "#0b63c7",
                              color: "#fff",
                              fontSize: "12px",
                              fontWeight: "700",
                            }}
                          >
                            i
                          </span>
                          Presentation Mode Guidance
                        </div>
                        <div style={{ fontSize: "13px", lineHeight: "1.6" }}>
                          Reviewer panel will decide the final mode (Oral or
                          Poster) after evaluation.
                          <br />
                          Select your preference now, and be prepared to present
                          in either format.
                        </div>
                      </div>
                      {!presentationMode && modes.length > 1 && (
                        <div
                          style={{
                            marginTop: "8px",
                            padding: "8px 12px",
                            backgroundColor: "#fff3cd",
                            border: "1px solid #ffc107",
                            borderRadius: "4px",
                            fontSize: "13px",
                            color: "#856404",
                          }}
                        >
                          ⚠️ Please select one presentation mode from the
                          options above
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              <div className="grid-item grid-item--full">
                <label
                  style={{
                    display: "block",
                    marginBottom: "10px",
                    fontWeight: "500",
                    color: "#000",
                  }}
                >
                  Submission Method *
                </label>
                <div style={{ display: "flex", gap: "15px", flexWrap: "wrap" }}>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: isPaperLocked ? "not-allowed" : "pointer",
                      padding: "10px 15px",
                      border:
                        submissionMethod === "TEXTAREA"
                          ? "2px solid #007bff"
                          : "1px solid #dee2e6",
                      borderRadius: "6px",
                      backgroundColor: isPaperLocked
                        ? "#f5f5f5"
                        : submissionMethod === "TEXTAREA"
                          ? "#e7f3ff"
                          : "#fff",
                      flex: "1",
                      minWidth: "150px",
                      transition: "all 0.2s",
                      opacity: isPaperLocked ? 0.6 : 1,
                    }}
                  >
                    <input
                      type="radio"
                      name="submissionMethod"
                      value="TEXTAREA"
                      checked={submissionMethod === "TEXTAREA"}
                      onChange={(e) => setSubmissionMethod(e.target.value)}
                      disabled={isPaperLocked}
                      style={{
                        marginRight: "8px",
                        width: "18px",
                        height: "18px",
                        cursor: isPaperLocked ? "not-allowed" : "pointer",
                      }}
                    />
                    <span style={{ fontSize: "15px", color: "#000" }}>
                      📝 Text Only
                    </span>
                  </label>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: isPaperLocked ? "not-allowed" : "pointer",
                      padding: "10px 15px",
                      border:
                        submissionMethod === "FILE"
                          ? "2px solid #007bff"
                          : "1px solid #dee2e6",
                      borderRadius: "6px",
                      backgroundColor: isPaperLocked
                        ? "#f5f5f5"
                        : submissionMethod === "FILE"
                          ? "#e7f3ff"
                          : "#fff",
                      flex: "1",
                      minWidth: "150px",
                      transition: "all 0.2s",
                      opacity: isPaperLocked ? 0.6 : 1,
                    }}
                  >
                    <input
                      type="radio"
                      name="submissionMethod"
                      value="FILE"
                      checked={submissionMethod === "FILE"}
                      onChange={(e) => setSubmissionMethod(e.target.value)}
                      disabled={isPaperLocked}
                      style={{
                        marginRight: "8px",
                        width: "18px",
                        height: "18px",
                        cursor: isPaperLocked ? "not-allowed" : "pointer",
                      }}
                    />
                    <span style={{ fontSize: "15px", color: "#000" }}>
                      📄 File Only
                    </span>
                  </label>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: isPaperLocked ? "not-allowed" : "pointer",
                      padding: "10px 15px",
                      border:
                        submissionMethod === "BOTH"
                          ? "2px solid #007bff"
                          : "1px solid #dee2e6",
                      borderRadius: "6px",
                      backgroundColor: isPaperLocked
                        ? "#f5f5f5"
                        : submissionMethod === "BOTH"
                          ? "#e7f3ff"
                          : "#fff",
                      flex: "1",
                      minWidth: "150px",
                      transition: "all 0.2s",
                      opacity: isPaperLocked ? 0.6 : 1,
                    }}
                  >
                    <input
                      type="radio"
                      name="submissionMethod"
                      value="BOTH"
                      checked={submissionMethod === "BOTH"}
                      onChange={(e) => setSubmissionMethod(e.target.value)}
                      disabled={isPaperLocked}
                      style={{
                        marginRight: "8px",
                        width: "18px",
                        height: "18px",
                        cursor: isPaperLocked ? "not-allowed" : "pointer",
                      }}
                    />
                    <span style={{ fontSize: "15px", color: "#000" }}>
                      📝📄 Both
                    </span>
                  </label>
                </div>
              </div>

              {/* Abstract Textarea - Show only if TEXTAREA or BOTH is selected */}
              {(submissionMethod === "TEXTAREA" ||
                submissionMethod === "BOTH") && (
                <div className="grid-item grid-item--full">
                  <div style={{ position: "relative" }}>
                    <textarea
                      placeholder={`Structured Abstract * (Maximum ${maxAbstractChars} characters)`}
                      value={abstractText}
                      onChange={(e) => {
                        const text = e.target.value;
                        setAbstractText(text);
                      }}
                      maxLength={maxAbstractChars}
                      rows={8}
                      disabled={isPaperLocked}
                      readOnly={isPaperLocked}
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        border:
                          abstractText.length > maxAbstractChars
                            ? "2px solid #dc3545"
                            : "1px solid #666",
                        borderRadius: "8px",
                        fontSize: "15px",
                        backgroundColor: isPaperLocked ? "#f5f5f5" : "#fff",
                        cursor: isPaperLocked ? "not-allowed" : "text",
                        resize: "vertical",
                        fontFamily: "inherit",
                      }}
                    />
                    <div
                      style={{
                        marginTop: "5px",
                        fontSize: "13px",
                        color:
                          abstractText.length > maxAbstractChars
                            ? "#dc3545"
                            : abstractText.length > maxAbstractChars * 0.9
                              ? "#ffc107"
                              : "#6c757d",
                        textAlign: "right",
                      }}
                    >
                      {abstractText.length} / {maxAbstractChars} characters
                      {abstractText.length > maxAbstractChars &&
                        " (exceeds limit!)"}
                    </div>
                  </div>
                </div>
              )}

              {/* Paper File Upload - Show only if FILE or BOTH is selected */}
              {(submissionMethod === "FILE" || submissionMethod === "BOTH") && (
                <div className="grid-item grid-item--full">
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "500",
                      color: "#000",
                    }}
                  >
                    Upload Paper File * (
                    {abstractConfig?.acceptedDocTypes === "PDF_ONLY"
                      ? "PDF only"
                      : abstractConfig?.acceptedDocTypes === "DOC_ONLY"
                        ? "DOC/DOCX only"
                        : "PDF, DOC, DOCX"}{" "}
                    - Max {maxFileSizeMb}MB)
                  </label>
                  {paperFileUploaded && !paperFile && (
                    <div
                      style={{
                        marginBottom: "12px",
                        padding: "12px",
                        backgroundColor: "#d1ecf1",
                        border: "1px solid #bee5eb",
                        borderRadius: "6px",
                        color: "#0c5460",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "500",
                            marginBottom: "4px",
                          }}
                        >
                          📎 Previously Uploaded File
                        </div>
                        <div style={{ fontSize: "13px" }}>
                          {paperFileName || "Paper file uploaded"}
                        </div>
                        {isPaperLocked && (
                          <div
                            style={{
                              fontSize: "12px",
                              marginTop: "6px",
                              fontStyle: "italic",
                            }}
                          >
                            File upload is locked while paper is under review
                          </div>
                        )}
                      </div>
                      {!isPaperLocked && (
                        <button
                          type="button"
                          onClick={() => {
                            if (
                              window.confirm(
                                "Are you sure you want to delete this file? This action cannot be undone.",
                              )
                            ) {
                              setPaperFileUploaded(false);
                              setPaperFileName("");
                              setPaperFile(null);
                              const fileInput = document.querySelector(
                                'input[type="file"][accept*=".pdf"]',
                              );
                              if (fileInput) fileInput.value = "";
                            }
                          }}
                          style={{
                            padding: "6px 12px",
                            backgroundColor: "#dc3545",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "13px",
                            fontWeight: "500",
                            marginLeft: "10px",
                          }}
                        >
                          🗑️ Delete
                        </button>
                      )}
                    </div>
                  )}
                  <input
                    type="file"
                    accept={
                      abstractConfig?.acceptedDocTypes === "PDF_ONLY"
                        ? ".pdf"
                        : abstractConfig?.acceptedDocTypes === "DOC_ONLY"
                          ? ".doc,.docx"
                          : ".pdf,.doc,.docx"
                    }
                    disabled={isPaperLocked}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        let allowedTypes = [];
                        let errorMessage = "";

                        if (abstractConfig?.acceptedDocTypes === "PDF_ONLY") {
                          allowedTypes = ["application/pdf"];
                          errorMessage = "Please upload a PDF file only";
                        } else if (
                          abstractConfig?.acceptedDocTypes === "DOC_ONLY"
                        ) {
                          allowedTypes = [
                            "application/msword",
                            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                          ];
                          errorMessage =
                            "Please upload a DOC or DOCX file only";
                        } else {
                          allowedTypes = [
                            "application/pdf",
                            "application/msword",
                            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                          ];
                          errorMessage =
                            "Please upload a PDF, DOC, or DOCX file only";
                        }

                        if (!allowedTypes.includes(file.type)) {
                          setPaperFileError(errorMessage);
                          setPaperFile(null);
                          e.target.value = "";
                        } else if (file.size > maxFileSizeMb * 1024 * 1024) {
                          setPaperFileError(
                            `File size must be less than ${maxFileSizeMb}MB`,
                          );
                          setPaperFile(null);
                          e.target.value = "";
                        } else {
                          setPaperFileError("");
                          setPaperFile(file);
                        }
                      }
                    }}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #666",
                      borderRadius: "8px",
                      fontSize: "15px",
                      cursor: "pointer",
                    }}
                  />
                  {paperFileError && (
                    <div
                      style={{
                        marginTop: "8px",
                        padding: "8px 12px",
                        backgroundColor: "#fee",
                        border: "1px solid #fcc",
                        borderRadius: "4px",
                        color: "#c33",
                        fontSize: "13px",
                      }}
                    >
                      ⚠️ {paperFileError}
                    </div>
                  )}
                  {paperFile && !paperFileError && (
                    <div
                      style={{
                        marginTop: "10px",
                        padding: "12px",
                        backgroundColor: "#d4edda",
                        border: "1px solid #c3e6cb",
                        borderRadius: "6px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontSize: "14px",
                            color: "#155724",
                            fontWeight: "500",
                          }}
                        >
                          ✓ File uploaded successfully
                        </div>
                        <div
                          style={{
                            fontSize: "13px",
                            color: "#155724",
                            marginTop: "4px",
                          }}
                        >
                          {paperFile.name} ({(paperFile.size / 1024).toFixed(2)}{" "}
                          KB)
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setPaperFile(null);
                          const fileInput = document.querySelector(
                            'input[type="file"][accept*=".pdf"]',
                          );
                          if (fileInput) fileInput.value = "";
                        }}
                        style={{
                          padding: "6px 12px",
                          backgroundColor: "#dc3545",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "13px",
                          marginLeft: "10px",
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Co-Authors Section - Only show if coAuthors is enabled in abstract config */}
              {abstractConfig && abstractConfig.coAuthors === true && (
                <div
                  className="grid-item grid-item--full"
                  style={{ marginTop: "20px" }}
                >
                  <div
                    style={{
                      padding: "15px",
                      backgroundColor: "#f8f9fa",
                      border: "1px solid #dee2e6",
                      borderRadius: "8px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "15px",
                      }}
                    >
                      <label
                        style={{
                          fontWeight: "600",
                          color: "#000",
                          fontSize: "15px",
                        }}
                      >
                        Co-Authors (Optional)
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          setCoAuthors([
                            ...coAuthors,
                            {
                              name: "",
                              email: "",
                              phone: "",
                              specialization: "",
                              designation: "",
                            },
                          ]);
                        }}
                        disabled={isPaperLocked}
                        style={{
                          padding: "6px 12px",
                          backgroundColor: isPaperLocked
                            ? "#6c757d"
                            : "#28a745",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          cursor: isPaperLocked ? "not-allowed" : "pointer",
                          fontSize: "13px",
                          fontWeight: "500",
                          opacity: isPaperLocked ? 0.6 : 1,
                        }}
                      >
                        + Add Co-Author
                      </button>
                    </div>

                    {coAuthors.length === 0 && (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "20px",
                          color: "#6c757d",
                          fontSize: "14px",
                        }}
                      >
                        No co-authors added yet. Click "Add Co-Author" to add
                        one.
                      </div>
                    )}

                    {coAuthors.map((coAuthor, index) => (
                      <div
                        key={index}
                        style={{
                          marginBottom: "15px",
                          padding: "15px",
                          backgroundColor: "white",
                          borderRadius: "6px",
                          border: "1px solid #dee2e6",
                          position: "relative",
                        }}
                      >
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "10px",
                          }}
                        >
                          <input
                            type="text"
                            placeholder="Co-Author Full Name"
                            value={coAuthor.name}
                            onChange={(e) => {
                              const updated = [...coAuthors];
                              updated[index].name = e.target.value;
                              setCoAuthors(updated);
                            }}
                            disabled={isPaperLocked}
                            readOnly={isPaperLocked}
                            style={{
                              padding: "8px 12px",
                              border: "1px solid #ced4da",
                              borderRadius: "4px",
                              fontSize: "14px",
                              backgroundColor: isPaperLocked
                                ? "#f5f5f5"
                                : "#fff",
                              cursor: isPaperLocked ? "not-allowed" : "text",
                            }}
                          />
                          <input
                            type="email"
                            placeholder="Co-Author Email"
                            value={coAuthor.email}
                            onChange={(e) => {
                              const updated = [...coAuthors];
                              updated[index].email = e.target.value;
                              setCoAuthors(updated);
                            }}
                            disabled={isPaperLocked}
                            readOnly={isPaperLocked}
                            style={{
                              padding: "8px 12px",
                              border: "1px solid #ced4da",
                              borderRadius: "4px",
                              fontSize: "14px",
                              backgroundColor: isPaperLocked
                                ? "#f5f5f5"
                                : "#fff",
                              cursor: isPaperLocked ? "not-allowed" : "text",
                            }}
                          />
                          <input
                            type="tel"
                            placeholder="Phone Number"
                            value={coAuthor.phone || ""}
                            onChange={(e) => {
                              const updated = [...coAuthors];
                              updated[index].phone = e.target.value;
                              setCoAuthors(updated);
                            }}
                            disabled={isPaperLocked}
                            readOnly={isPaperLocked}
                            style={{
                              padding: "8px 12px",
                              border: "1px solid #ced4da",
                              borderRadius: "4px",
                              fontSize: "14px",
                              backgroundColor: isPaperLocked
                                ? "#f5f5f5"
                                : "#fff",
                              cursor: isPaperLocked ? "not-allowed" : "text",
                            }}
                          />
                          <input
                            type="text"
                            placeholder="Specialization"
                            value={coAuthor.specialization || ""}
                            onChange={(e) => {
                              const updated = [...coAuthors];
                              updated[index].specialization = e.target.value;
                              setCoAuthors(updated);
                            }}
                            disabled={isPaperLocked}
                            readOnly={isPaperLocked}
                            style={{
                              padding: "8px 12px",
                              border: "1px solid #ced4da",
                              borderRadius: "4px",
                              fontSize: "14px",
                              backgroundColor: isPaperLocked
                                ? "#f5f5f5"
                                : "#fff",
                              cursor: isPaperLocked ? "not-allowed" : "text",
                            }}
                          />
                          <input
                            type="text"
                            placeholder="Designation"
                            value={coAuthor.designation || ""}
                            onChange={(e) => {
                              const updated = [...coAuthors];
                              updated[index].designation = e.target.value;
                              setCoAuthors(updated);
                            }}
                            disabled={isPaperLocked}
                            readOnly={isPaperLocked}
                            style={{
                              padding: "8px 12px",
                              border: "1px solid #ced4da",
                              borderRadius: "4px",
                              fontSize: "14px",
                              backgroundColor: isPaperLocked
                                ? "#f5f5f5"
                                : "#fff",
                              cursor: isPaperLocked ? "not-allowed" : "text",
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const updated = coAuthors.filter(
                                (_, i) => i !== index,
                              );
                              setCoAuthors(updated);
                            }}
                            disabled={isPaperLocked}
                            style={{
                              padding: "8px 12px",
                              backgroundColor: isPaperLocked
                                ? "#6c757d"
                                : "#dc3545",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              cursor: isPaperLocked ? "not-allowed" : "pointer",
                              fontSize: "13px",
                              opacity: isPaperLocked ? 0.6 : 1,
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          );
        }

        // Otherwise, show payment form (original case 7 content)
        let displayCurrency = "INR";
        let displaySymbol = "₹";
        let displayAmount = "0.00";

        if (registrationFee && registrationFee.rate) {
          displayCurrency = registrationFee.currency || "INR";
          displaySymbol = formData.country === "India" ? "₹" : "₹";
        }

        const outstandingSummary = calculateOutstandingAmounts();
        const {
          registrationOutstanding,
          totalOutstanding,
          outstandingItems,
          totalOutstandingBeforeFee,
          conveyanceFeeAmount,
        } = outstandingSummary;

        const successfulPayments = (formData.payment || []).filter(
          (p) =>
            p &&
            p.status &&
            ["SUCCESS", "COMPLETED"].includes(String(p.status).toUpperCase()),
        );
        const paidAmountFromRecords = successfulPayments.reduce(
          (sum, p) => sum + parseFloat(p.amount || 0),
          0,
        );

        let settledPaidAmount = paidAmountFromRecords;
        if (
          settledPaidAmount === 0 &&
          formData.paid === true &&
          registrationFee
        ) {
          const baseFee = parseFloat(registrationFee.rate || 0);
          const feeRate =
            conveyanceFeePercentage !== null && conveyanceFeePercentage > 0
              ? conveyanceFeePercentage / 100
              : 0;
          settledPaidAmount = baseFee + baseFee * feeRate;
        }

        console.log("💳 Step 7 Payment Calculation (shared):", {
          registrationOutstanding,
          outstandingItems,
          totalOutstandingBeforeFee,
          conveyanceFeeAmount,
          totalOutstanding,
        });

        if (totalOutstanding > 0) {
          displayAmount = totalOutstanding.toFixed(2);
        } else if (formData.paid === true && settledPaidAmount > 0) {
          displayAmount = settledPaidAmount.toFixed(2);
        } else {
          displayAmount = totalOutstanding.toFixed(2);
        }

        return (
          <div className="summary-container">
            <div className="summary-section">
              <h4>Payment Details</h4>

              <div className="summary-row">
                <span className="summary-label">Registration Fee:</span>
                <span className="summary-value">
                  {displaySymbol}
                  {registrationFee && registrationFee.rate
                    ? parseFloat(registrationFee.rate).toFixed(2)
                    : "0.00"}{" "}
                  ({displayCurrency})
                </span>
              </div>

              {/* Show UNPAID accompanying persons breakdown only */}
              {accompanyingPersons.length > 0 &&
                accompanyingPersonFee &&
                (() => {
                  // Filter to only unpaid accompanying persons
                  const unpaidAccompanyingPersons = accompanyingPersons.filter(
                    (p) =>
                      p.feeTypeId === accompanyingPersonFee.id &&
                      !p.paid &&
                      !p.paymentId,
                  );
                  if (unpaidAccompanyingPersons.length === 0) return null;
                  return (
                    <>
                      <div className="summary-row">
                        <span className="summary-label">
                          Accompanying Persons (
                          {unpaidAccompanyingPersons.length}):
                        </span>
                        <span className="summary-value">
                          {displaySymbol}
                          {(
                            parseFloat(accompanyingPersonFee.rate) *
                            unpaidAccompanyingPersons.length
                          ).toFixed(2)}{" "}
                          ({displayCurrency})
                        </span>
                      </div>
                      <div className="summary-row">
                        <span className="summary-label"></span>
                        <span
                          className="summary-value"
                          style={{
                            fontSize: "0.9em",
                            color: "#666",
                            fontStyle: "italic",
                          }}
                        >
                          (
                          {unpaidAccompanyingPersons
                            .map((p) => p.name)
                            .join(", ")}
                          )
                        </span>
                      </div>
                    </>
                  );
                })()}

              {/* Show UNPAID items from additionalFees (saved but not paid) */}
              {(() => {
                const existingAdditionalFees = formData.additionalFees || [];

                // Get outstanding items (saved but not paid) excluding accompanying persons
                const outstandingFocusedSessions =
                  existingAdditionalFees.filter(
                    (fee) =>
                      !fee.paymentId &&
                      (!accompanyingPersonFee ||
                        fee.feeTypeId !== accompanyingPersonFee.id),
                  );

                if (outstandingFocusedSessions.length === 0) return null;

                // Calculate total and collect session details
                const sessionDetails = outstandingFocusedSessions.map((fee) => {
                  let itemRate = fee.rate ? parseFloat(fee.rate) : 0;

                  // If rate is null/0, try to get it from focusedSessionFees
                  if (itemRate === 0 && focusedSessionFees[fee.feeTypeId]) {
                    itemRate = parseFloat(
                      focusedSessionFees[fee.feeTypeId].rate || 0,
                    );
                  }

                  return {
                    id: fee.id,
                    name: fee.name,
                    rate: itemRate,
                  };
                });

                const outstandingTotal = sessionDetails.reduce(
                  (sum, session) => sum + session.rate,
                  0,
                );

                return (
                  <>
                    <div className="summary-row">
                      <span className="summary-label">
                        Outstanding Focused Sessions (
                        {outstandingFocusedSessions.length}):
                      </span>
                      <span className="summary-value">
                        {displaySymbol}
                        {outstandingTotal.toFixed(2)} ({displayCurrency})
                      </span>
                    </div>
                    {sessionDetails.map((session) => (
                      <div className="summary-row" key={session.id}>
                        <span
                          className="summary-label"
                          style={{
                            paddingLeft: "20px",
                            fontSize: "0.9em",
                            color: "#666",
                          }}
                        >
                          • {session.name}
                        </span>
                        <span
                          className="summary-value"
                          style={{ fontSize: "0.9em", color: "#666" }}
                        >
                          {displaySymbol}
                          {session.rate.toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </>
                );
              })()}

              {/* Show NEW UNPAID focused sessions from state (not yet saved) */}
              {selectedFocusedSessions.length > 0
                ? (() => {
                    const existingAdditionalFees =
                      formData.additionalFees || [];
                    const existingZeroPayments = formData.zeroPayments || [];

                    // Get IDs of sessions already in database (paid or unpaid)
                    const existingFocusedSessionIds = existingAdditionalFees
                      .filter(
                        (fee) =>
                          !accompanyingPersonFee ||
                          fee.feeTypeId !== accompanyingPersonFee.id,
                      )
                      .map((fee) => fee.feeTypeId);

                    // Filter to only NEW unpaid sessions (not in database yet)
                    const newUnpaidSessions = selectedFocusedSessions.filter(
                      (sessionId) =>
                        !existingFocusedSessionIds.includes(sessionId),
                    );

                    if (newUnpaidSessions.length === 0) return null;

                    // Calculate total and collect session details
                    const sessionDetails = newUnpaidSessions.map(
                      (sessionId) => {
                        const sessionFee = focusedSessionFees[sessionId];

                        // Try to get session name from multiple sources
                        let sessionName = null;

                        // 1. Try from focusedSessions array
                        const sessionFromList = focusedSessions.find(
                          (s) => s.id === sessionId,
                        );
                        if (sessionFromList) {
                          sessionName = sessionFromList.value;
                        }

                        // 2. Try from additionalFees (might have name stored)
                        if (!sessionName) {
                          const feeRecord = existingAdditionalFees.find(
                            (fee) => fee.feeTypeId === sessionId,
                          );
                          if (feeRecord && feeRecord.name) {
                            sessionName = feeRecord.name;
                          }
                        }

                        // 3. Try from zeroPayments
                        if (!sessionName) {
                          const zeroRecord = existingZeroPayments.find(
                            (zp) => zp.feeTypeId === sessionId,
                          );
                          if (zeroRecord && zeroRecord.name) {
                            sessionName = zeroRecord.name;
                          }
                        }

                        // 4. Fallback to session ID
                        if (!sessionName) {
                          sessionName = `Session ${sessionId}`;
                        }

                        const rate =
                          sessionFee && sessionFee.rate
                            ? parseFloat(sessionFee.rate)
                            : 0;

                        return {
                          id: sessionId,
                          name: sessionName,
                          rate: rate,
                        };
                      },
                    );

                    const newUnpaidTotal = sessionDetails.reduce(
                      (sum, session) => sum + session.rate,
                      0,
                    );

                    return (
                      <>
                        <div className="summary-row">
                          <span className="summary-label">
                            New Focused Sessions ({newUnpaidSessions.length}):
                          </span>
                          <span className="summary-value">
                            {displaySymbol}
                            {newUnpaidTotal.toFixed(2)} ({displayCurrency})
                          </span>
                        </div>
                        {sessionDetails.map((session) => (
                          <div className="summary-row" key={session.id}>
                            <span
                              className="summary-label"
                              style={{
                                paddingLeft: "20px",
                                fontSize: "0.9em",
                                color: "#666",
                              }}
                            >
                              • {session.name}
                            </span>
                            <span
                              className="summary-value"
                              style={{ fontSize: "0.9em", color: "#666" }}
                            >
                              {displaySymbol}
                              {session.rate.toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </>
                    );
                  })()
                : null}

              {feeError && (
                <div className="summary-row">
                  <span className="summary-value" style={{ color: "#dc2626" }}>
                    ⚠️ {feeError}
                  </span>
                </div>
              )}

              <div
                className="summary-row"
                style={{
                  marginTop: "1rem",
                  paddingTop: "1rem",
                  borderTop: "2px solid #e5e7eb",
                }}
              >
                <span
                  className="summary-label"
                  style={{ fontSize: "1.1rem", fontWeight: "600" }}
                >
                  {totalOutstanding > 0
                    ? "Total Amount:"
                    : formData.paid
                      ? "Paid Amount:"
                      : "Total Amount:"}
                </span>
                <span
                  className="summary-value"
                  style={{ fontSize: "1.1rem", fontWeight: "600" }}
                >
                  {displaySymbol}
                  {displayAmount} ({displayCurrency})
                </span>
              </div>

              {registrationFee && registrationFee.feeCategory && (
                <div className="summary-row">
                  <span className="summary-label">Fee Type:</span>
                  <span className="summary-value">
                    {registrationFee.feeCategory.value}
                  </span>
                </div>
              )}

              {registrationFee &&
                registrationFee.validFrom &&
                registrationFee.validTo && (
                  <div className="summary-row">
                    <span className="summary-label">Valid Period:</span>
                    <span className="summary-value">
                      {new Date(registrationFee.validFrom).toLocaleDateString()}{" "}
                      - {new Date(registrationFee.validTo).toLocaleDateString()}
                    </span>
                  </div>
                )}
            </div>

            <div className="summary-section">
              <h4>Registrant Information</h4>
              {formData.category && (
                <div className="summary-row">
                  <span className="summary-label">Category:</span>
                  <span className="summary-value">{formData.category}</span>
                </div>
              )}
              {formData.registrationType &&
                formData.registrationType.trim() !== "" &&
                formData.registrationType !== "TBD" && (
                  <div className="summary-row">
                    <span className="summary-label">Registration Type:</span>
                    <span className="summary-value">
                      {formData.registrationType}
                    </span>
                  </div>
                )}
              {formData.firstName && formData.lastName && (
                <div className="summary-row">
                  <span className="summary-label">Name:</span>
                  <span className="summary-value">
                    {formData.firstName} {formData.lastName}
                  </span>
                </div>
              )}
              {formData.email && (
                <div className="summary-row">
                  <span className="summary-label">Email:</span>
                  <span className="summary-value">{formData.email}</span>
                </div>
              )}
            </div>

            <div className="summary-actions">
              <div className="summary-buttons">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={startPayment}
                  disabled={
                    processingPayment ||
                    !hasRegistrationId ||
                    paymentInitiated ||
                    feeLoading ||
                    totalOutstanding <= 0
                  }
                >
                  {processingPayment
                    ? "Processing Payment..."
                    : paymentInitiated
                      ? "Payment Initiated..."
                      : feeLoading
                        ? "Calculating Fee..."
                        : totalOutstanding <= 0
                          ? "Payment Completed"
                          : !hasRegistrationId
                            ? "Save Registration First"
                            : "Pay & Complete Registration"}
                </button>
              </div>
              {!hasRegistrationId && (
                <div
                  className="registration-status warning"
                  style={{ marginTop: "1rem" }}
                >
                  ⚠️ Please go back to the Summary step and save your
                  registration before proceeding with payment.
                </div>
              )}
            </div>
          </div>
        );
      }
      default:
        return null;
    }
  };

  return (
    <div className="page-container">
      <div className="form-container">
        <div className="banner-container">
          {bannerUrl && (
            <div className="banner-container">
              <img src={bannerUrl} alt="Banner" className="banner-image" />
            </div>
          )}
        </div>
        <div className="registration-header">
          <div className="header-content">
            <h2 style={{ color: "#000", fontWeight: "bold" }}>
              Conference Registration
            </h2>
            {isExistingUser && existingUser && (
              <div className="user-info">
                <span
                  className="welcome-text"
                  style={{ color: "#000", fontWeight: "600" }}
                >
                  Welcome back,{" "}
                  <strong>
                    {existingUser.firstName} {existingUser.lastName}
                  </strong>
                  !
                </span>
                <span className="user-email">{existingUser.email}</span>
              </div>
            )}
            {!isExistingUser && prefilledEmail && (
              <div className="user-info">
                <span
                  className="new-user-text"
                  style={{ color: "#000", fontWeight: "600" }}
                >
                  Creating new registration for:{" "}
                  <strong>{prefilledEmail}</strong>
                </span>
              </div>
            )}
          </div>
          {onBackToLogin && (
            <button
              type="button"
              className="btn btn-outline back-to-login"
              onClick={onBackToLogin}
            >
              Back to Login
            </button>
          )}
        </div>

        <ProgressBar
          step={step}
          isAbstractRequired={isAbstractRequired}
          abstractStepNumber={abstractStepNumber}
        />

        <form className="form-grid" onSubmit={(e) => e.preventDefault()}>
          {renderStepFields()}
        </form>

        {/* Button row - show for appropriate steps based on whether abstract is required */}
        {/* When abstract required: show for steps 1-5. When not required: show for steps 1,2,4,5 (skipping 3) */}
        {step >= 1 && step <= 5 && (
          <div
            className="button-row"
            style={step === 1 ? { justifyContent: "flex-end" } : {}}
          >
            {step > 1 && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={prev}
              >
                Prev
              </button>
            )}

            {step === 3 && wantsToSubmitAbstract === true ? (
              <>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={async () => {
                    if (!validateCurrentStep(step)) {
                      return;
                    }
                    const success = await saveStep("draft");
                    if (success) {
                      setPaperApprovalStatus("DRAFT");
                      alert("Paper saved as draft. You can submit it later.");
                      setShowPaperSubmissionSummary(true);
                    }
                  }}
                  disabled={saving}
                  style={{ marginRight: "10px" }}
                >
                  {saving ? "Saving..." : "💾 Save as Draft"}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={async () => {
                    if (!validateCurrentStep(step)) {
                      return;
                    }
                    const success = await saveStep("submit");
                    if (success) {
                      setPaperApprovalStatus("SUBMITTED");
                      alert(
                        "Paper submitted successfully. You will receive an email once it is reviewed.",
                      );
                      setShowPaperSubmissionSummary(true);
                    }
                  }}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "📤 Submit"}
                </button>
              </>
            ) : (
              <>
                {step === 3 && showPaperSubmissionSummary ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowPaperSubmissionSummary(false)}
                    >
                      Back to Edit
                    </button>
                    {paperApprovalStatus === "SUBMITTED" ? (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => {
                          setShowPaperSubmissionSummary(false);
                          setStep(4);
                        }}
                      >
                        Continue to Affiliation
                      </button>
                    ) : isRevisionPaperStatus ? (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => {
                          setShowPaperSubmissionSummary(false);
                          setStep(3);
                        }}
                      >
                        Continue to Edit Paper
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => {
                          setShowPaperSubmissionSummary(false);
                          setStep(4);
                        }}
                      >
                        Continue Registration
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={saveAndContinue}
                    disabled={
                      saving ||
                      (step === 4 &&
                        wantsToSubmitAbstract === true &&
                        paperApprovalStatus === "SUBMITTED")
                    }
                    title={
                      step === 4 &&
                      wantsToSubmitAbstract === true &&
                      paperApprovalStatus === "SUBMITTED"
                        ? "Please wait for paper approval before continuing"
                        : ""
                    }
                  >
                    {saving
                      ? "Saving..."
                      : step === 3
                        ? "Save & continue"
                        : "Save & Continue"}
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {/* Step 7 abstract submission button - only show when abstractStepNumber is 7 and payment is complete */}
        {step === 7 && abstractStepNumber === 7 && formData.paid === true && (
          <div className="button-row">
            <button className="btn btn-secondary" onClick={() => setStep(6)}>
              Back to Summary
            </button>
            <button
              className="btn btn-secondary"
              onClick={async () => {
                // Validate step 7 fields before saving
                if (!validateCurrentStep(7)) {
                  return;
                }

                const success = await saveStep("draft");
                if (success) {
                  alert("Paper saved as draft.");
                  setStep(6);
                }
              }}
              disabled={saving}
              style={{
                padding: "15px",
                fontSize: "16px",
                fontWeight: "600",
                marginRight: "10px",
              }}
            >
              {saving ? "Saving..." : "💾 Save as Draft"}
            </button>
            <button
              className="btn btn-primary"
              onClick={async () => {
                // Validate step 7 fields before saving
                if (!validateCurrentStep(7)) {
                  return;
                }

                const success = await saveStep("submit");
                if (success) {
                  alert("Abstract submitted successfully!");
                  setStep(6);
                }
              }}
              disabled={saving}
              style={{
                padding: "15px",
                fontSize: "16px",
                fontWeight: "600",
              }}
            >
              {saving ? "Saving..." : "📤 Submit"}
            </button>
          </div>
        )}
      </div>

      {/* Terms and Conditions Modal */}
      <TermsAndConditions
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAcknowledge={handleTermsAcknowledged}
      />
    </div>
  );
};
export default ConferenceRegistration;
